const { useEffect, useMemo, useRef, useState } = React;
const { createRoot } = ReactDOM;

const API_BASE = window.API_BASE || "";
const APP_LANG = window.__APP_LANG__ === "en" ? "en" : "ko";
const DEFAULT_DAILY_INTERVAL = "일봉";
const CASH_TICKER = "__CASH__";
const CASH_DISPLAY_NAME = "현금";

const isEnglishLang = () => (window.__APP_LANG__ || APP_LANG) === "en";

// 2026-09-19 다크모드: CSS 변수 현재값 읽기(차트 캔버스/Chart.js가 CSS 상속을 못 받으므로).
const cssVar = (name, fallback = "") => {
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  } catch (_) {
    return fallback;
  }
};
const isDarkTheme = () => document.documentElement.classList.contains("dark");

const EN_TEXT = {
  // 2026-10-05 round2: 드로다운/월수익/거장13F/스크리너 카드 EN 사본
  // 2026-10-28 B workbench: 종목분석 워크벤치 재배치 신규 문자열 EN 사본
  "분석 종목 정보": "Analysis subject",
  "저장 종가 없음": "No stored close",
  "관련 링크": "Related links",
  "비교 분석 모드": "Comparison mode",
  "종목 스냅샷": "Stock snapshot",
  "비교 분석": "Comparison",
  "여러 종목 비교 결과는 왼쪽 차트 영역에 표시됩니다.": "Multi-ticker comparison results are shown in the chart area.",
  "시세 스냅샷을 불러오는 중...": "Loading price snapshot...",
  "종목 스크리너": "Stock Screener",
  "스크리너": "Screener",
  "저장된 전체 시장 일봉의 가격 조건 충족 나열입니다 — 매수 추천이 아닙니다. 펀더멘털 조건(PER/PBR 등)은 확정 캐시가 62종목만 커버해 지원하지 않습니다.": "A listing of tickers meeting price conditions across the stored market — not buy recommendations. Fundamental filters (PER/PBR etc.) are unsupported: the confirmed cache covers only 62 tickers.",
  "52주 위치 최소(%)": "Min 52w position (%)",
  "52주 위치 최대(%)": "Max 52w position (%)",
  "20일 평균 거래량 최소": "Min 20d avg volume",
  "20일 평균 거래대금 최소": "Min 20d avg dollar volume",
  "1년 최대 낙폭 한도(%)": "Max 1y drawdown (%)",
  "SMA200 위치": "SMA200 position",
  "전체": "All",
  "200일선 위": "Above 200d MA",
  "200일선 아래": "Below 200d MA",
  "최대 행 수": "Max rows",
  "조회 중... (콜드 시 ~10초)": "Scanning... (~10s cold)",
  "스크린 실행": "Run screener",
  "조건을 충족한 종목이 없습니다. 조건을 완화해 다시 실행하세요.": "No tickers match. Loosen the conditions and run again.",
  "데이터는 저장된 일봉 종가 기준(실시간 아님). 스크리닝 결과는 투자 조언이 아닙니다.": "Based on stored daily closes (not realtime). Screening output is not investment advice.",
  "티커": "Ticker",
  "종가": "Close",
  "일변화": "Day change",
  "52주 위치": "52w position",
  "52주 최고": "52w high",
  "52주 최저": "52w low",
  "MDD(1년)": "MDD (1y)",
  "20일 평균 거래량": "20d avg volume",
  "기준일": "As of",
  "드로다운 (선택 기간)": "Drawdown (selected period)",
  "선택 기간 최대 낙폭": "Max drawdown in period",
  "고점 대비 하락폭(종가 기준). 미실현 손실 지표이며 미래 수익과 무관합니다.": "decline from peak (close basis). An unrealized-loss gauge — unrelated to future returns.",
  "월수익 히트맵": "Monthly returns heatmap",
  "연도": "Year",
  "월말 종가 기준 월수익률(%). 배당 미포함, 결측 월은 회색. 과거 패턴이 미래를 보장하지 않습니다.": "Month-end close returns (%). Excludes dividends; gray = missing. Past patterns do not guarantee the future.",
  "거장 13F 보유 현황": "Guru 13F ownership",
  "거장": "Guru",
  "보유주식수": "Shares held",
  "평가액": "Value",
  "포트 비중": "Portfolio weight",
  "분기 변화": "QoQ change",
  "추이": "Trend",
  "보유 유지": "Held",
  "신규": "New",
  "이력 부족": "Insufficient history",
  "SEC 13F 공시 기반(분기별, 사후 확정). 일부 거장은 ETF/옵션 제외 티커 매칭 누락이 있을 수 있으며 미보유 종목도 매칭 실패가 있을 수 있습니다. 13F는 장기전략의 일부만 반영합니다.": "Based on quarterly SEC 13F filings (after the fact). Ticker matching may miss some ETF/option rows, and non-holdings can also fail to match. 13F reflects only part of long-term strategies.",
  "원문": "Source",
  "4분기 도출": "Q4 derived",
  "매출": "Revenue",
  "영업이익": "Operating income",
  "영업이익률": "Operating margin",
  "순이익": "Net income",
  "기간": "Period",
  "공시": "Filing",
  "로그인 후 사용 가능합니다.": "Please log in to use this feature.",
  "저장 포트폴리오를 불러오지 못했습니다.": "Couldn’t load saved portfolios.",
  "저장 포트 없음": "No saved portfolios",
  "합계": "Total",
  "포트 삭제": "Delete portfolio",
  "투자 아이디어를 빠르게 비교하고 검증하세요.": "Compare and validate investment ideas quickly.",
  "종목 가격 흐름, 성과 지표, 포트폴리오 시뮬레이션을 한 곳에서 확인하는 리서치 워크스페이스입니다.": "A research workspace for price trends, performance metrics, and portfolio simulations.",
  "데이터로 시작하는 종목 분석": "Stock analysis that starts with data",
  "종목을 선택하면 가격 흐름, 성과·위험 지표와 주요 펀더멘털을 한 화면에서 확인할 수 있습니다.": "Select a ticker to review price trends, performance and risk metrics, and key fundamentals in one workspace.",
  "분석 조건": "Analysis setup",
  "종목과 조회 기간을 선택하세요. 비교 종목은 필요할 때 추가할 수 있습니다.": "Choose a ticker and date range. Add comparison tickers only when you need them.",
  "빠른 기간 선택": "Quick range",
  "보조지표": "Indicators",
  "보조지표 선택": "Indicator selection",
  "펼치기": "Expand",
  "접기": "Collapse",
  "시작주가 환산": "Rebased to starting stock price",
  "기준지수는 라인 차트에 시작주가 기준으로 환산해 표시합니다.": "The benchmark is rebased to the starting stock price on the line chart.",
  "벤치마크를 끄면 캔들 차트를 선택할 수 있습니다.": "Turn off the benchmark to select the candlestick chart.",
  "기준지수는 첫 공통 거래일의 종목 주가로 환산한 비교선입니다. 실제 지수 가격이 아닙니다.": "The comparison line is rebased to the stock price on the first common trading date, not the actual index level.",
  "선택 구간에 기준지수와 겹치는 가격 데이터가 부족해 비교선을 표시하지 않습니다.": "Not enough overlapping benchmark data in the selected range to display a comparison line.",
  "라인": "Line",
  "캔들": "Candles",
  "분석할 종목을 선택하세요": "Choose a ticker to analyze",
  "불러오는 중...": "Loading...",
  "검색창에 티커나 종목명을 입력하면 가격 차트와 핵심 지표가 이 영역에 표시됩니다.": "Enter a ticker or company name to see its price chart and key metrics here.",
  "주요 성과": "Performance snapshot",
  "포트폴리오 분석 시작": "Start portfolio analysis",
  "커뮤니티 보기": "View community",
  "서비스 특징": "Service highlights",
  "기간별 가격 추세": "Price trends by period",
  "다중 포트 성과 비교": "Multi-portfolio performance comparison",
  "비율(%)": "Weight (%)",
  "100%로 정규화": "Normalize to 100%",
  "입력한 포트/종목/비중/기간 선택을 모두 초기화할까요?": "Reset all portfolios, tickers, weights, and date selections?",
  "포트를 삭제할까요?": "Delete this portfolio?",
  "가격·지표를 확인하고, 비교할 수 있습니다.": "Check and compare prices and metrics.",
  "원하는 종목이 검색되지 않는 경우, 해당 종목의 티커를 검색하세요": "If you can’t find the stock you want, search by its ticker symbol.",
  "차트명": "Chart name",
  "Y축 명": "Y-axis label",
  "핵심 지표 요약": "Key metrics summary",
  "지표": "Metric",
  "값": "Value",
  "가격 추세": "Price trend",
  "가격": "Price",
  "차트 유형": "Chart type",
  "라인 차트": "Line chart",
  "캔들 차트": "Candlestick chart",
  "토탈 리턴 (%)": "Total return (%)",
  "12개월 EPS": "12-month EPS",
  "성과": "Performance",
  "조회 기간 동안 가격이 실제로 얼마나 성장했는지 봅니다.": "Shows how much the price actually grew during the selected period.",
  "누적 수익률": "Total return",
  "기간 시작가 대비 마지막 가격의 총 수익률": "Total return from the start price to the last price in the period",
  "높을수록 좋음": "Higher is better",
  "연환산 수익률": "Annualized return",
  "보유 기간 수익률을 연 단위로 환산한 성장률": "The holding-period return converted to an annualized growth rate",
  "위험 조정": "Risk-adjusted",
  "수익률이 얼마나 큰 낙폭과 변동성을 감수하고 만들어졌는지 확인합니다.": "Shows how much drawdown and volatility were taken to generate returns.",
  "최대 낙폭": "Max drawdown",
  "고점 대비 가장 깊었던 하락폭": "The deepest decline from a previous high",
  "절댓값이 작을수록 안정적": "Smaller absolute value is more stable",
  "연환산 변동성": "Annualized volatility",
  "일별 수익률 변동성을 연 단위로 환산": "Daily return volatility annualized",
  "낮을수록 안정적": "Lower is more stable",
  "샤프 비율": "Sharpe ratio",
  "전체 변동성 대비 초과 성과": "Excess performance relative to total volatility",
  "높을수록 효율적": "Higher is more efficient",
  "소르티노 비율": "Sortino ratio",
  "하방 변동성만 고려한 위험 조정 성과": "Risk-adjusted performance considering downside volatility only",
  "밸류에이션": "Valuation",
  "현재 가격이 이익·자본 대비 어느 정도 프리미엄을 받고 있는지 봅니다.": "Shows the premium of the current price relative to earnings and book value.",
  "최근 이익 대비 주가 배수": "Price multiple versus recent earnings",
  "낮을수록 저평가 단서": "Lower can indicate undervaluation",
  "예상 이익 기준 주가 배수": "Price multiple based on expected earnings",
  "순자산 대비 주가 배수": "Price multiple versus book value",
  "시가총액 대비 잉여현금흐름 수익률": "Free-cash-flow yield versus market cap",
  "높을수록 현금창출 매력": "Higher indicates stronger cash-generation appeal",
  "규모": "Size",
  "기업의 시장 평가 규모를 별도로 확인합니다.": "Shows the company’s market valuation scale separately.",
  "시가총액": "Market cap",
  "주가와 발행주식수로 계산된 시장 가치": "Market value calculated from price and shares outstanding",
  "규모 판단 참고": "Reference for company size",
  "제공사 기준 통화의 압축 표기": "Compact notation in the provider’s currency",
  "수익성·성장성": "Profitability & growth",
  "회사가 자본·자산을 얼마나 효율적으로 이익화하고 매출을 키우는지 확인합니다.": "Shows how efficiently the company turns capital and assets into profit and grows revenue.",
  "자기자본 대비 이익 창출력": "Profit generation relative to equity",
  "총자산 대비 이익 창출력": "Profit generation relative to total assets",
  "최근 매출 성장률": "Recent revenue growth",
  "높을수록 성장성 우수": "Higher indicates stronger growth",
  "주주환원": "Shareholder return",
  "현금 배당으로 돌려주는 수익률을 확인합니다.": "Shows the yield returned through cash dividends.",
  "배당수익률": "Dividend yield",
  "현재가 대비 기억해 둔 연간 배당액 기준 수익률": "Annual dividend yield calculated from the cached annual dividend amount versus current price",
  "높을수록 배당 매력": "Higher indicates stronger dividend appeal",
  "안정성": "Stability",
  "재무 레버리지 부담이 어느 정도인지 봅니다.": "Shows the level of financial leverage burden.",
  "부채비율": "Debt-to-equity",
  "자기자본 대비 부채 비율": "Debt relative to equity",
  "낮을수록 재무 부담 작음": "Lower indicates less financial burden",
  "성과 지표": "Performance metrics",
  "선택한 기간의 가격 데이터로 계산한 성과와 위험 조정 지표입니다.": "Performance and risk-adjusted metrics calculated from price data over the selected period.",
  "펀더멘털 지표": "Fundamental metrics",
  "기업의 밸류에이션, 수익성, 성장성, 안정성, 주주환원을 보여주는 재무 기반 지표입니다.": "Finance-based metrics for valuation, profitability, growth, stability, and shareholder returns.",
  "기타": "Other",
  "데이터 소스에서 추가로 제공된 참고 지표입니다.": "Additional reference metrics provided by the data source.",
  "포트폴리오 합산 가치의 가격 흐름으로 계산한 성과와 위험 지표입니다.": "Performance and risk metrics calculated from the aggregate portfolio value trend.",
  "성과 지표와 펀더멘털 지표를 구분해 가격 흐름과 기업 기초체력을 함께 확인합니다.": "Separates performance and fundamental metrics so you can review both price action and business fundamentals.",
  "PER/PBR/ROE/ROA/시가총액/배당수익률/부채비율/Revenue Growth/FCF Yield는 제공사 데이터가 있는 개별주에만 표시되며, ETF·지수형 상품은 성과·위험 지표 중심으로 표시됩니다.": "PER/PBR/ROE/ROA/market cap/dividend yield/debt-to-equity/Revenue Growth/FCF Yield are shown only for individual stocks with provider data; ETFs and index products focus on performance and risk metrics.",
  "데이터 출처와 산식 안내": "Data source and formula notes",
  "데이터 출처/산식: 가격·성과 지표는 선택 기간 가격 데이터로 계산합니다. 펀더멘털은 Yahoo Finance(yfinance)를 우선 사용하고, 한국 6자리 종목은 Naver Finance/OpenDART로 보강합니다. 배당수익률=캐시된 연간 배당액÷현재가, 시가총액=현재가×발행주식수 또는 제공사 marketCap. 모든 값은 참고용입니다.": "Data source/formulas: price and performance metrics are calculated from the selected-period price data. Fundamentals use Yahoo Finance (yfinance) first, with Naver Finance/OpenDART fallback for Korean six-digit tickers. Dividend yield = cached annual dividend amount ÷ current price; market cap = current price × shares outstanding or provider marketCap. All values are for reference only.",
  "현재 이 종목은 확정 12개월 EPS 시계열이 없어 EPS 오버레이를 표시할 수 없습니다. 가격 차트와 기본 지표는 정상 표시됩니다.": "This ticker does not currently have a confirmed 12-month EPS time series, so the EPS overlay cannot be shown. The price chart and basic metrics are still available.",
  "선택한 기간에는 EPS 발표일 이후의 가격 데이터가 없어 EPS 오버레이를 표시할 수 없습니다. 기간을 넓혀 다시 시도해보세요.": "The selected range has no price data after EPS filing dates, so the EPS overlay cannot be shown. Try a wider date range.",
  "이 종목의 발행된 분석 글": "Published analyses for this ticker",
  "관련 발행 분석 글을 불러오는 중...": "Loading related analyses...",
  "관련 글을 불러오지 못했습니다.": "Couldn't load related analyses.",
  "아직 이 종목을 다룬 발행 분석 글이 없습니다.": "No published analyses cover this ticker yet.",
  "전체 분석 글 보기": "View all analyses",
};

const sfText = (text) => (isEnglishLang() ? EN_TEXT[text] || text : text);
const sfMetricDefinition = (definition) => ({
  ...definition,
  label: sfText(definition.label),
  description: definition.description ? sfText(definition.description) : definition.description,
  direction: definition.direction ? sfText(definition.direction) : definition.direction,
});

const defaultPortfolioName = (idx) => (isEnglishLang() ? `Portfolio ${idx + 1}` : `포트 ${idx + 1}`);

const intervalDisplayName = (interval) => {
  if (!isEnglishLang()) return interval;
  return {
    일봉: "Daily",
    주봉: "Weekly",
    월봉: "Monthly",
  }[interval] || interval;
};

const pickDefaultInterval = (intervals, current = "") => {
  const list = Array.isArray(intervals) ? intervals : [];
  if (current && list.includes(current)) return current;
  return list.includes("일봉") ? "일봉" : list[0] || "";
};

const uniqueNonEmptyStrings = (values) =>
  Array.from(new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean)));

const metadataDisplayName = (item) => {
  const name = String(item?.name || "").trim();
  const nameEn = String(item?.name_en || "").trim();
  return isEnglishLang() && nameEn ? nameEn : (name || nameEn);
};

const metadataAliases = (item) => uniqueNonEmptyStrings([item?.name, item?.name_en]);

const buildTickerMetadataMaps = (items) => {
  const names = {};
  const aliases = {};
  const tags = {};
  (items || []).forEach((item) => {
    if (!item || !item.ticker) return;
    const key = normalizeTickerKey(String(item.ticker));
    const displayName = metadataDisplayName(item);
    if (displayName) names[key] = displayName;
    aliases[key] = metadataAliases(item);
    tags[key] = Array.isArray(item.tags) ? item.tags.map((t) => String(t).trim()).filter(Boolean) : [];
  });
  return { names, aliases, tags };
};

const formatCompactLargeNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  const abs = Math.abs(numeric);
  if (abs >= 1e12) return `${(numeric / 1e12).toFixed(abs >= 10e12 ? 1 : 2)}T`;
  if (abs >= 1e9) return `${(numeric / 1e9).toFixed(abs >= 10e9 ? 1 : 2)}B`;
  if (abs >= 1e6) return `${(numeric / 1e6).toFixed(abs >= 10e6 ? 1 : 2)}M`;
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatMetric = (metric, value) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "N/A";
  }
  const metricName = String(metric || "").trim().toUpperCase();
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "N/A";
  }
  if (metricName === "MARKET CAP") {
    return formatCompactLargeNumber(numeric);
  }
  if (metric.includes("Ratio") || ["PER", "PER(FWD)", "PBR"].includes(metricName)) {
    return numeric.toFixed(2);
  }
  if (metricName === "DEBT/EQUITY") {
    return `${numeric.toFixed(2)}%`;
  }
  return `${(numeric * 100).toFixed(2)}%`;
};

const formatChartTick = (value, yLabel = "") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  if (String(yLabel).includes("%")) return `${numeric.toFixed(Math.abs(numeric) >= 10 ? 0 : 1)}%`;
  const abs = Math.abs(numeric);
  if (abs >= 1000000) return `${(numeric / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `${Math.round(numeric / 1000)}K`;
  return `${Math.round(numeric)}`;
};

const formatChartDateTick = (value) => {
  const raw = String(value || "");
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return raw;
  return `${Number(match[2])}/${Number(match[3])}`;
};

const formatCandlePrice = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCandleVolume = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return formatChartTick(numeric);
};

const workbenchChartTypography = ({ mobile, dark }) => {
  const family = '"Noto Sans CJK KR", "Apple SD Gothic Neo", sans-serif';
  return {
    axis: { family: "Arial", size: 11, weight: "400", lineHeight: 1.6 },
    title: { family, size: mobile ? 14 : 15, weight: "600", lineHeight: 1.6 },
    legend: { family, size: 11, weight: "400", lineHeight: 1.6 },
    ink: dark ? "#e0e7ec" : "#202326",
    muted: dark ? "#a5b3be" : "#626c73",
    legendInk: dark ? "#a5b3be" : "#737a7e",
    grid: dark ? "#35434e" : "#e7e9ea",
    datasetColor: (original, index, benchmark) => benchmark
      ? (dark ? "#d8a081" : "#bb7855")
      : index === 0 ? (dark ? "#e0e7ec" : "#283238") : original,
  };
};

const LineChart = ({ labels, datasets, title, yLabel, onRangeChange, rangeBounds }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const dragRef = useRef(null);
  const touchRef = useRef(null);
  const [selectionRect, setSelectionRect] = useState(null);

  const [darkChart, setDarkChart] = useState(() => isDarkTheme());
  useEffect(() => {
    const onTheme = () => setDarkChart(isDarkTheme());
    window.addEventListener("sfthemechange", onTheme);
    return () => window.removeEventListener("sfthemechange", onTheme);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !labels.length || !datasets.length || !window.Chart) {
      return;
    }
    const workbenchChart = !!canvasRef.current.closest(".analysis-workbench") || document.body.classList.contains("site-workbench");
    const isMobileChart = window.matchMedia && window.matchMedia(workbenchChart ? "(max-width: 700px)" : "(max-width: 640px)").matches;
    const typography = workbenchChart ? workbenchChartTypography({ mobile: isMobileChart, dark: darkChart }) : null;
    const translatedDatasets = datasets.map((ds) => ({
      ...ds,
      label: sfText(ds.label),
      yAxisLabel: ds.yAxisLabel ? sfText(ds.yAxisLabel) : ds.yAxisLabel,
    }));
    const chart = new Chart(canvasRef.current, {
      type: "line",
      data: {
        labels,
        datasets: translatedDatasets.map((ds, idx) => {
          const isSecondaryAxis = ds.yAxisID === "y1";
          const originalBorderColor = ds.borderColor || [`#2563eb`, `#7c3aed`, `#0891b2`, `#fb7185`][idx % 4];
          const resolvedBorderColor = typography
            ? typography.datasetColor(originalBorderColor, idx, String(ds.label || "").includes(sfText("시작주가 환산")))
            : originalBorderColor;
          return {
            ...ds,
            fill: false,
            borderColor: resolvedBorderColor,
            borderWidth: ds.borderWidth ?? (isSecondaryAxis ? (isMobileChart ? 4.6 : 3.2) : (isMobileChart ? 2.8 : 2.2)),
            borderCapStyle: ds.borderCapStyle || "round",
            borderJoinStyle: ds.borderJoinStyle || "round",
            tension: ds.stepped ? 0 : 0.18,
            spanGaps: ds.spanGaps ?? isSecondaryAxis,
            pointRadius: ds.pointRadius ?? (isSecondaryAxis ? (isMobileChart ? 3.2 : 2.6) : 0),
            pointHoverRadius: ds.pointHoverRadius ?? (isSecondaryAxis ? 7 : (isMobileChart ? 5 : 4)),
            pointHitRadius: ds.pointHitRadius ?? (isSecondaryAxis ? 22 : 16),
            pointBackgroundColor: ds.pointBackgroundColor || resolvedBorderColor,
            pointBorderColor: ds.pointBorderColor || cssVar("--chart-ink", "#ffffff"),
            pointBorderWidth: ds.pointBorderWidth ?? (isSecondaryAxis ? 2 : 1.5),
          };
        }),
      },
      options: {
        ...(typography ? { font: typography.axis } : {}),
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        stacked: false,
        layout: {
          padding: isMobileChart ? { top: 2, right: 6, bottom: 0, left: 0 } : { top: 4, right: 10, bottom: 4, left: 4 },
        },
        plugins: {
          title: {
            display: !!title,
            text: sfText(title),
            color: typography?.ink || cssVar("--chart-ink", "#0f172a"),
            font: typography?.title || { size: isMobileChart ? 14 : 16, weight: "700" },
            align: typography ? "start" : "center",
            padding: { bottom: isMobileChart ? 8 : 12 },
          },
          legend: {
            display: datasets.length > 1,
            position: isMobileChart ? "bottom" : "top",
            labels: { boxWidth: typography ? 18 : 10, boxHeight: typography ? 2 : 10, usePointStyle: !typography, ...(typography ? { font: typography.legend, color: typography.legendInk } : {}) },
          },
          tooltip: {
            displayColors: true,
            titleFont: typography?.legend || { size: 13, weight: "700" },
            bodyFont: typography?.legend || { size: 12 },
            ...(typography ? { backgroundColor: darkChart ? "#1b252d" : "#fff", titleColor: typography.ink, bodyColor: typography.ink, borderColor: darkChart ? "#35434e" : "#bdc5ca", borderWidth: 1, cornerRadius: 0 } : {}),
            callbacks: {
              afterBody: (items) => {
                const item = Array.isArray(items) ? items.find((entry) => Array.isArray(entry.dataset?.anomalyNotes?.[entry.dataIndex])) : null;
                if (!item) return [];
                const notes = item.dataset?.anomalyNotes?.[item.dataIndex];
                if (!Array.isArray(notes) || !notes.length) return [];
                const lines = ["", "⚠ EPS 일회성 후보"];
                notes.slice(0, 4).forEach((note) => {
                  const valueLabel = note?.value_label ? ` ${note.value_label}` : "";
                  lines.push(`• ${note?.label || "일회성 항목"}${valueLabel}`);
                });
                lines.push("GAAP EPS 기준: 공식 조정 EPS는 아님");
                return lines;
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              autoSkip: true,
              maxTicksLimit: isMobileChart ? 4 : 8,
              maxRotation: 0,
              color: typography?.muted || cssVar("--chart-muted", "#64748b"),
              font: typography?.axis || { size: isMobileChart ? 10 : 12 },
              callback: function (value) {
                const label = this.getLabelForValue ? this.getLabelForValue(value) : value;
                return isMobileChart ? formatChartDateTick(label) : label;
              },
            },
            grid: { display: false },
            border: { color: typography?.grid || cssVar("--chart-grid", "rgba(148, 163, 184, 0.35)") },
          },
          y: {
            type: "linear",
            position: "left",
            title: { display: !!yLabel && !isMobileChart, text: sfText(yLabel), ...(typography ? { font: typography.axis, color: typography.muted } : {}) },
            ticks: {
              maxTicksLimit: isMobileChart ? 5 : 6,
              color: typography?.muted || cssVar("--chart-muted", "#64748b"),
              font: typography?.axis || { size: isMobileChart ? 10 : 12 },
              callback: (value) => formatChartTick(value, yLabel),
            },
            grid: { color: typography?.grid || cssVar("--chart-grid", "rgba(148, 163, 184, 0.22)"), drawTicks: false },
            border: { display: false },
          },
          ...(datasets.some((ds) => ds.yAxisID === "y1")
            ? {
                y1: {
                  type: "linear",
                  position: "right",
                  title: { display: !isMobileChart, text: translatedDatasets.find((ds) => ds.yAxisID === "y1")?.yAxisLabel || sfText("보조축") },
                  ticks: {
                    maxTicksLimit: isMobileChart ? 5 : 6,
                    color: translatedDatasets.find((ds) => ds.yAxisID === "y1")?.axisColor || "#059669",
                    font: typography?.axis || { size: isMobileChart ? 10 : 12, weight: isMobileChart ? "700" : "600" },
                    callback: (value) => formatChartTick(value, translatedDatasets.find((ds) => ds.yAxisID === "y1")?.yAxisLabel || ""),
                  },
                  grid: { drawOnChartArea: false },
                  border: { display: false },
                },
              }
            : {}),
        },
      },
    });
    chartRef.current = chart;

    if (typography && document.fonts) {
      document.fonts.ready.then(() => {
        if (chartRef.current === chart) chart.update("none");
      });
    }

    return () => {
      chartRef.current = null;
      chart.destroy();
    };
  }, [labels, datasets, title, yLabel, darkChart]);

  const clampLabelIndex = (value) => Math.max(0, Math.min(labels.length - 1, Math.round(Number(value) || 0)));

  const labelIndexFromClientX = (clientX) => {
    const chart = chartRef.current;
    const canvas = canvasRef.current;
    if (!chart || !canvas || !labels.length) return null;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(chart.scales.x.left, Math.min(chart.scales.x.right, clientX - rect.left));
    return clampLabelIndex(chart.scales.x.getValueForPixel(x));
  };

  const selectionFromClientX = (startClientX, currentClientX) => {
    const chart = chartRef.current;
    const canvas = canvasRef.current;
    if (!chart || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x1 = Math.max(chart.scales.x.left, Math.min(chart.scales.x.right, startClientX - rect.left));
    const x2 = Math.max(chart.scales.x.left, Math.min(chart.scales.x.right, currentClientX - rect.left));
    return {
      left: canvas.offsetLeft + Math.min(x1, x2),
      top: canvas.offsetTop + chart.chartArea.top,
      width: Math.abs(x2 - x1),
      height: Math.max(0, chart.chartArea.bottom - chart.chartArea.top),
    };
  };

  const applyLabelRange = (startIndex, endIndex) => {
    if (typeof onRangeChange !== "function" || startIndex === null || endIndex === null) return;
    const minIndex = Math.min(startIndex, endIndex);
    const maxIndex = Math.max(startIndex, endIndex);
    if (maxIndex - minIndex < 1) return;
    const nextStart = labels[minIndex];
    const nextEnd = labels[maxIndex];
    if (nextStart && nextEnd && nextStart !== nextEnd) {
      onRangeChange(nextStart, nextEnd);
    }
  };

  const applyDateRange = (nextStart, nextEnd) => {
    if (typeof onRangeChange !== "function" || !nextStart || !nextEnd || nextStart === nextEnd) return;
    onRangeChange(nextStart, nextEnd);
  };

  const handleMouseDown = (event) => {
    if (event.button !== 0 || typeof onRangeChange !== "function") return;
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return;
    const startIndex = labelIndexFromClientX(event.clientX);
    if (startIndex === null) return;
    dragRef.current = { startClientX: event.clientX, startIndex, moved: false };
    setSelectionRect(null);
  };

  const handleMouseMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    const width = Math.abs(event.clientX - drag.startClientX);
    drag.moved = drag.moved || width > 6;
    if (drag.moved) {
      event.preventDefault();
      setSelectionRect(selectionFromClientX(drag.startClientX, event.clientX));
    }
  };

  const finishMouseDrag = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    setSelectionRect(null);
    if (!drag.moved) return;
    const endIndex = labelIndexFromClientX(event.clientX);
    applyLabelRange(drag.startIndex, endIndex);
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", finishMouseDrag);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", finishMouseDrag);
    };
  });

  const handleTouchStart = (event) => {
    if (typeof onRangeChange !== "function" || event.touches.length !== 2 || labels.length < 3) return;
    const [a, b] = Array.from(event.touches);
    const dx = Math.abs(a.clientX - b.clientX);
    const dy = Math.abs(a.clientY - b.clientY);
    if (dx < 24 || dx < dy) return;
    const centerIndex = labelIndexFromClientX((a.clientX + b.clientX) / 2);
    const currentStartMs = isoDateToUtcMs(labels[0]);
    const currentEndMs = isoDateToUtcMs(labels[labels.length - 1]);
    if (centerIndex === null || currentStartMs === null || currentEndMs === null) return;
    touchRef.current = {
      startDx: dx,
      currentDx: dx,
      centerMs: isoDateToUtcMs(labels[centerIndex]) || ((currentStartMs + currentEndMs) / 2),
      startSpanMs: Math.max(86400000, currentEndMs - currentStartMs),
      boundsStartMs: isoDateToUtcMs(rangeBounds?.start) ?? currentStartMs,
      boundsEndMs: isoDateToUtcMs(rangeBounds?.end) ?? currentEndMs,
    };
  };

  const handleTouchMove = (event) => {
    const touch = touchRef.current;
    if (!touch || event.touches.length !== 2) return;
    const [a, b] = Array.from(event.touches);
    const dx = Math.abs(a.clientX - b.clientX);
    const dy = Math.abs(a.clientY - b.clientY);
    if (dx < 24 || dx < dy) return;
    touch.currentDx = dx;
    event.preventDefault();
  };

  const handleTouchEnd = (event) => {
    const touch = touchRef.current;
    if (!touch) return;
    if (event.touches.length > 0) return;
    touchRef.current = null;
    const dx = touch.currentDx || touch.startDx;
    if (dx < 24 || touch.startDx < 24) return;
    const scale = Math.max(0.35, Math.min(3.5, dx / touch.startDx));
    if (Math.abs(scale - 1) < 0.08) return;
    const boundsSpan = Math.max(86400000, touch.boundsEndMs - touch.boundsStartMs);
    const nextSpan = Math.max(86400000 * 7, Math.min(boundsSpan, touch.startSpanMs / scale));
    let startMs = touch.centerMs - nextSpan / 2;
    let endMs = touch.centerMs + nextSpan / 2;
    if (startMs < touch.boundsStartMs) {
      endMs += touch.boundsStartMs - startMs;
      startMs = touch.boundsStartMs;
    }
    if (endMs > touch.boundsEndMs) {
      startMs -= endMs - touch.boundsEndMs;
      endMs = touch.boundsEndMs;
    }
    startMs = Math.max(touch.boundsStartMs, startMs);
    endMs = Math.min(touch.boundsEndMs, endMs);
    applyDateRange(utcMsToIsoDate(startMs), utcMsToIsoDate(endMs));
  };

  if (!labels.length || !datasets.length) {
    return <p className="chart-placeholder">{sfText("데이터가 없습니다.")}</p>;
  }

  return (
    <div className={`chart-wrapper${typeof onRangeChange === "function" ? " interactive-chart" : ""}`}>
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={() => { touchRef.current = null; }}
      />
      {selectionRect && selectionRect.width > 1 && (
        <div
          className="chart-range-selection"
          style={{
            left: `${selectionRect.left}px`,
            top: `${selectionRect.top}px`,
            width: `${selectionRect.width}px`,
            height: `${selectionRect.height}px`,
          }}
        />
      )}
    </div>
  );
};


const CandlestickChart = ({ data, title, yLabel }) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const points = useMemo(() => candlePoints(data), [data]);

  const drawChart = () => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper || !points.length) return;
    const rect = wrapper.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(320, Math.floor(rect.width));
    const height = Math.max(260, Math.floor(rect.height));
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const isMobileChart = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
    const padding = isMobileChart
      ? { top: 30, right: 12, bottom: 34, left: 48 }
      : { top: 38, right: 18, bottom: 42, left: 64 };
    const plotWidth = Math.max(1, width - padding.left - padding.right);
    const plotHeight = Math.max(1, height - padding.top - padding.bottom);
    const lows = points.map((point) => point.low);
    const highs = points.map((point) => point.high);
    const minPrice = Math.min(...lows);
    const maxPrice = Math.max(...highs);
    const priceRange = Math.max(1e-9, maxPrice - minPrice);
    const paddedMin = minPrice - priceRange * 0.06;
    const paddedMax = maxPrice + priceRange * 0.06;
    const paddedRange = Math.max(1e-9, paddedMax - paddedMin);
    const xStep = plotWidth / Math.max(points.length, 1);
    const candleWidth = Math.max(2, Math.min(12, xStep * 0.62));
    const toX = (idx) => padding.left + xStep * idx + xStep / 2;
    const toY = (value) => padding.top + ((paddedMax - value) / paddedRange) * plotHeight;

    ctx.save();
    ctx.fillStyle = cssVar("--chart-ink", "#0f172a");
    ctx.font = `${isMobileChart ? 14 : 16}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    if (title) ctx.fillText(sfText(title), width / 2, 4);
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = cssVar("--chart-grid", "rgba(148, 163, 184, 0.22)");
    ctx.lineWidth = 1;
    ctx.fillStyle = cssVar("--chart-muted", "#64748b");
    ctx.font = `${isMobileChart ? 10 : 12}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    const yTicks = isMobileChart ? 4 : 5;
    for (let i = 0; i <= yTicks; i += 1) {
      const value = paddedMin + (paddedRange * i) / yTicks;
      const y = toY(value);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
      ctx.fillText(formatChartTick(value, yLabel), padding.left - 8, y);
    }
    ctx.strokeStyle = "rgba(148, 163, 184, 0.35)";
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top + plotHeight);
    ctx.lineTo(width - padding.right, padding.top + plotHeight);
    ctx.stroke();

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    const xTicks = Math.min(isMobileChart ? 4 : 7, points.length);
    for (let i = 0; i < xTicks; i += 1) {
      const idx = Math.round((points.length - 1) * (i / Math.max(1, xTicks - 1)));
      const x = toX(idx);
      const label = isMobileChart ? formatChartDateTick(points[idx].date) : points[idx].date;
      ctx.fillText(label, x, padding.top + plotHeight + 10);
    }
    ctx.restore();

    points.forEach((point, idx) => {
      const x = toX(idx);
      const openY = toY(point.open);
      const closeY = toY(point.close);
      const highY = toY(point.high);
      const lowY = toY(point.low);
      const rising = point.close >= point.open;
      const color = rising ? cssVar("--chart-rise", "#dc2626") : cssVar("--chart-fall", "#2563eb");
      const bodyTop = Math.min(openY, closeY);
      const bodyHeight = Math.max(1.5, Math.abs(closeY - openY));
      ctx.strokeStyle = color;
      ctx.fillStyle = rising ? cssVar("--chart-rise", "rgba(220, 38, 38, 0.82)") : cssVar("--chart-fall", "rgba(37, 99, 235, 0.82)");
      ctx.lineWidth = Math.max(1, Math.min(2, candleWidth / 4));
      ctx.beginPath();
      ctx.moveTo(x, highY);
      ctx.lineTo(x, lowY);
      ctx.stroke();
      ctx.fillRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight);
      ctx.strokeRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight);
    });
  };

  useEffect(() => {
    drawChart();
    const handleResize = () => drawChart();
    // 2026-09-19 다크모드: 테마 토글 시 CSS 변수 색으로 캔버스 재렌더
    const handleThemeChange = () => drawChart();
    window.addEventListener("resize", handleResize);
    window.addEventListener("sfthemechange", handleThemeChange);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("sfthemechange", handleThemeChange);
    };
  }, [points, title, yLabel]);

  const resolvePointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const wrapper = wrapperRef.current;
    if (!canvas || !wrapper || !points.length) return null;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;
    const isMobileChart = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
    const padding = isMobileChart
      ? { top: 30, right: 12, bottom: 34, left: 48 }
      : { top: 38, right: 18, bottom: 42, left: 64 };
    const plotWidth = Math.max(1, width - padding.left - padding.right);
    if (x < padding.left || x > width - padding.right || y < padding.top || y > height - padding.bottom) return null;
    const idx = Math.max(0, Math.min(points.length - 1, Math.floor(((x - padding.left) / plotWidth) * points.length)));
    const point = points[idx];
    if (!point) return null;
    const tooltipWidth = 224;
    const tooltipHeight = 136;
    const left = Math.min(Math.max(8, x + 12), Math.max(8, width - tooltipWidth - 8));
    const top = Math.min(Math.max(8, y - tooltipHeight - 10), Math.max(8, height - tooltipHeight - 8));
    return { point, left, top };
  };

  const handlePointerMove = (event) => {
    const next = resolvePointFromEvent(event);
    setTooltip(next);
  };

  const handlePointerLeave = () => setTooltip(null);

  if (!points.length) {
    return <p className="chart-placeholder">{sfText("데이터가 없습니다.")}</p>;
  }

  return (
    <div
      ref={wrapperRef}
      className="chart-wrapper candlestick-chart-wrapper"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <canvas ref={canvasRef} aria-label={sfText("캔들 차트")} />
      {tooltip && (
        <div className="candle-tooltip" style={{ left: `${tooltip.left}px`, top: `${tooltip.top}px` }}>
          <strong>{tooltip.point.date}</strong>
          <span>Open {formatCandlePrice(tooltip.point.open)}</span>
          <span>High {formatCandlePrice(tooltip.point.high)}</span>
          <span>Low {formatCandlePrice(tooltip.point.low)}</span>
          <span>Close {formatCandlePrice(tooltip.point.close)}</span>
          {tooltip.point.volume !== null && <span>Volume {formatCandleVolume(tooltip.point.volume)}</span>}
        </div>
      )}
    </div>
  );
};

const METRIC_GROUPS = [
  {
    key: "performance",
    title: "성과",
    subtitle: "조회 기간 동안 가격이 실제로 얼마나 성장했는지 봅니다.",
    metrics: [
      { key: "Total Return", label: "누적 수익률", description: "기간 시작가 대비 마지막 가격의 총 수익률", direction: "높을수록 좋음" },
      { key: "CAGR", label: "연환산 수익률", description: "보유 기간 수익률을 연 단위로 환산한 성장률", direction: "높을수록 좋음" },
    ],
  },
  {
    key: "risk",
    title: "위험 조정",
    subtitle: "수익률이 얼마나 큰 낙폭과 변동성을 감수하고 만들어졌는지 확인합니다.",
    metrics: [
      { key: "Max Drawdown", label: "최대 낙폭", description: "고점 대비 가장 깊었던 하락폭", direction: "절댓값이 작을수록 안정적" },
      { key: "Volatility", label: "연환산 변동성", description: "일별 수익률 변동성을 연 단위로 환산", direction: "낮을수록 안정적" },
      { key: "Sharpe Ratio", label: "샤프 비율", description: "전체 변동성 대비 초과 성과", direction: "높을수록 효율적" },
      { key: "Sortino Ratio", label: "소르티노 비율", description: "하방 변동성만 고려한 위험 조정 성과", direction: "높을수록 효율적" },
    ],
  },
  {
    key: "valuation",
    title: "밸류에이션",
    subtitle: "현재 가격이 이익·자본 대비 어느 정도 프리미엄을 받고 있는지 봅니다.",
    metrics: [
      { key: "PER", label: "PER", description: "최근 이익 대비 주가 배수", direction: "낮을수록 저평가 단서" },
      { key: "PER(FWD)", label: "Forward PER", description: "예상 이익 기준 주가 배수", direction: "낮을수록 저평가 단서" },
      { key: "PBR", label: "PBR", description: "순자산 대비 주가 배수", direction: "낮을수록 저평가 단서" },
      { key: "FCF Yield", label: "FCF Yield", description: "시가총액 대비 잉여현금흐름 수익률", direction: "높을수록 현금창출 매력" },
    ],
  },
  {
    key: "size",
    title: "규모",
    subtitle: "기업의 시장 평가 규모를 별도로 확인합니다.",
    metrics: [
      { key: "Market Cap", label: "시가총액", description: "주가와 발행주식수로 계산된 시장 가치", direction: "규모 판단 참고" },
    ],
  },
  {
    key: "profitability",
    title: "수익성·성장성",
    subtitle: "회사가 자본·자산을 얼마나 효율적으로 이익화하고 매출을 키우는지 확인합니다.",
    metrics: [
      { key: "ROE", label: "ROE", description: "자기자본 대비 이익 창출력", direction: "높을수록 좋음" },
      { key: "ROA", label: "ROA", description: "총자산 대비 이익 창출력", direction: "높을수록 좋음" },
      { key: "Revenue Growth", label: "Revenue Growth", description: "최근 매출 성장률", direction: "높을수록 성장성 우수" },
    ],
  },
  {
    key: "shareholderReturn",
    title: "주주환원",
    subtitle: "현금 배당으로 돌려주는 수익률을 확인합니다.",
    metrics: [
      { key: "Dividend Yield", label: "배당수익률", description: "현재가 대비 기억해 둔 연간 배당액 기준 수익률", direction: "높을수록 배당 매력" },
    ],
  },
  {
    key: "stability",
    title: "안정성",
    subtitle: "재무 레버리지 부담이 어느 정도인지 봅니다.",
    metrics: [
      { key: "Debt/Equity", label: "부채비율", description: "자기자본 대비 부채 비율", direction: "낮을수록 재무 부담 작음" },
    ],
  },
];

const metricValueMap = (metrics) => {
  const map = new Map();
  (metrics || []).forEach((metric) => {
    const key = String(metric?.metric || "").trim();
    if (key) map.set(key, metric);
  });
  return map;
};

const MetricRow = ({ definition, metric }) => {
  const hasValue = metric && metric.value !== null && metric.value !== undefined && !Number.isNaN(Number(metric.value));
  return (
    <tr className={!hasValue ? "is-muted" : ""}>
      <th scope="row">
        <span className="metric-table-label">{sfText(definition.label)}</span>
        <span className="metric-table-key">{definition.key}</span>
      </th>
      <td className="metric-table-value">{hasValue ? formatMetric(definition.key, metric.value) : "N/A"}</td>
    </tr>
  );
};

const METRIC_SECTIONS = [
  {
    key: "performance",
    title: "성과 지표",
    subtitle: "선택한 기간의 가격 데이터로 계산한 성과와 위험 조정 지표입니다.",
    groupKeys: ["performance", "risk"],
  },
  {
    key: "fundamental",
    title: "펀더멘털 지표",
    subtitle: "기업의 밸류에이션, 수익성, 성장성, 안정성, 주주환원을 보여주는 재무 기반 지표입니다.",
    groupKeys: ["valuation", "size", "profitability", "shareholderReturn", "stability", "etc"],
  },
];

const MetricsGrid = ({ metrics, mode = "full" }) => {
  if (!metrics.length) {
    return null;
  }
  const values = metricValueMap(metrics);
  const usedKeys = new Set(METRIC_GROUPS.flatMap((group) => group.metrics.map((metric) => metric.key)));
  const extraMetrics = metrics
    .filter((metric) => metric?.metric && !usedKeys.has(String(metric.metric)))
    .map((metric) => ({ key: String(metric.metric), label: String(metric.metric) }));
  const baseGroups = mode === "performanceOnly"
    ? METRIC_GROUPS.filter((group) => ["performance", "risk"].includes(group.key))
    : METRIC_GROUPS;
  const groups = extraMetrics.length && mode !== "performanceOnly"
    ? [...baseGroups, { key: "etc", title: "기타", subtitle: "데이터 소스에서 추가로 제공된 참고 지표입니다.", metrics: extraMetrics }]
    : baseGroups;
  const groupMap = new Map(groups.map((group) => [group.key, group]));
  const renderGroup = (group) => {
    const visibleDefinitions = group.metrics.filter((definition) => values.has(definition.key));
    if (!visibleDefinitions.length) return null;
    return (
      <div key={group.key} className="metric-group" role="group" aria-labelledby={`metric-group-${group.key}`}>
        <div className="metric-group-heading">
          <h4 id={`metric-group-${group.key}`}>{sfText(group.title)}</h4>
          <p>{sfText(group.subtitle)}</p>
        </div>
        <div className="metrics-table-wrap">
          <table className="metrics-table">
            <thead>
              <tr>
                <th scope="col">{sfText("지표")}</th>
                <th scope="col">{sfText("값")}</th>
              </tr>
            </thead>
            <tbody>
              {visibleDefinitions.map((definition) => (
                <MetricRow
                  key={definition.key}
                  definition={sfMetricDefinition(definition)}
                  metric={values.get(definition.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="metrics-panel" aria-label="핵심 투자 지표">
      <div className="metrics-panel-header">
        <div>
          <p className="eyebrow">Key Metrics</p>
          <h3>{sfText("핵심 지표 요약")}</h3>
        </div>
        <p>{sfText(mode === "performanceOnly" ? "포트폴리오 합산 가치의 가격 흐름으로 계산한 성과와 위험 지표입니다." : "성과 지표와 펀더멘털 지표를 구분해 가격 흐름과 기업 기초체력을 함께 확인합니다.")}</p>
      </div>
      <div className="metric-section-stack">
        {METRIC_SECTIONS.filter((section) => mode !== "performanceOnly" || section.key === "performance").map((section) => {
          const renderedGroups = section.groupKeys.map((key) => groupMap.get(key)).filter(Boolean).map(renderGroup).filter(Boolean);
          if (!renderedGroups.length) return null;
          return (
            <div key={section.key} className={`metric-section metric-section-${section.key}`}>
              <div className="metric-section-heading">
                <h4>{sfText(section.title)}</h4>
                <p>{sfText(section.subtitle)}</p>
              </div>
              <div className="metric-group-stack">
                {renderedGroups}
              </div>
            </div>
          );
        })}
      </div>
      {mode !== "performanceOnly" && (
        <div className="metrics-footnotes" aria-label={sfText("데이터 출처와 산식 안내")}>
          <p className="metrics-footnote">{sfText("PER/PBR/ROE/ROA/시가총액/배당수익률/부채비율/Revenue Growth/FCF Yield는 제공사 데이터가 있는 개별주에만 표시되며, ETF·지수형 상품은 성과·위험 지표 중심으로 표시됩니다.")}</p>
          <p className="metrics-footnote metrics-source-note">{sfText("데이터 출처/산식: 가격·성과 지표는 선택 기간 가격 데이터로 계산합니다. 펀더멘털은 Yahoo Finance(yfinance)를 우선 사용하고, 한국 6자리 종목은 Naver Finance/OpenDART로 보강합니다. 배당수익률=캐시된 연간 배당액÷현재가, 시가총액=현재가×발행주식수 또는 제공사 marketCap. 모든 값은 참고용입니다.")}</p>
        </div>
      )}
    </div>
  );
};

const AnalysisSummary = ({ metrics }) => {
  if (!Array.isArray(metrics) || !metrics.length) return null;
  const values = metricValueMap(metrics);
  const items = [
    ["Total Return", "누적 수익률"],
    ["CAGR", "연환산 수익률"],
    ["Max Drawdown", "최대 낙폭"],
    ["Volatility", "연환산 변동성"],
    ["Sharpe Ratio", "샤프 비율"],
  ].map(([key, label]) => ({ key, label, metric: values.get(key) })).filter((item) => item.metric);
  if (!items.length) return null;
  return (
    <section className="analysis-summary" aria-label={sfText("주요 성과")}>
      {items.map(({ key, label, metric }) => (
        <div className="analysis-summary-item" key={key}>
          <span>{sfText(label)}</span>
          <strong>{formatMetric(key, metric.value)}</strong>
        </div>
      ))}
    </section>
  );
};

const CompareMetricsTable = ({ items, tickerNameMap }) => {
  if (!Array.isArray(items) || items.length < 2) {
    return null;
  }
  const metricNames = Array.from(
    new Set(
      items.flatMap((item) =>
        Array.isArray(item?.metrics) ? item.metrics.map((metric) => String(metric.metric || "")) : [],
      ),
    ),
  ).filter(Boolean);

  if (!metricNames.length) {
    return null;
  }

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            {items.map((item) => (
              <th key={item.ticker}>{tickerDisplayName(item.ticker, tickerNameMap)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metricNames.map((metricName) => {
            return (
              <tr key={metricName}>
                <td>{metricName}</td>
                {items.map((item) => {
                  const metric = (item.metrics || []).find((m) => m.metric === metricName);
                  return (
                    <td key={`${item.ticker}-${metricName}`}>
                      {metric ? formatMetric(metricName, metric.value) : "N/A"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const formatTickerLabel = (value) => {
  if (!value) {
    return "";
  }
  return value
    .replace(/^.*[\\/]/, "")
    .replace(/\.db$/i, "")
    .replace(/\s*\([^)]*\)\s*$/, "");
};

const normalizeTickerKey = (value) => formatTickerLabel(value).trim().toUpperCase();

const isValidTickerQuery = (value) => {
  const key = normalizeTickerKey(value);
  if (!key) return false;
  return /^\^[A-Z0-9]{1,12}$/.test(key) || /^[0-9]{6}$/.test(key) || /^[0-9]{4}[0-9A-Z]{2}$/.test(key) || /^[A-Z]{1,6}([._-][A-Z0-9]{1,8})?$/.test(key);
};

const isTickerInList = (ticker, list) => !!ticker && (list.includes(ticker) || isValidTickerQuery(ticker));

const dedupeAndSortTickerKeys = (values) =>
  Array.from(new Set((values || []).filter(Boolean))).sort((a, b) => a.localeCompare(b, "en"));

const normalizeTickerList = (values) =>
  dedupeAndSortTickerKeys((Array.isArray(values) ? values : []).map((value) => normalizeTickerKey(value)));

const resolveTickerListFromApi = (apiTickers, tickerNameMap) => {
  const normalized = normalizeTickerList(apiTickers);
  const metadataKeys = normalizeTickerList(Object.keys(tickerNameMap || {}));

  if (!metadataKeys.length) {
    return normalized;
  }
  if (!normalized.length) {
    return metadataKeys;
  }

  const lone = normalized[0];
  if (
    normalized.length === 1 &&
    metadataKeys.length > 1 &&
    !metadataKeys.includes(lone) &&
    (lone === "MARKET" || lone === "PRICES")
  ) {
    return metadataKeys;
  }

  return normalized;
};

const parseTickerKeyFromInput = (input) => {
  const trimmed = (input || "").trim();
  if (!trimmed) return "";

  const parenMatch = trimmed.match(/\(([^)]+)\)\s*$/);
  if (parenMatch) {
    const candidate = parenMatch[1].trim();
    if (candidate) return candidate.toUpperCase();
  }

  // Korean tickers are often 6 digits; prefer this if present.
  const digits = trimmed.match(/\b\d{6}\b/);
  if (digits) return digits[0];

  // Fallback: use the full string (for US tickers like QQQ, SPY, ...)
  return trimmed.toUpperCase();
};

const tickerInputToQueryTicker = (input) => {
  const key = parseTickerKeyFromInput(input);
  return isValidTickerQuery(key) ? key : "";
};

const INDEX_TICKER_NAMES = Object.freeze({
  "^DJI": "Dow Jones", "^GDAXI": "DAX", "^GSPC": "S&P 500", "^HSI": "Hang Seng",
  "^KS11": "KOSPI", "^N225": "Nikkei 225", "^RUT": "Russell 2000", "^STOXX50E": "EURO STOXX 50",
});
// Current user-selected Korean default; explicit ticker/share links always win.
const ANALYSIS_DEFAULT_TICKER = "005930";
const analysisInitialTicker = (search) => {
  const params = new URLSearchParams(search || "");
  if (!params.has("t") && !params.has("ticker")) return ANALYSIS_DEFAULT_TICKER;
  const raw = (params.has("t") ? params.get("t") : params.get("ticker")) || "";
  return isValidTickerQuery(raw) ? normalizeTickerKey(raw) : "";
};

const tickerDisplayName = (tickerValue, tickerNameMap) => {
  const key = normalizeTickerKey(tickerValue);
  if (key === CASH_TICKER) return CASH_DISPLAY_NAME;
  const name = INDEX_TICKER_NAMES[key] || tickerNameMap?.[key];
  return name || key;
};

const tickerInputDisplayName = (tickerValue, tickerNameMap) => {
  const key = normalizeTickerKey(tickerValue);
  if (!key) return "";
  if (key === CASH_TICKER) return CASH_DISPLAY_NAME;
  const name = INDEX_TICKER_NAMES[key] || tickerNameMap?.[key];
  return name ? `${name} (${key})` : key;
};

const tickerTagsFor = (tickerValue, tickerTagMap) => {
  const key = normalizeTickerKey(tickerValue);
  return tickerTagMap?.[key] || [];
};

const findTickerByInput = (input, list, tickerNameMap, tickerAliasMap = {}) => {
  const trimmed = (input || "").trim();
  if (!trimmed) return "";

  const normalizedLower = trimmed.toLowerCase();
  const direct =
    list.find((ticker) => formatTickerLabel(ticker).toLowerCase() === normalizedLower) || "";
  if (direct) return direct;

  const key = parseTickerKeyFromInput(trimmed);
  const byKey =
    list.find((ticker) => normalizeTickerKey(ticker) === key) ||
    list.find((ticker) => normalizeTickerKey(ticker).toLowerCase() === normalizedLower);
  if (byKey) return byKey;

  const byName = list.find((ticker) => {
    const tickerKey = normalizeTickerKey(ticker);
    const names = uniqueNonEmptyStrings([INDEX_TICKER_NAMES[tickerKey], tickerNameMap?.[tickerKey], ...(tickerAliasMap?.[tickerKey] || [])]);
    return names.some((name) => name.toLowerCase() === normalizedLower);
  });
  return byName || "";
};

const matchesSelectedTags = (tickerValue, selectedTags, mode, tickerTagMap) => {
  if (!selectedTags || selectedTags.length === 0) return true;
  const tags = tickerTagsFor(tickerValue, tickerTagMap).map((t) => String(t).toLowerCase());
  const needles = selectedTags.map((t) => String(t).toLowerCase());
  if (mode === "AND") {
    return needles.every((tag) => tags.includes(tag));
  }
  return needles.some((tag) => tags.includes(tag));
};

const RECENT_TICKER_STORAGE_KEY = "stockforum.guest-recent-tickers.v1";
const mergeRecentTickerKeys = (...groups) => {
  const seen = new Set();
  const result = [];
  for (const group of groups) for (const raw of Array.isArray(group) ? group : []) {
    if (typeof raw !== "string") continue;
    const key = normalizeTickerKey(raw);
    if (!isValidTickerQuery(key) || seen.has(key)) continue;
    seen.add(key); result.push(key);
    if (result.length >= 40) return result;
  }
  return result;
};
const loadLocalRecentTickers = () => {
  try { return mergeRecentTickerKeys(JSON.parse(window.localStorage.getItem(RECENT_TICKER_STORAGE_KEY) || "[]")); }
  catch (_) { return []; }
};
const saveLocalRecentTickers = (items) => {
  try { window.localStorage.setItem(RECENT_TICKER_STORAGE_KEY, JSON.stringify(mergeRecentTickerKeys(items))); }
  catch (_) { /* Storage-disabled browsers retain in-session recents. */ }
};
const tickerAutocompleteLabel = (item) => item.tickerKey.startsWith("^") && item.name ? item.name : item.tickerKey;
const buildTickerSuggestions = (filteredTickers, inputValue, tickerNameMap, tickerAliasMap, recentTickerKeys, recentOnlyWhenEmpty = false, allowUnlistedRecent = true) => {
  const query = (inputValue || "").trim().toLowerCase();
  const universe = new Map(filteredTickers.map((ticker) => [normalizeTickerKey(ticker), ticker]));
  const toItem = (ticker) => {
    const tickerKey = normalizeTickerKey(ticker);
    const name = INDEX_TICKER_NAMES[tickerKey] || tickerNameMap?.[tickerKey] || "";
    return { ticker, tickerKey, name, aliases: uniqueNonEmptyStrings([...(tickerAliasMap?.[tickerKey] || []), name]) };
  };
  const matches = (item) => !query || item.tickerKey === parseTickerKeyFromInput(inputValue)
    || item.tickerKey.toLowerCase().includes(query) || item.aliases.some((alias) => alias.toLowerCase().includes(query));
  const keys = mergeRecentTickerKeys(recentTickerKeys);
  const recent = keys.filter((key) => allowUnlistedRecent || universe.has(key))
    .map((key) => toItem(universe.get(key) || key)).filter(matches).slice(0, 30)
    .map((item) => ({ ...item, section: "recent" }));
  if (!query && recentOnlyWhenEmpty) return recent;
  const recentSet = new Set(keys);
  const rest = filteredTickers.map(toItem).filter(matches).filter((item) => !recentSet.has(item.tickerKey))
    .slice(0, Math.max(0, 40 - recent.length)).map((item) => ({ ...item, section: "all" }));
  return [...recent, ...rest];
};

const TickerAutocompleteInput = ({
  label,
  inputValue,
  onInputChange,
  tickers,
  tickerNameMap,
  tickerAliasMap = {},
  selectedTags,
  tagMode,
  tickerTagMap,
  onOpenTagSearch,
  placeholder,
  className = "",
  showFooter = true,
  recentTickerKeys = [],
  recentOnlyWhenEmpty = false,
  savedPortfolioItems = [],
  portfolioTickerMembership = {},
  activePortfolioStarTicker = "",
  portfolioQuickAddMessage = "",
  portfolioQuickAddError = "",
  loadingSavedPortfolios = false,
  onPortfolioStarClick,
  onPortfolioQuickAdd,
  onEnterKey,
  inputRef,
}) => {
  const [open, setOpen] = useState(false);
  const [showFocusRecents, setShowFocusRecents] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const enterKeyRef = useRef(onEnterKey);
  enterKeyRef.current = onEnterKey;
  const containerRef = React.useRef(null);

  const filteredTickers = useMemo(() => {
    if (!selectedTags.length) return tickers;
    return tickers.filter((ticker) => matchesSelectedTags(ticker, selectedTags, tagMode, tickerTagMap));
  }, [tickers, selectedTags, tagMode, tickerTagMap]);

  const suggestions = useMemo(() => buildTickerSuggestions(
    filteredTickers, showFocusRecents ? "" : inputValue, tickerNameMap, tickerAliasMap, recentTickerKeys,
    recentOnlyWhenEmpty, selectedTags.length === 0,
  ), [filteredTickers, inputValue, tickerNameMap, tickerAliasMap, recentTickerKeys, recentOnlyWhenEmpty, selectedTags.length, showFocusRecents]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event) => {
      if (!containerRef.current || !containerRef.current.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const pickSuggestion = (item) => {
    if (!item) return;
    const display = item.name ? `${item.name} (${item.tickerKey})` : item.tickerKey;
    onInputChange(display, { commit: true, tickerKey: item.tickerKey });
    setOpen(false);
    setActiveIndex(-1);
  };

  const committedTicker = findTickerByInput(inputValue, filteredTickers, tickerNameMap, tickerAliasMap);
  const committedDisplay = committedTicker ? tickerInputDisplayName(committedTicker, tickerNameMap) : "";
  const isCommittedInputDisplay = Boolean(
    committedDisplay && String(inputValue || "").trim() === committedDisplay,
  );
  const shouldShowAutocompleteMenu = open && (showFocusRecents || !isCommittedInputDisplay);

  const footerText = selectedTags.length ? `선택 태그 ${selectedTags.length}개 (${tagMode})` : "태그 검색";
  const hasPortfolioQuickAdd = typeof onPortfolioStarClick === "function" && typeof onPortfolioQuickAdd === "function";

  return (
    <label ref={containerRef} className={className}>
      {label}
      <input
        ref={inputRef}
        type="search"
        value={inputValue}
        onChange={(e) => {
          setShowFocusRecents(false);
          setActiveIndex(-1);
          onInputChange(e.target.value, { commit: false });
          setOpen(true);
        }}
        onFocus={() => {
          setShowFocusRecents(recentOnlyWhenEmpty && !!findTickerByInput(inputValue, filteredTickers, tickerNameMap, tickerAliasMap));
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setOpen(true);
            return;
          }
          if (!open) {
            if (e.key === "Enter" && onEnterKey) {
              e.preventDefault();
              onEnterKey();
            }
            return;
          }
          if (e.key === "Escape") {
            // 2026-09-19: type="search" 입력에서 Esc는 Chrome이 네이티브로 값을 지운 뒤
            // onChange가 메뉴를 재오픈한다. preventDefault로 네이티브 클리어를 막아야
            // Esc가 메뉴 닫기로 동작한다.
            e.preventDefault();
            setOpen(false);
            setActiveIndex(-1);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, 0));
            return;
          }
          if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) {
              pickSuggestion(suggestions[activeIndex]);
              if (onEnterKey) {
                window.setTimeout(() => enterKeyRef.current?.(), 0);
              }
              return;
            }
            if (onEnterKey) {
              onEnterKey();
            }
          }
        }}
        placeholder={placeholder}
      />
      {shouldShowAutocompleteMenu && (
        <div className={`autocomplete-menu${activePortfolioStarTicker ? " has-portfolio-popover" : ""}`} role="listbox">
          <ul className={`autocomplete-list${activePortfolioStarTicker ? " has-portfolio-popover" : ""}`}>
            {suggestions.length ? (
              suggestions.map((item, idx) => {
                const showRecentHeader = item.section === "recent" && (idx === 0 || suggestions[idx - 1]?.section !== "recent");
                const showAllHeader = item.section === "all" && idx > 0 && suggestions[idx - 1]?.section === "recent";
                return (
                  <React.Fragment key={`${item.ticker}-${idx}`}>
                    {showRecentHeader && <li className="autocomplete-section-label">최근 검색</li>}
                    {showAllHeader && <li className="autocomplete-section-label">전체 종목</li>}
                    <li
                      className={`autocomplete-item${idx === activeIndex ? " active" : ""}`}
                      data-ticker={item.tickerKey}
                      title={item.tickerKey}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(item);
                      }}
                    >
                      <span className="ticker">{tickerAutocompleteLabel(item)}</span>
                      <span className="name">{item.tickerKey.startsWith("^") ? "" : item.name || ""}</span>
                      {hasPortfolioQuickAdd && (
                        <span className="autocomplete-portfolio-slot">
                          <button
                            type="button"
                            className={`autocomplete-portfolio-star${portfolioTickerMembership[item.tickerKey] ? " saved" : ""}`}
                            aria-label={`${item.tickerKey} 포트폴리오에 추가`}
                            title={portfolioTickerMembership[item.tickerKey] ? "저장 포트폴리오에 포함됨" : "저장 포트폴리오에 추가"}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onPortfolioStarClick(item.tickerKey);
                            }}
                          >
                            {portfolioTickerMembership[item.tickerKey] ? "★" : "☆"}
                          </button>
                          {activePortfolioStarTicker === item.tickerKey && (
                            <div
                              className="autocomplete-portfolio-popover"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="autocomplete-portfolio-title">내 포트폴리오 저장 목록</div>
                              {loadingSavedPortfolios ? (
                                <div className="autocomplete-portfolio-empty">불러오는 중...</div>
                              ) : savedPortfolioItems.length ? (
                                savedPortfolioItems.map((portfolio) => {
                                  const portfolioId = String(portfolio?.id || "");
                                  const portfolioName = String(portfolio?.name || "포트폴리오");
                                  const alreadySaved = (portfolio.holdings || []).some((holding) => normalizeTickerKey(holding?.ticker) === item.tickerKey);
                                  return (
                                    <button
                                      key={portfolioId}
                                      type="button"
                                      className="autocomplete-portfolio-option"
                                      disabled={!portfolioId || alreadySaved}
                                      onClick={() => onPortfolioQuickAdd(portfolioId, item.tickerKey)}
                                    >
                                      <span>{portfolioName}</span>
                                      <small>{alreadySaved ? "이미 추가됨" : "추가"}</small>
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="autocomplete-portfolio-empty">저장 포트 없음</div>
                              )}
                              {portfolioQuickAddMessage && <div className="autocomplete-portfolio-message ok">{portfolioQuickAddMessage}</div>}
                              {portfolioQuickAddError && <div className="autocomplete-portfolio-message bad">{portfolioQuickAddError}</div>}
                            </div>
                          )}
                        </span>
                      )}
                    </li>
                  </React.Fragment>
                );
              })
            ) : (
              <li className="autocomplete-item" style={{ cursor: "default" }}>
                <span className="name">{recentOnlyWhenEmpty && !String(inputValue || "").trim() ? "최근 조회 내역이 없습니다. 종목명이나 티커를 입력하세요." : "일치하는 항목이 없습니다."}</span>
              </li>
            )}
          </ul>
          {showFooter && (
            <div className="autocomplete-footer">
              <a
                href="#tag-search"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  setActiveIndex(-1);
                  onOpenTagSearch();
                }}
              >
                {footerText}
              </a>
              <a
                href="#tag-search"
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  setActiveIndex(-1);
                  onOpenTagSearch();
                }}
              >
                더보기
              </a>
            </div>
          )}
        </div>
      )}
    </label>
  );
};

const TagSearchPanel = ({
  open,
  tagsByCategory,
  selectedTags,
  setSelectedTags,
  mode,
  setMode,
  recommended,
  tickerNameMap,
  onPickTickerKey,
  onClose,
}) => {
  if (!open) return null;
  const categoryOrder = ["구분", "국가", "테마"];
  const categories = [
    ...categoryOrder.filter((category) => Object.prototype.hasOwnProperty.call(tagsByCategory || {}, category)),
    ...Object.keys(tagsByCategory || {}).filter((category) => !categoryOrder.includes(category)),
  ];
  const selectedSet = new Set(selectedTags);
  const toggleTag = (tag) => {
    if (selectedSet.has(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  return (
    <div className="tag-panel">
      <div className="tag-panel-header">
        <h3>태그 검색</h3>
        <button type="button" className="ghost-button" style={{ marginTop: 0 }} onClick={onClose}>
          닫기
        </button>
      </div>
      <div className="tag-panel-body">
        <div className="tag-toolbar">
          <label style={{ width: "240px" }}>
            추천 기준
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="AND">AND (모든 태그 포함)</option>
              <option value="OR">OR (하나라도 포함)</option>
            </select>
          </label>
          <span className="pill">선택: {selectedTags.length}개</span>
          {selectedTags.length > 0 && (
            <button
              type="button"
              className="ghost-button"
              style={{ marginTop: 0 }}
              onClick={() => setSelectedTags([])}
            >
              선택 해제
            </button>
          )}
        </div>

        <div className="tag-sections">
          {categories.map((category) => {
            const tags = Array.from(new Set(tagsByCategory[category] || []));
            return (
              <div key={category} className={`tag-section${category === "테마" ? " tag-section-wide" : ""}`}>
                <h4>{category}</h4>
                <div className="tag-list">
                  {tags.map((tag) => (
                    <label key={`${category}-${tag}`} className="tag-item">
                      <input type="checkbox" checked={selectedSet.has(tag)} onChange={() => toggleTag(tag)} />
                      <span>{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="recommendations">
          <h4>추천 종목</h4>
          {recommended.length ? (
            <div className="recommendation-grid">
              {recommended.slice(0, 30).map((tickerKey) => (
                <button
                  key={tickerKey}
                  type="button"
                  className="recommendation-button"
                  onClick={() => onPickTickerKey(tickerKey)}
                >
                  <span>{tickerNameMap[tickerKey] || tickerKey}</span>
                  <span className="sub">{tickerKey}</span>
                </button>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, color: "#475569" }}>일치하는 종목이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const priceLabels = (data) =>
  data.map((point) => String(point.date).slice(0, 10));

const priceValues = (data) =>
  data.map((point) =>
    point.adj_close ?? point.close ?? point.open ?? point.high ?? point.low ?? null,
  );

const candlePoints = (data) =>
  (Array.isArray(data) ? data : [])
    .map((point) => {
      const date = String(point?.date || "").slice(0, 10);
      const open = Number(point?.open);
      const high = Number(point?.high);
      const low = Number(point?.low);
      const close = Number(point?.close);
      const volume = Number(point?.volume);
      if (!date || ![open, high, low, close].every(Number.isFinite)) return null;
      return {
        date,
        open,
        high,
        low,
        close,
        volume: Number.isFinite(volume) ? volume : null,
      };
    })
    .filter(Boolean);

const sampleStdDev = (values) => {
  const clean = values.filter((value) => Number.isFinite(value));
  if (clean.length < 2) return null;
  const mean = clean.reduce((sum, value) => sum + value, 0) / clean.length;
  const variance = clean.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / (clean.length - 1);
  return Math.sqrt(variance);
};

const inferPeriodsPerYearFromSeries = (series) => {
  if (!Array.isArray(series) || series.length < 2) return 252;
  const first = new Date(`${series[0].date}T00:00:00`);
  const last = new Date(`${series[series.length - 1].date}T00:00:00`);
  const totalDays = (last - first) / (24 * 60 * 60 * 1000);
  if (!(totalDays > 0)) return 252;
  const years = totalDays / 365.25;
  if (!(years > 0)) return 252;
  return Math.max(1, series.length / years);
};

const computeSeriesMetrics = (series) => {
  const clean = (series || [])
    .map((point) => ({ date: point.date, close: Number(point.close) }))
    .filter((point) => point.date && Number.isFinite(point.close) && point.close > 0)
    .sort((a, b) => a.date.localeCompare(b.date));

  const emptyMetrics = [
    { metric: "Total Return", value: null },
    { metric: "CAGR", value: null },
    { metric: "Volatility", value: null },
    { metric: "Max Drawdown", value: null },
    { metric: "Sharpe Ratio", value: null },
    { metric: "Sortino Ratio", value: null },
  ];
  if (clean.length < 2) return emptyMetrics;

  const first = clean[0].close;
  const last = clean[clean.length - 1].close;
  const firstDate = new Date(`${clean[0].date}T00:00:00`);
  const lastDate = new Date(`${clean[clean.length - 1].date}T00:00:00`);
  const days = (lastDate - firstDate) / (24 * 60 * 60 * 1000);
  const years = days > 0 ? days / 365.25 : 0;
  const totalReturn = last / first - 1;
  const cagrValue = years > 0 ? (last / first) ** (1 / years) - 1 : null;
  const returns = clean.slice(1).map((point, idx) => point.close / clean[idx].close - 1).filter(Number.isFinite);
  let cumulative = 1;
  let peak = 1;
  let maxDd = 0;
  returns.forEach((ret) => {
    cumulative *= 1 + ret;
    peak = Math.max(peak, cumulative);
    maxDd = Math.min(maxDd, cumulative / peak - 1);
  });
  const periodsPerYear = inferPeriodsPerYearFromSeries(clean);
  const mean = returns.length ? returns.reduce((sum, value) => sum + value, 0) / returns.length : null;
  const std = sampleStdDev(returns);
  const volatility = std ? std * Math.sqrt(periodsPerYear) : null;
  const downsideStd = sampleStdDev(returns.filter((value) => value < 0));
  const sharpe = mean !== null && std ? (mean * periodsPerYear) / (std * Math.sqrt(periodsPerYear)) : null;
  const sortino = mean !== null && downsideStd ? (mean * periodsPerYear) / (downsideStd * Math.sqrt(periodsPerYear)) : null;

  return [
    { metric: "Total Return", value: Number.isFinite(totalReturn) ? totalReturn : null },
    { metric: "CAGR", value: Number.isFinite(cagrValue) ? cagrValue : null },
    { metric: "Volatility", value: Number.isFinite(volatility) ? volatility : null },
    { metric: "Max Drawdown", value: Number.isFinite(maxDd) ? maxDd : null },
    { metric: "Sharpe Ratio", value: Number.isFinite(sharpe) ? sharpe : null },
    { metric: "Sortino Ratio", value: Number.isFinite(sortino) ? sortino : null },
  ];
};

const buildCompareChartData = (points, tickerNameMap) => {
  if (!points.length) {
    return { labels: [], datasets: [] };
  }
  const sorted = [...points].sort((a, b) => new Date(a.date) - new Date(b.date));
  const labels = Array.from(new Set(sorted.map((p) => new Date(p.date).toISOString().slice(0, 10))));
  const datasetMap = {};
  sorted.forEach((point) => {
    if (!datasetMap[point.ticker]) {
      datasetMap[point.ticker] = new Array(labels.length).fill(null);
    }
    const idx = labels.indexOf(new Date(point.date).toISOString().slice(0, 10));
    datasetMap[point.ticker][idx] = point.totalReturnPct;
  });
  const datasets = Object.entries(datasetMap).map(([ticker, data]) => ({
    label: tickerDisplayName(ticker, tickerNameMap),
    data,
    spanGaps: true,
  }));
  return { labels, datasets };
};

const buildUrl = (base, params) => {
  const query = params.toString();
  return query ? `${base}?${query}` : base;
};

const pad2 = (value) => `${value}`.padStart(2, "0");

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

const formatDisplayDate = (year, month, day) => `${year}. ${pad2(month)}. ${pad2(day)}`;

const formatIsoDate = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

const parseDateParts = (input) => {
  const text = (input || "").trim();
  if (!text) return null;

  const groups = text.match(/\d+/g) || [];
  if (!groups.length) return null;

  const yearGroup = groups[0];
  if (yearGroup.length !== 4) return null;
  const year = Number(yearGroup);
  if (!Number.isInteger(year) || year < 1000 || year > 9999) return null;

  const month = groups.length >= 2 ? Number(groups[1]) : null;
  if (month !== null && (!Number.isInteger(month) || month < 1 || month > 12)) return null;

  const day = groups.length >= 3 ? Number(groups[2]) : null;
  if (day !== null && (!Number.isInteger(day) || day < 1 || day > 31)) return null;

  return { year, month, day };
};

const completeDateInput = (input, kind) => {
  const parts = parseDateParts(input);
  if (!parts) return null;

  const { year } = parts;
  let { month, day } = parts;

  if (month === null) {
    month = kind === "end" ? 12 : 1;
  }

  const maxDay = daysInMonth(year, month);
  if (day === null) {
    day = kind === "end" ? maxDay : 1;
  }

  if (day < 1 || day > maxDay) return null;

  return {
    iso: formatIsoDate(year, month, day),
    display: formatDisplayDate(year, month, day),
  };
};

const isoDateToDisplay = (isoDate) => {
  const text = String(isoDate || "").trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return text;
  return `${match[1]}. ${match[2]}. ${match[3]}`;
};

const isoDateToUtcMs = (isoDate) => {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const utcMsToIsoDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const localDateParts = (date = new Date()) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
});

const isoFromLocalDate = (date = new Date()) => {
  const { year, month, day } = localDateParts(date);
  return formatIsoDate(year, month, day);
};

const shiftIsoDateByYears = (isoDate, years) => {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const base = match
    ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    : localDateParts(new Date());
  const targetYear = base.year + years;
  const targetDay = Math.min(base.day, daysInMonth(targetYear, base.month));
  return formatIsoDate(targetYear, base.month, targetDay);
};

const shiftIsoDateByMonths = (isoDate, months) => {
  const match = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const base = match
    ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    : localDateParts(new Date());
  const zeroBasedMonth = base.month - 1 + months;
  const targetYear = base.year + Math.floor(zeroBasedMonth / 12);
  const targetMonth = ((zeroBasedMonth % 12) + 12) % 12 + 1;
  const targetDay = Math.min(base.day, daysInMonth(targetYear, targetMonth));
  return formatIsoDate(targetYear, targetMonth, targetDay);
};

const defaultAnalysisStartDate = () => shiftIsoDateByYears(isoFromLocalDate(), -1);

const analysisRangeStartDate = (option, todayIso = isoFromLocalDate()) => {
  if (!option || option.key === "all") return "";
  if (option.months) return shiftIsoDateByMonths(todayIso, -option.months);
  if (option.years) return shiftIsoDateByYears(todayIso, -option.years);
  return "";
};

const analysisRangeKeyForSelection = (selection, todayIso = isoFromLocalDate()) => {
  const { start = "", end = "" } = selection || {};
  if (!start && !end) return "all";
  if (end && end !== todayIso) return "";
  return ANALYSIS_RANGE_OPTIONS.find((option) => option.key !== "all" && start === analysisRangeStartDate(option, todayIso))?.key || "";
};

const analysisRangeTitle = (option) => {
  if (!option || option.key === "all") return "전체 기간";
  if (option.months) return `최근 ${option.months}개월`;
  if (option.years) return `최근 ${option.years}년`;
  return option.label || "조회 기간";
};

const ANALYSIS_RANGE_OPTIONS = [
  { key: "1m", label: "1M", months: 1 },
  { key: "3m", label: "3M", months: 3 },
  { key: "6m", label: "6M", months: 6 },
  { key: "1y", label: "1Y", years: 1 },
  { key: "3y", label: "3Y", years: 3 },
  { key: "5y", label: "5Y", years: 5 },
  { key: "10y", label: "10Y", years: 10 },
  { key: "all", label: "All" },
];

const makeUniqueName = (desiredName, existingNames = []) => {
  const base = String(desiredName || "").trim() || "포트폴리오";
  const used = new Set(existingNames.map((name) => String(name || "").trim().toLowerCase()).filter(Boolean));
  if (!used.has(base.toLowerCase())) {
    return base;
  }
  let suffix = 2;
  while (used.has(`${base} (${suffix})`.toLowerCase())) {
    suffix += 1;
  }
  return `${base} (${suffix})`;
};

// 2026-10-04: 분석 페이지의 관련 발행 분석 글 카드 (user rule: 현재 분석 종목의
// 실제 발행 글만 — 백엔드 /api/hubs/{ticker} 관련글 엔드포인트, 증거 매처 적용,
// hub 자격 게이트 없음). 링크는 React JSX 속성이라 안전하게 이스케이프되고
// href는 encodeURIComponent로 구성된 서버 값(slug quote safe='')만 사용한다.
// =====================================================================
// 2026-10-05 stock analysis expansion (proposal A1~A6, B1, B6)
// Snapshot header / indicator overlays / volume / benchmark / PER band /
// events / financials / follow button. Data honesty rules:
//  - snapshot is_realtime=false always (stored daily closes)
//  - KR dividend note states facts only (no generic +1.5~3%p estimate)
//  - missing modules show a reason, never fabricated zeros
// =====================================================================

const ANALYSIS_PREF_KEY = "sf.analysis.prefs.v1";

const ANALYSIS_INDICATOR_DEFS = [
  { key: "sma20", label: "SMA20" },
  { key: "sma50", label: "SMA50" },
  { key: "sma200", label: "SMA200" },
  { key: "ema20", label: "EMA20" },
  { key: "bollinger", label: "볼린저(20,2σ)" },
  { key: "rsi", label: "RSI-14" },
  { key: "macd", label: "MACD(12,26,9)" },
];

const buildBenchmarkOverlay = (prices, benchmark) => {
  if (!benchmark?.sufficient || !Array.isArray(prices) || !Array.isArray(benchmark.dates)) return null;
  const byDate = new Map();
  benchmark.dates.forEach((date, i) => {
    const raw = benchmark.curve_benchmark?.[i];
    const value = raw == null ? NaN : Number(raw);
    if (Number.isFinite(value) && value > 0) byDate.set(String(date).slice(0, 10), value);
  });
  const common = prices.filter((p) => byDate.has(String(p?.date || "").slice(0, 10)) && p?.close != null && Number.isFinite(Number(p.close)) && Number(p.close) > 0);
  if (common.length < 2) return null;
  const baseDate = String(common[0].date).slice(0, 10);
  const basePrice = Number(common[0].close);
  const baseBenchmark = byDate.get(baseDate);
  const values = prices.map((p) => {
    const value = byDate.get(String(p?.date || "").slice(0, 10));
    return value == null ? null : basePrice * (value / baseBenchmark);
  });
  return { values, baseDate, basePrice, commonPoints: common.length };
};

const ANALYSIS_INDICATOR_DISCLAIMER = "참고용 산술 통계 — 매매 신호가 아닙니다. 봉 수가 부족한 구간은 N/A(0 대체 없음)이며, 인터벌이 바뀌면 해당 인터벌로 재계산합니다.";

const loadAnalysisPrefs = () => {
  try {
    const raw = window.localStorage.getItem(ANALYSIS_PREF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_) {
    return null;
  }
};

const saveAnalysisPrefs = (prefs) => {
  try {
    window.localStorage.setItem(ANALYSIS_PREF_KEY, JSON.stringify(prefs));
  } catch (_) {
    // 저장 실패는 분석 흐름을 막지 않는다.
  }
};

const formatSnapPct = (ratio) => {
  if (ratio === null || ratio === undefined || Number.isNaN(Number(ratio))) return "N/A";
  const pct = Number(ratio) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
};

const formatSnapPrice = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "N/A";
  if (Math.abs(num) >= 10000) return num.toLocaleString("ko-KR", { maximumFractionDigits: 0 });
  return num.toLocaleString("ko-KR", { maximumFractionDigits: 2 });
};

// A6/G6: 종목 요약 헤더 — 최근 종가 스냅샷. 실시간 아님을 항상 명시한다.
const SnapshotHeader = ({ snapshot, loading, error, krNoDividend }) => {
  if (loading && !snapshot) {
    return <section className="snapshot-header loading" aria-busy="true"><p className="snapshot-status">{sfText("시세 스냅샷을 불러오는 중...")}</p></section>;
  }
  if (error && !snapshot) {
    return <section className="snapshot-header error"><p className="snapshot-status">{error}</p></section>;
  }
  if (!snapshot || snapshot.last_close === null || snapshot.last_close === undefined) return null;
  const s = snapshot;
  const posPct = s.week_52_position_pct !== null && s.week_52_position_pct !== undefined
    ? `${(Number(s.week_52_position_pct) * 100).toFixed(1)}%` : "N/A";
  return (
    <section className="snapshot-header" aria-label={sfText("종목 시세 요약")}>
      <div className="snapshot-price-block">
        <strong className="snapshot-last-close">{formatSnapPrice(s.last_close)}</strong>
        <span className={`snapshot-day-change ${(s.day_change_pct || 0) >= 0 ? "up" : "down"}`}>
          {formatSnapPct(s.day_change_pct)}
        </span>
        <span className="snapshot-asof">
          {sfText("최근 종가 기준")} {s.as_of_date} · {sfText("실시간 시세 아님")}
        </span>
      </div>
      <dl className="snapshot-range-block">
        <div><dt>{sfText("52주 최고")}</dt><dd>{s.week_52_high !== null && s.week_52_high !== undefined ? formatSnapPrice(s.week_52_high) : "N/A"}</dd></div>
        <div><dt>{sfText("52주 최저")}</dt><dd>{s.week_52_low !== null && s.week_52_low !== undefined ? formatSnapPrice(s.week_52_low) : "N/A"}</dd></div>
        <div><dt>{sfText("52주 위치")}</dt><dd>{posPct}</dd></div>
        <div><dt>{sfText("52주 수익률")}</dt><dd>{formatSnapPct(s.week_52_return_pct)}</dd></div>
      </dl>
      {krNoDividend && (
        <p className="price-basis-note">
          {sfText("국내 종목 수익률은 배당이 미포함된 가격수익률입니다(조정종가 데이터 없음). 배당 반영 수익률은 제공 데이터가 확인된 후 추가됩니다.")}
        </p>
      )}
    </section>
  );
};

// B workbench 식별 헤더 — "지금 로드된 데이터가 무엇인지"만 말한다.
// 정합성 규칙: 표기은 snapshotTickerKey(로드된 스냅샷의 티커)와 snapshot·priceTickerKey
// (로드된 가격 시계열의 티커)만 근거로 한다. 사용자가 입력란을 고치는 중(pending)에는
// 기존 데이터를 새 이름으로 재라벨링하지 않고, 실제 로드된 종목명을 유지한다.
// 숫자는 전부 실제 snapshot 객체에서 온다 — 정적 시안 값 없음, N/A는 그대로 N/A.
const WorkbenchIdentity = ({ snapshot, snapshotTickerKey, priceTickerKey, loading, error, tickerNameMap }) => {
  const displayKey = snapshotTickerKey || priceTickerKey || "";
  const displayName = displayKey ? (tickerNameMap?.[displayKey] || displayKey) : "";
  const hasQuote = !!(snapshot && snapshot.last_close !== null && snapshot.last_close !== undefined);
  // 국내 6자리 종목 = 배당 미포함 가격수익률 안내 대상 (기존 SnapshotHeader 규칙과 동일).
  const krNoDividend = /^\d{6}$/.test(displayKey);
  return (
    <section className="wb-identity" aria-label={sfText("분석 종목 정보")}>
      <div className="wb-identity-name">
        <h1 className="wb-title">{displayName || sfText("분석할 종목을 선택하세요")}</h1>
        {displayKey && (
          <p className="wb-ticker-line">
            <span className="wb-ticker-code">{displayKey}</span>
            {displayName && displayName !== displayKey && <span className="wb-ticker-sep"> · </span>}
            {displayName && displayName !== displayKey && <span>{displayName}</span>}
            <span className="wb-asof">
              {hasQuote && snapshot.as_of_date
                ? <> · {sfText("최근 종가 기준")} {snapshot.as_of_date} · {sfText("실시간 시세 아님")}</>
                : (loading ? ` · ${sfText("시세 스냅샷을 불러오는 중...")}` : "")}
            </span>
          </p>
        )}
      </div>
      <div className={`wb-quote${hasQuote ? "" : " is-empty"}`} aria-live="polite">
        {hasQuote ? (
          <>
            <strong className="wb-quote-num num">{formatSnapPrice(snapshot.last_close)}</strong>
            <span className={`wb-quote-change num ${(snapshot.day_change_pct || 0) >= 0 ? "up" : "down"}`}>
              {formatSnapPct(snapshot.day_change_pct)}
            </span>
          </>
        ) : (
          <span className="wb-quote-placeholder">{loading ? sfText("시세 스냅샷을 불러오는 중...") : (error ? error : sfText("저장 종가 없음"))}</span>
        )}
      </div>
      {krNoDividend && hasQuote && (
        <p className="wb-kr-note">{sfText("국내 종목 수익률은 배당이 미포함된 가격수익률입니다(조정종가 데이터 없음). 배당 반영 수익률은 제공 데이터가 확인된 후 추가됩니다.")}</p>
      )}
    </section>
  );
};

// 모듈 공통 래퍼 — 로딩/오류/미제공 사유를 일관되게 표시 (missing reason rule).
const AnalysisModuleCard = ({ id, title, state, errorText, reason, children }) => (
  <section className="analysis-module-card" id={id} aria-label={title}>
    <h3>{title}</h3>
    {state === "loading" && <p className="analysis-module-status">{sfText("불러오는 중...")}</p>}
    {state === "error" && <p className="analysis-module-status is-error">{errorText || sfText("데이터를 불러오지 못했습니다.")}</p>}
    {state === "done" && reason && <p className="analysis-module-status is-muted">{reason}</p>}
    {state === "done" && !reason && children}
  </section>
);

// A3: PER 밴드/백분위 — 표본·기간·음수 EPS 제외 수를 명시. 저평가 단정 문구 없음.
const PerBandCard = ({ band }) => {
  if (!band || band.sample_count === undefined) return null;
  const f1 = (v) => (v === null || v === undefined ? "N/A" : Number(v).toFixed(2));
  return (
    <div className="per-band-grid">
      <div className="per-band-hist" aria-hidden="true">
        <div className="per-band-scale">
          <span>{f1(band.min)}</span>
          <span>P25 {f1(band.p25)}</span>
          <span>중앙값 {f1(band.median)}</span>
          <span>P75 {f1(band.p75)}</span>
          <span>{f1(band.max)}</span>
        </div>
        <div className="per-band-bar">
          <span className="per-band-q1" style={{ width: "25%" }} />
          <span className="per-band-q2" style={{ width: "25%" }} />
          <span className="per-band-q3" style={{ width: "25%" }} />
          <span className="per-band-q4" style={{ width: "25%" }} />
          {band.current !== null && band.current !== undefined && band.max > band.min && (
            <span
              className="per-band-marker"
              style={{ left: `${Math.min(98, Math.max(2, ((band.current - band.min) / (band.max - band.min)) * 100))}%` }}
              title={sfText(`현재 PER ${f1(band.current)}`)}
            />
          )}
        </div>
        <p className="per-band-caption">
          {sfText("백분위는 과거 분포 내 위치 표시일 뿐이며 평균 회귀·저평가 여부를 의미하지 않습니다.")}
        </p>
      </div>
      <table className="per-band-table">
        <tbody>
          <tr><th scope="row">현재 PER</th><td>{f1(band.current)}</td></tr>
          <tr><th scope="row">백분위</th><td>{band.percentile !== null && band.percentile !== undefined ? `${band.percentile.toFixed(1)}%` : "N/A"}</td></tr>
          <tr><th scope="row">표본</th><td>{`${band.sample_count}개 종가 (${band.window_start}~${band.window_end})`}</td></tr>
          <tr><th scope="row">제외</th><td>{sfText(`TTM EPS ≤ 0 구간 ${band.excluded_negative_eps_points}개 제외`)}</td></tr>
        </tbody>
      </table>
    </div>
  );
};

// B1: 공시 이벤트 리스트 — 공식 filing 일자만. 실적 예정 캘린더가 아님을 명시.
const EventsCard = ({ events, sourceNote }) => {
  const items = Array.isArray(events) ? events : [];
  return (
    <div className="events-wrap">
      <ul className="events-list">
        {items.map((ev, idx) => {
          const isEarnings = ev.event_type === "earnings";
          return (
            <li key={`${ev.accession || idx}`} className={`event-row event-${ev.event_type}`}>
              <span className="event-icon" aria-hidden="true">{isEarnings ? "▲" : "●"}</span>
              <span className="event-date">{ev.event_date}</span>
              <span className="event-label">{ev.filing_label || ev.title}</span>
              <span className="event-desc">{isEarnings ? sfText("실적 보고서 접수") : sfText("공시 접수")}</span>
              {ev.source_url ? (
                <a className="event-link" href={ev.source_url} target="_blank" rel="noopener noreferrer nofollow">
                  {sfText("원문")} ↗
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="events-source-note">{sourceNote}</p>
    </div>
  );
};

// B6: 공식 분기 재무 — 저장된 확정 데이터만. 결측은 N/A.
// KR(DART): 당기(3개월) 값 + 4분기 도출 배지 + 원문 보고서 링크(접수일 표기).
const FinancialsCard = ({ financials }) => {
  const quarters = Array.isArray(financials?.quarters) ? financials.quarters : [];
  if (!quarters.length) return null;
  const isKr = financials.unit === "KRW";
  const fmtB = (v) => {
    if (v === null || v === undefined) return "N/A";
    const num = Number(v);
    if (!Number.isFinite(num)) return "N/A";
    const div = isKr ? 1e12 : 1e9;
    const suffix = isKr ? "조" : "B";
    return `${(num / div).toFixed(2)}${suffix}`;
  };
  return (
    <div className="table-container financials-table-wrap">
      <table className="financials-table">
        <thead>
          <tr>
            <th scope="col">{sfText("기간")}</th>
            <th scope="col">{sfText("매출")}</th>
            <th scope="col">{sfText("영업이익")}</th>
            <th scope="col">{sfText("영업이익률")}</th>
            <th scope="col">{sfText("순이익")}</th>
            {isKr ? <th scope="col">{sfText("원문")}</th> : null}
          </tr>
        </thead>
        <tbody>
          {quarters.map((q, idx) => {
            const derived = !!(q.revenue_derived || q.operating_income_derived || q.net_income_derived || q.revenue_source_kind === "derived_q4");
            const label = isKr
              ? `${q.quarter_label || ""} (${String(q.period_end || "").slice(2)})`
              : String(q.period_end || "");
            return (
              <tr key={q.period_end || q.quarter_label || idx}>
                <td>
                  {label}
                  {derived ? <small className="fin-derived-tag"> {sfText(isKr ? "4분기 도출" : "분기 환산")}</small> : null}
                </td>
                <td>{fmtB(q.revenue)}</td>
                <td>{fmtB(q.operating_income)}</td>
                <td>{q.operating_margin !== null && q.operating_margin !== undefined ? `${(q.operating_margin * 100).toFixed(1)}%` : "N/A"}</td>
                <td>{fmtB(q.net_income)}</td>
                {isKr ? (
                  <td>
                    {q.source_url ? (
                      <a className="event-link" href={q.source_url} target="_blank" rel="noopener noreferrer nofollow" title={q.rcept_date ? `접수 ${q.rcept_date}` : ""}>
                        {q.rcept_date || sfText("보고서")} ↗
                      </a>
                    ) : "—"}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="fin-source-note">{sfText(financials.statement_note || "")}</p>
      {Array.isArray(financials.notes) && financials.notes.length > 0 && (
        <ul className="fin-source-note">
          {financials.notes.map((n, i) => <li key={i}>{sfText(String(n))}</li>)}
        </ul>
      )}
    </div>
  );
};

// A5: 드로다운 곡선 — 표시 중 동일 종가 시계열 기준(엔진과 동일 정의), 참고용.
const DrawdownCard = ({ priceSeries }) => {
  const points = Array.isArray(priceSeries) ? priceSeries : [];
  if (points.length < 2) return null;
  let peak = -Infinity;
  const dd = [];
  let minVal = 0;
  points.forEach((p) => {
    const v = Number(p?.close);
    if (!Number.isFinite(v)) return;
    peak = Math.max(peak, v);
    const d = peak > 0 ? v / peak - 1 : 0;
    dd.push({ date: String(p.date || "").slice(0, 10), d });
    if (d < minVal) minVal = d;
  });
  if (dd.length < 2) return null;
  const maxDd = minVal * 100;
  const bars = dd.map((pt, i) => {
    const h = Math.max(1, Math.round((pt.d / (minVal || -1)) * 100));
    return (
      <span
        key={pt.date || i}
        className="dd-bar"
        style={{ height: `${h}%` }}
        title={`${pt.date} ${(pt.d * 100).toFixed(2)}%`}
      />
    );
  });
  return (
    <div className="dd-wrap">
      <div className="dd-bars" role="img" aria-label={sfText("드로다운 곡선")}>{bars}</div>
      <p className="dd-note">
        {sfText(`선택 기간 최대 낙폭 ${maxDd.toFixed(1)}% · 고점 대비 하락폭(종가 기준). 미실현 손실 지표이며 미래 수익과 무관합니다.`)}
      </p>
    </div>
  );
};

// A5: 월수익 히트맵 — 월말 종가 기준 수익률. 13개월 미만이면 미표시(엔진 규칙 동일).
const MonthlyReturnsCard = ({ priceSeries }) => {
  const points = Array.isArray(priceSeries) ? priceSeries : [];
  const monthClose = new Map();
  points.forEach((p) => {
    const v = Number(p?.close);
    const mk = String(p?.date || "").slice(0, 7);
    if (!Number.isFinite(v) || !/^\d{4}-\d{2}$/.test(mk)) return;
    monthClose.set(mk, v); // 정렬된 시계열이라 마지막 값 = 월말
  });
  const months = Array.from(monthClose.keys()).sort();
  if (months.length < 13) return null;
  const cells = [];
  for (let i = 1; i < months.length; i++) {
    const prev = monthClose.get(months[i - 1]);
    const cur = monthClose.get(months[i]);
    cells.push({ m: months[i], r: cur / prev - 1 });
  }
  const maxAbs = Math.max(0.01, ...cells.map((c) => Math.abs(c.r)));
  const yearRows = new Map();
  cells.forEach((c) => {
    const y = c.m.slice(0, 4);
    if (!yearRows.has(y)) yearRows.set(y, []);
    yearRows.get(y).push(c);
  });
  return (
    <div className="heatmap-wrap">
      <table className="heatmap-table">
        <thead>
          <tr>
            <th>{sfText("연도")}</th>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((mm) => <th key={mm}>{mm}</th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from(yearRows.entries()).map(([year, rowCells]) => (
            <tr key={year}>
              <th scope="row">{year}</th>
              {["01","02","03","04","05","06","07","08","09","10","11","12"].map((mm) => {
                const cell = rowCells.find((c) => c.m === `${year}-${mm}`);
                if (!cell) return <td key={mm} className="heatmap-cell is-empty" title="N/A" />;
                const pct = cell.r * 100;
                const intensity = Math.min(1, Math.abs(cell.r) / maxAbs);
                const bg = cell.r >= 0
                  ? `rgba(22, 163, 74, ${0.15 + intensity * 0.65})`
                  : `rgba(220, 38, 38, ${0.15 + intensity * 0.65})`;
                return (
                  <td
                    key={mm}
                    className="heatmap-cell"
                    style={{ backgroundColor: bg }}
                    title={`${cell.m} ${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                  >
                    {pct.toFixed(0)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="heatmap-note">{sfText("월말 종가 기준 월수익률(%). 배당 미포함, 결측 월은 회색. 과거 패턴이 미래를 보장하지 않습니다.")}</p>
    </div>
  );
};

// A7: 거장 13F 보유 현황 — 이 종목을 보유한 등록 거장 + 분기 보유 추이(공개 13F만).
const GuruOwnersCard = ({ guruOwners }) => {
  const owners = Array.isArray(guruOwners) ? guruOwners : [];
  if (!owners.length) return null;
  return (
    <div className="guru-owners-wrap">
      <table className="guru-owners-table">
        <thead>
          <tr>
            <th scope="col">{sfText("거장")}</th>
            <th scope="col">{sfText("보유주식수")}</th>
            <th scope="col">{sfText("평가액")}</th>
            <th scope="col">{sfText("포트 비중")}</th>
            <th scope="col">{sfText("분기 변화")}</th>
            <th scope="col">{sfText("기준일")}</th>
            <th scope="col">{sfText("추이")}</th>
          </tr>
        </thead>
        <tbody>
          {owners.map((o, idx) => {
            const delta = Number(o.prev_shares) - Number(o.shares);
            let deltaTxt = sfText("보유 유지");
            let deltaCls = "is-hold";
            if (Number(o.prev_shares) === 0 && Number(o.shares) > 0) { deltaTxt = sfText("신규"); deltaCls = "is-new"; }
            else if (delta > 0) { deltaTxt = sfText(`증축 +${Math.round(delta).toLocaleString()}`); deltaCls = "is-up"; }
            else if (delta < 0) { deltaTxt = sfText(`축소 ${Math.round(delta).toLocaleString()}`); deltaCls = "is-down"; }
            const hist = Array.isArray(o.history) ? o.history : [];
            const sharesVals = hist.map((h) => Number(h.shares)).filter(Number.isFinite);
            const hMin = Math.min(...sharesVals, 0);
            const hMax = Math.max(...sharesVals, 1);
            return (
              <tr key={o.guru || idx}>
                <td>
                  <a href={`/gurus/${o.guru}`}>{o.name_ko || o.name || o.guru}</a>
                </td>
                <td>{Number(o.shares).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td>${(Number(o.value) / 1e9).toFixed(2)}B</td>
                <td>{o.pct_of_pf !== null && o.pct_of_pf !== undefined ? `${Number(o.pct_of_pf).toFixed(1)}%` : "N/A"}</td>
                <td><span className={`guru-delta ${deltaCls}`}>{deltaTxt}</span></td>
                <td title={o.filed_date ? `제출 ${o.filed_date}` : ""}>{o.report_date || "—"}</td>
                <td>
                  {hist.length >= 2 ? (
                    <span className="guru-spark" role="img" aria-label={sfText("분기별 보유주식수 추이")}>
                      {hist.map((h, i) => {
                        const v = Number(h.shares);
                        if (!Number.isFinite(v)) return null;
                        const leftPct = (i / Math.max(1, hist.length - 1)) * 100;
                        const bottomPct = hMax > hMin ? ((v - hMin) / (hMax - hMin)) * 100 : 50;
                        return (
                          <span
                            key={i}
                            className="guru-spark-dot"
                            style={{ left: `${leftPct}%`, bottom: `${bottomPct}%` }}
                            title={`${h.report_date} ${Math.round(v).toLocaleString()}주`}
                          />
                        );
                      })}
                    </span>
                  ) : sfText("이력 부족")}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="guru-owners-note">
        {sfText("SEC 13F 공시 기반(분기별, 사후 확정). 일부 거장은 ETF/옵션 제외 티커 매칭 누락이 있을 수 있으며 미보유 종목도 매칭 실패일 수 있습니다. 13F는 장기전략의 일부만 반영합니다.")}
      </p>
    </div>
  );
};

const RelatedPostsCard = ({ state, posts }) => {
  if (state === "idle") return null;
  const items = Array.isArray(posts) ? posts : [];
  const safeHref = (value) => {
    // 내부 블로그 경로만 허용 — 외부/스킴 URL은 렌더하지 않는다.
    const raw = String(value || "");
    return raw.startsWith("/blog/") || raw.startsWith("/en/blog/") ? raw : "";
  };
  const heading = sfText("이 종목의 발행된 분석 글");
  return (
    <section className="related-posts-card" aria-label={heading}>
      <h3>{heading}</h3>
      {state === "loading" && items.length === 0 && (
        <p className="related-posts-status">{sfText("관련 발행 분석 글을 불러오는 중...")}</p>
      )}
      {state === "error" && <p className="related-posts-status">{sfText("관련 글을 불러오지 못했습니다.")}</p>}
      {state !== "loading" && state !== "error" && items.length === 0 && (
        <p className="related-posts-status">{sfText("아직 이 종목을 다룬 발행 분석 글이 없습니다.")}</p>
      )}
      {items.length > 0 && (
        <ul className="related-posts-list">
          {items.map((post, idx) => {
            const href = safeHref(post?.url);
            const title = String(post?.title || "").trim() || String(post?.slug || "");
            if (!href || !title) return null;
            const date = String(post?.date || "").slice(0, 10);
            return (
              <li key={String(post?.slug || idx)}>
                <a href={href}>{title}</a>
                {date ? <small>{date}</small> : null}
              </li>
            );
          })}
        </ul>
      )}
      <a className="related-posts-all-link" href={isEnglishLang() ? "/en/blog" : "/blog"}>
        {sfText("전체 분석 글 보기")} <span aria-hidden="true">→</span>
      </a>
    </section>
  );
};

function StockAnalysisApp() {
  const [tickers, setTickers] = useState([]);
  const [tickerNameMap, setTickerNameMap] = useState({});
  const [tickerAliasMap, setTickerAliasMap] = useState({});
  const [tickerTagMap, setTickerTagMap] = useState({});
  const [tagList, setTagList] = useState({});
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagMode, setTagMode] = useState("AND");
  const [tagPanelOpen, setTagPanelOpen] = useState(false);
  const [tagPanelTarget, setTagPanelTarget] = useState("primary");
  const [selectedTicker, setSelectedTicker] = useState("");
  const [selectedTickerInput, setSelectedTickerInput] = useState("");
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");
  const [priceSeries, setPriceSeries] = useState([]);
  const [priceInteractionBounds, setPriceInteractionBounds] = useState({ ticker: "", start: "", end: "" });
  const [valuationSeries, setValuationSeries] = useState([]);
  const [valuationNotes, setValuationNotes] = useState([]);
  const [showEpsOverlay, setShowEpsOverlay] = useState(false);
  const [chartMode, setChartMode] = useState("line");
  const [metrics, setMetrics] = useState([]);
  const [priceError, setPriceError] = useState("");
  const [valuationError, setValuationError] = useState("");
  const [loadingPrice, setLoadingPrice] = useState(false);

  // 2026-10-05 확장: 스냅샷·지표·벤치마크·PER밴드·이벤트·재무·팔로우 모듈 상태.
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotState, setSnapshotState] = useState("idle"); // idle|loading|done|error
  const [snapshotError, setSnapshotError] = useState("");
  // B workbench 정합성 태그: 지금 화면의 스냅샷이 "어느 티커의 데이터"인지 기록한다.
  // 식별 헤더는 이 태그만 근거로 표기한다 — 사용자가 입력을 고치는 중에는 절대 재라벨링하지 않는다.
  const [snapshotTickerKey, setSnapshotTickerKey] = useState("");
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [showVolume, setShowVolume] = useState(true);
  const [benchmarkEnabled, setBenchmarkEnabled] = useState(false);
  const [rangeSelection, setRangeSelection] = useState(null);
  const [benchmarkTicker, setBenchmarkTicker] = useState("");
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkState, setBenchmarkState] = useState("idle");
  const [benchmarkError, setBenchmarkError] = useState("");
  const [perBandData, setPerBandData] = useState(null);
  const [perBandState, setPerBandState] = useState("idle");
  const [perBandRange, setPerBandRange] = useState("5y");
  const [eventsData, setEventsData] = useState(null);
  const [eventsState, setEventsState] = useState("idle");
  const [eventsError, setEventsError] = useState("");
  const [financialsData, setFinancialsData] = useState(null);
  const [financialsState, setFinancialsState] = useState("idle");
  const [financialsError, setFinancialsError] = useState("");
  const [guruOwners, setGuruOwners] = useState(null); // null=미로딩, []=보유 거장 없음
  const [guruOwnersState, setGuruOwnersState] = useState("idle");
  const [followState, setFollowState] = useState("idle"); // idle|checking|following|not
  const [followMessage, setFollowMessage] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showAnchors] = useState(true);
  const expansionReqRef = useRef(0); // 티커 전환 시 늦은 확장 응답 폐기
  const [compareTickers, setCompareTickers] = useState([]);
  const [compareChart, setCompareChart] = useState([]);
  const [compareDetails, setCompareDetails] = useState(null);
  const [compareError, setCompareError] = useState("");
  const [compareSaveMessage, setCompareSaveMessage] = useState("");
  const [compareSaveError, setCompareSaveError] = useState("");
  const [savingComparePortfolio, setSavingComparePortfolio] = useState(false);
  const [loadingCompare, setLoadingCompare] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [recentTickerKeys, setRecentTickerKeys] = useState([]);
  const [analysisSavedPortfolios, setAnalysisSavedPortfolios] = useState([]);
  const [loadingAnalysisSavedPortfolios, setLoadingAnalysisSavedPortfolios] = useState(false);
  const [activePortfolioStarTicker, setActivePortfolioStarTicker] = useState("");
  const [portfolioQuickAddMessage, setPortfolioQuickAddMessage] = useState("");
  const [portfolioQuickAddError, setPortfolioQuickAddError] = useState("");

  const recentTickerKeysRef = useRef([]);
  const guestRecentHistoryRef = useRef(false);
  const applyRecentTickerHistory = (...groups) => {
    const items = mergeRecentTickerKeys(...groups);
    recentTickerKeysRef.current = items;
    setRecentTickerKeys(items);
    return items;
  };
  const rememberGuestTickerHistory = (...groups) => {
    const items = applyRecentTickerHistory(...groups);
    saveLocalRecentTickers(items);
    return items;
  };
  const fetchRecentTickerSearches = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/recent-ticker-searches`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        guestRecentHistoryRef.current = true;
        rememberGuestTickerHistory(loadLocalRecentTickers());
        return;
      }
      if (!res.ok) return;
      const json = await res.json();
      applyRecentTickerHistory(recentTickerKeysRef.current, json.items);
    } catch (_) { /* A failed history request never blocks analysis. */ }
  };

  const recordRecentTickerSearch = async (ticker) => {
    const tickerKey = normalizeTickerKey(ticker);
    if (!isValidTickerQuery(tickerKey)) return;
    applyRecentTickerHistory([tickerKey], recentTickerKeysRef.current);
    if (guestRecentHistoryRef.current) {
      rememberGuestTickerHistory(recentTickerKeysRef.current, loadLocalRecentTickers());
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/recent-ticker-searches`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ ticker: tickerKey }),
      });
      if (res.status === 401 || res.status === 403) {
        guestRecentHistoryRef.current = true;
        // Never copy a signed-in account's history into the guest store.
        rememberGuestTickerHistory([tickerKey], loadLocalRecentTickers());
        return;
      }
      if (!res.ok) return;
      const json = await res.json();
      applyRecentTickerHistory(recentTickerKeysRef.current, json.items);
    } catch (_) { /* The in-session order already reflects the completed view. */ }
  };

  const loadAnalysisSavedPortfolios = async () => {
    setLoadingAnalysisSavedPortfolios(true);
    try {
      const res = await fetch(`${API_BASE}/my-portfolios`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        setAnalysisSavedPortfolios([]);
        return [];
      }
      const json = res.ok ? await res.json() : { items: [] };
      const items = Array.isArray(json.items) ? json.items : [];
      setAnalysisSavedPortfolios(items);
      return items;
    } catch (_) {
      setAnalysisSavedPortfolios([]);
      return [];
    } finally {
      setLoadingAnalysisSavedPortfolios(false);
    }
  };

  const portfolioTickerMembership = useMemo(() => {
    const membership = {};
    (analysisSavedPortfolios || []).forEach((portfolio) => {
      (portfolio?.holdings || []).forEach((holding) => {
        const tickerKey = normalizeTickerKey(holding?.ticker);
        if (tickerKey && tickerKey !== CASH_TICKER) membership[tickerKey] = true;
      });
    });
    return membership;
  }, [analysisSavedPortfolios]);

  const handlePortfolioStarClick = async (tickerKey) => {
    const normalized = normalizeTickerKey(tickerKey);
    setPortfolioQuickAddMessage("");
    setPortfolioQuickAddError("");
    setActivePortfolioStarTicker((prev) => (prev === normalized ? "" : normalized));
    if (!analysisSavedPortfolios.length && !loadingAnalysisSavedPortfolios) {
      await loadAnalysisSavedPortfolios();
    }
  };

  const addTickerToSavedPortfolio = async (portfolioId, tickerKey) => {
    const targetId = String(portfolioId || "");
    const normalizedTicker = normalizeTickerKey(tickerKey);
    const portfolio = (analysisSavedPortfolios || []).find((item) => String(item?.id || "") === targetId);
    if (!portfolio || !normalizedTicker) return;

    const existingHoldings = (portfolio.holdings || [])
      .map((holding) => ({
        ...holding,
        ticker: normalizeTickerKey(holding?.ticker),
        weight: Number(holding?.weight) || 0,
      }))
      .filter((holding) => holding.ticker && holding.weight > 0);

    if (existingHoldings.some((holding) => holding.ticker === normalizedTicker)) {
      setPortfolioQuickAddError("이미 선택한 포트폴리오에 있는 종목입니다.");
      return;
    }

    const existingWeightSum = existingHoldings.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
    const isSharesMode = (portfolio.input_mode || "weight") === "shares";
    const newWeight = isSharesMode
      ? 1
      : existingHoldings.length
        ? Math.min(10, roundTo(100 / (existingHoldings.length + 1), 4))
        : 100;
    const scale = isSharesMode
      ? 1
      : existingWeightSum > 0
        ? (100 - newWeight) / existingWeightSum
        : 0;
    const nextHoldings = existingHoldings.map((holding) => ({
      ...holding,
      weight: isSharesMode ? holding.weight : roundTo((Number(holding.weight) || 0) * scale, 4),
    }));
    nextHoldings.push({ ticker: normalizedTicker, weight: roundTo(newWeight, 4) });
    if (!isSharesMode) {
      const nextSum = nextHoldings.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
      const delta = roundTo(100 - nextSum, 4);
      if (nextHoldings.length && delta !== 0) {
        nextHoldings[0] = { ...nextHoldings[0], weight: roundTo((Number(nextHoldings[0].weight) || 0) + delta, 4) };
      }
    }

    const payload = {
      name: portfolio.name || "포트폴리오",
      memo: portfolio.memo || "",
      table: portfolio.table || null,
      start_date: portfolio.start_date || null,
      end_date: portfolio.end_date || null,
      input_mode: portfolio.input_mode || "weight",
      total_amount: portfolio.total_amount ?? null,
      cash_amount: portfolio.cash_amount ?? null,
      holdings: nextHoldings,
    };

    setPortfolioQuickAddMessage("");
    setPortfolioQuickAddError("");
    try {
      const res = await fetch(`${API_BASE}/my-portfolios/${encodeURIComponent(targetId)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || "포트폴리오에 종목을 추가하지 못했습니다.");
      }
      setAnalysisSavedPortfolios((prev) => prev.map((item) => (String(item?.id || "") === targetId ? json : item)));
      setPortfolioQuickAddMessage(`${normalizedTicker} 추가 완료`);
    } catch (exc) {
      setPortfolioQuickAddError(exc?.message || "포트폴리오에 종목을 추가하지 못했습니다.");
    }
  };

  // ===== 기억된 분석 설정 (localStorage) — 최초 1회 로드 후 UI 반영 =====
  const prefsLoadedRef = useRef(false);
  useEffect(() => {
    if (prefsLoadedRef.current) return;
    prefsLoadedRef.current = true;
    const prefs = loadAnalysisPrefs();
    if (!prefs) return;
    if (Array.isArray(prefs.indicators)) {
      const valid = new Set(ANALYSIS_INDICATOR_DEFS.map((d) => d.key));
      const tokens = prefs.indicators.filter((t) => valid.has(t));
      if (tokens.length) setSelectedIndicators(tokens);
    }
    if (typeof prefs.showVolume === "boolean") setShowVolume(prefs.showVolume);
    if (typeof prefs.benchmarkEnabled === "boolean") setBenchmarkEnabled(prefs.benchmarkEnabled);
    if (prefs.defaultRange) {
      const option = ANALYSIS_RANGE_OPTIONS.find((o) => o.key === prefs.defaultRange);
      if (option) {
        const todayIso = isoFromLocalDate();
        setStartDate(analysisRangeStartDate(option, todayIso));
        setStartDateInput(isoDateToDisplay(analysisRangeStartDate(option, todayIso)));
        if (option.key !== "all") {
          setEndDate(todayIso);
          setEndDateInput(isoDateToDisplay(todayIso));
        }
      }
    }
  }, []);

  // 설정 변경 시 저장 (초기 로드 직후 첫 저장은 무해 — 동일 값 덮어쓰기)
  useEffect(() => {
    if (!prefsLoadedRef.current) return;
    saveAnalysisPrefs({
      indicators: selectedIndicators,
      showVolume,
      benchmarkEnabled,
    });
  }, [selectedIndicators, showVolume, benchmarkEnabled]);

  useEffect(() => {
    fetch(`${API_BASE}/auth/me`, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : { is_admin: false }))
      .then((json) => setIsAdmin(!!json.is_admin))
      .catch(() => setIsAdmin(false));
    fetchRecentTickerSearches();
    loadAnalysisSavedPortfolios();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/ticker-metadata`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((json) => {
        const { names, aliases, tags } = buildTickerMetadataMaps(json.items || []);
        setTickerNameMap(names);
        setTickerAliasMap(aliases);
        setTickerTagMap(tags);
      })
      .catch(() => {
        setTickerNameMap({});
        setTickerAliasMap({});
        setTickerTagMap({});
      });
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/tag-list`)
      .then((res) => (res.ok ? res.json() : { tags: {} }))
      .then((json) => setTagList(json.tags || {}))
      .catch(() => setTagList({}));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/tickers`)
      .then((res) => res.json())
      .then((json) => {
        const nextTickers = resolveTickerListFromApi(json.tickers || [], tickerNameMap);
        setTickers(nextTickers);
        if (nextTickers.length === 0) {
          setSelectedTicker("");
          setSelectedTickerInput("");
          setCompareTickers([]);
          return;
        }
        setSelectedTicker((prev) => {
          if (!prev) return "";
          const stillExists = nextTickers.some((ticker) => normalizeTickerKey(ticker) === normalizeTickerKey(prev));
          if (!stillExists) {
            setSelectedTickerInput("");
          }
          return stillExists ? prev : "";
        });
      })
      .catch(() => {
        setPriceError("티커 목록을 불러오지 못했습니다.");
      });
  }, [tickerNameMap]);

  useEffect(() => {
    setTickers((prev) => {
      const next = resolveTickerListFromApi(prev, tickerNameMap);
      if (next.length === prev.length && next.every((value, idx) => value === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [tickerNameMap]);

  useEffect(() => {
    if (selectedTicker) {
      setSelectedTickerInput(tickerDisplayName(selectedTicker, tickerNameMap));
    }
    setCompareTickers((prev) =>
      prev.map((item) => ({
        ...item,
        input: item.ticker ? tickerDisplayName(item.ticker, tickerNameMap) : item.input,
      })),
    );
  }, [tickerNameMap, selectedTicker]);

  // v4.5: /analysis?ticker=XXX 딥링크 — 거장 포트폴리오의 티커 클릭 시
  // 해당 종목이 자동으로 입력·분석된 화면을 보여준다.
  const urlTickerRef = useRef(null);
  const urlTickerAppliedRef = useRef(false);
  // 2026-09-19 UI 개선: 공유 URL(?t=&start=&end=&table=) 복원용 ref.
  const restoreStartRef = useRef("");
  const restoreEndRef = useRef("");
  const restoreTableRef = useRef("");
  useEffect(() => {
    const params = new URLSearchParams(window.location?.search || "");
    urlTickerRef.current = analysisInitialTicker(window.location?.search || "");
    // 공유 링크 복원: t(티커)·start·end(ISO)·table(인터벌). ticker는 v4.5 자동실행 전용.
    const shareTicker = (params.get("t") || "").trim();
    if (shareTicker && isValidTickerQuery(shareTicker)) {
      urlTickerRef.current = normalizeTickerKey(shareTicker);
      const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;
      const shareStart = (params.get("start") || "").trim();
      const shareEnd = (params.get("end") || "").trim();
      if (isoDateRe.test(shareStart)) {
        restoreStartRef.current = shareStart;
        setStartDate(shareStart);
        setStartDateInput(isoDateToDisplay(shareStart));
      }
      if (isoDateRe.test(shareEnd)) {
        restoreEndRef.current = shareEnd;
        setEndDate(shareEnd);
        setEndDateInput(isoDateToDisplay(shareEnd));
      }
      const shareTable = (params.get("table") || "").trim();
      if (shareTable) restoreTableRef.current = shareTable;
      // 2026-10-05 확장 상태: ind(지표)/vol(거래량)/bm(벤치마크)
      const indParam = (params.get("ind") || "").trim();
      if (indParam) {
        const valid = new Set(ANALYSIS_INDICATOR_DEFS.map((d) => d.key));
        const tokens = indParam.split(",").map((t) => t.trim().toLowerCase()).filter((t) => valid.has(t));
        if (tokens.length) setSelectedIndicators(tokens);
      }
      if ((params.get("vol") || "") === "0") setShowVolume(false);
      if ((params.get("bm") || "") === "1") setBenchmarkEnabled(true);
    }
  }, []);
  useEffect(() => {
    if (urlTickerAppliedRef.current) return;
    const wanted = urlTickerRef.current;
    if (!wanted) return;
    // v4.5b: 조회 이력이 없는 신규 티커도 즉시 분석 — 티커 목록 로드를 기다리지
    // 않고, 목록에 있으면 목록 표기(대소문자 정규화)를, 없으면 URL 값을 그대로 사용.
    const match = tickers.find((ticker) => normalizeTickerKey(ticker) === wanted);
    const resolved = match || wanted;
    urlTickerAppliedRef.current = true;
    setSelectedTicker(resolved);
    setSelectedTickerInput(tickerDisplayName(resolved, tickerNameMap));
    // Keep explicit selections on refresh by canonicalizing the legacy ticker parameter.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("ticker") && !url.searchParams.has("t")) url.searchParams.set("t", resolved);
      url.searchParams.delete("ticker");
      window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
    } catch {}
  }, [tickers, tickerNameMap]);

  const tickerKeyList = useMemo(() => tickers.map((t) => normalizeTickerKey(t)), [tickers]);

  const recommendedTickerKeys = useMemo(() => {
    if (!tickerKeyList.length) return [];
    const base = tickers.filter((ticker) => matchesSelectedTags(ticker, selectedTags, tagMode, tickerTagMap));
    return base.map((t) => normalizeTickerKey(t));
  }, [tickers, tickerKeyList, selectedTags, tagMode, tickerTagMap]);

  const pickTickerKey = (tickerKey, target) => {
    const match = tickers.find((ticker) => normalizeTickerKey(ticker) === tickerKey);
    if (!match) return;
    if (target && target.startsWith("compare:")) {
      const id = target.replace("compare:", "");
      setCompareTickers((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ticker: match, input: tickerDisplayName(match, tickerNameMap) } : item,
        ),
      );
    } else {
      setSelectedTicker(match);
      setSelectedTickerInput(tickerDisplayName(match, tickerNameMap));
    }
    setTagPanelOpen(false);
  };

  const updateTables = (tickerValue, setList, setSelected, preferredTable = "") => {
    if (!tickerValue) {
      setList([]);
      setSelected("");
      return;
    }
    const encodedTicker = encodeURIComponent(tickerValue);
    fetch(`${API_BASE}/tickers/${encodedTicker}/tables`)
      .then((res) => res.json())
      .then((json) => {
        const nextTables = json.tables || [];
        setList(nextTables);
        setSelected((prev) => {
          // 2026-09-19 UI 개선: 공유 URL의 table 파라미터가 유효하면 우선 선택.
          if (preferredTable && nextTables.includes(preferredTable)) return preferredTable;
          return pickDefaultInterval(nextTables, prev);
        });
      })
      .catch(() => {
        setList([]);
        setSelected("");
      });
  };

  useEffect(() => {
    if (!selectedTicker || !isValidTickerQuery(selectedTicker)) {
      setTables([]);
      setSelectedTable("");
      return;
    }
    // 공유 링크 복원 흐름에서만 URL table 파라미터를 인터벌 기본값으로 사용.
    const preferred = urlTickerAppliedRef.current ? restoreTableRef.current : "";
    updateTables(selectedTicker, setTables, setSelectedTable, preferred);
  }, [selectedTicker, tickers]);

  // v4.5: 딥링크 종목이 세팅되고 기간 상태가 준비되면 자동으로 1회 분석 실행.
  const autoRunKeyRef = useRef("");
  useEffect(() => {
    if (!urlTickerAppliedRef.current) return;
    if (!selectedTicker || !isValidTickerQuery(selectedTicker)) return;
    // 기간: 사용자가 지정한 게 없으면 기본 1년으로 세팅 (수동 분석과 동일 기준)
    let runStart = startDate;
    if (!runStart) {
      runStart = defaultAnalysisStartDate();
      setStartDate(runStart);
      setStartDateInput(isoDateToDisplay(runStart));
    }
    // 2026-09-19 UI 개선: 공유 링크 복원 시 URL의 기간·인터벌을 그대로 사용해
    // "도착하면 이미 실행된 화면"을 보장한다(사용자 운영 원칙).
    const runEnd = restoreEndRef.current;
    const runTable = restoreTableRef.current;
    restoreEndRef.current = "";
    restoreTableRef.current = "";
    const key = `${normalizeTickerKey(selectedTicker)}|${runStart}|${runEnd}|${runTable}`;
    if (autoRunKeyRef.current === key) return;
    autoRunKeyRef.current = key;
    fetchPriceData(runStart, runEnd, selectedTicker, runTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTickerAppliedRef.current, selectedTicker, startDate]);

  const fetchPriceData = async (startOverride = startDate, endOverride = endDate, tickerOverride = selectedTicker, tableOverride = "") => {
    const tickerForRequest = tickerOverride || selectedTicker;
    if (!isValidTickerQuery(tickerForRequest)) {
      return;
    }
    const tableForRequest = tableOverride || selectedTable || "일봉";
    setRangeSelection({ start: startOverride || "", end: endOverride || "" });
    setLoadingPrice(true);
    setPriceError("");
    setValuationError("");
    try {
      const params = new URLSearchParams();
      params.append("table", tableForRequest);
      if (startOverride) params.append("start_date", startOverride);
      if (endOverride) params.append("end_date", endOverride);
      const encoded = encodeURIComponent(tickerForRequest);
      const priceRes = await fetch(buildUrl(`${API_BASE}/prices/${encoded}`, params));
      if (!priceRes.ok) {
        const err = await priceRes.json();
        throw new Error(err.detail || "가격을 불러오지 못했습니다.");
      }
      const priceJson = await priceRes.json();
      const nextPriceSeries = priceJson.data || [];
      setPriceSeries(nextPriceSeries);
      const chartDates = nextPriceSeries
        .map((row) => String(row?.date || "").slice(0, 10))
        .filter(Boolean)
        .sort();
      const chartStartDate = chartDates[0] || "";
      const chartEndDate = chartDates[chartDates.length - 1] || "";
      if (chartStartDate && chartEndDate) {
        setPriceInteractionBounds((prev) => {
          if (prev.ticker !== tickerForRequest) {
            return { ticker: tickerForRequest, start: chartStartDate, end: chartEndDate };
          }
          return {
            ticker: tickerForRequest,
            start: prev.start && prev.start < chartStartDate ? prev.start : chartStartDate,
            end: prev.end && prev.end > chartEndDate ? prev.end : chartEndDate,
          };
        });
      }
      if (!startOverride && chartStartDate) {
        setStartDate(chartStartDate);
        setStartDateInput(isoDateToDisplay(chartStartDate));
      }
      if (chartEndDate) {
        setEndDate(chartEndDate);
        setEndDateInput(isoDateToDisplay(chartEndDate));
      }

      try {
        const valuationRes = await fetch(buildUrl(`${API_BASE}/valuation/${encoded}`, params));
        if (!valuationRes.ok) {
          const err = await valuationRes.json().catch(() => ({}));
          throw new Error(err.detail || "이익/밸류에이션 데이터를 불러오지 못했습니다.");
        }
        const valuationJson = await valuationRes.json();
        setValuationSeries(Array.isArray(valuationJson.series) ? valuationJson.series : []);
        setValuationNotes(Array.isArray(valuationJson.notes) ? valuationJson.notes : []);
      } catch (valuationFetchError) {
        setValuationSeries([]);
        setValuationNotes([]);
        setValuationError(sfText(valuationFetchError.message || "이익/밸류에이션 데이터를 불러오지 못했습니다."));
      }

      const metricRes = await fetch(buildUrl(`${API_BASE}/metrics/${encoded}`, params));
      if (!metricRes.ok) {
        const err = await metricRes.json();
        throw new Error(err.detail || "지표를 불러오지 못했습니다.");
      }
      const metricJson = await metricRes.json();
      setMetrics(metricJson.metrics || []);
      recordRecentTickerSearch(tickerForRequest);
      // 2026-09-19 UI 개선: 분석 상태를 URL에 반영해 링크로 공유 가능.
      // 이 파라미터는 항상 최신 상태(마지막 조회)를 가리키는 공유용 상태이므로 지우지 않는다.
      syncAnalysisUrlParams(tickerForRequest, startOverride, endOverride);
    } catch (error) {
      setPriceError(error.message);
      setPriceSeries([]);
      setValuationSeries([]);
      setValuationNotes([]);
      setMetrics([]);
    } finally {
      setLoadingPrice(false);
    }
  };

  // 2026-09-19 UI 개선: 현재 분석 상태(티커·기간·인터벌)를 ?t=·?start=·?end=·?table= 로
  // URL에 반영한다. 값이 없는 파라미터는 제거해 URL을 항상 최소로 유지한다.
  const expansionStateRef = useRef({ indicators: [], showVolume: true, benchmarkEnabled: false });
  expansionStateRef.current = { indicators: selectedIndicators, showVolume, benchmarkEnabled };
  const syncAnalysisUrlParams = (tickerForRequest, startOverride, endOverride) => {
    try {
      const url = new URL(window.location.href);
      const tickerKey = normalizeTickerKey(tickerForRequest);
      if (tickerKey) url.searchParams.set("t", tickerKey); else url.searchParams.delete("t");
      const startDateIso = startOverride || startDate;
      const endDateIso = endOverride || endDate;
      if (startDateIso) url.searchParams.set("start", startDateIso); else url.searchParams.delete("start");
      if (endDateIso) url.searchParams.set("end", endDateIso); else url.searchParams.delete("end");
      if (selectedTable) url.searchParams.set("table", selectedTable); else url.searchParams.delete("table");
      // 2026-10-05 확장 상태 반영 (ref로 최신값 사용 — 토글 직후 호출에도 정확)
      const expansion = expansionStateRef.current;
      if (expansion.indicators.length) url.searchParams.set("ind", expansion.indicators.join(","));
      else url.searchParams.delete("ind");
      if (!expansion.showVolume) url.searchParams.set("vol", "0"); else url.searchParams.delete("vol");
      if (expansion.benchmarkEnabled) url.searchParams.set("bm", "1"); else url.searchParams.delete("bm");
      url.searchParams.delete("ticker"); // 구 딥링크 파라미터는 자동실행 전용 — 상태 반영은 t 사용
      window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
    } catch (_) {
      // URL 반영 실패는 분석 흐름을 막지 않는다.
    }
  };
  // 확장 토글 직후 URL 동기화 (분석 재실행 없이 상태 공유 유지)
  useEffect(() => {
    if (!prefsLoadedRef.current) return;
    const tickerKey = normalizeTickerKey(selectedTicker || "");
    if (!tickerKey || !isValidTickerQuery(tickerKey)) return;
    syncAnalysisUrlParams(tickerKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndicators, showVolume, benchmarkEnabled, normalizeTickerKey(selectedTicker || "")]);

  // 2026-10-04: 현재 정규화·분석 중인 티커의 발행 분석 글 (user rule: 분석 페이지에서
  // 해당 종목의 실제 발행 글을 보여준다 — hub 자격 게이트 없이 증거 매처만 적용).
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [relatedState, setRelatedState] = useState("idle"); // idle | loading | done | error
  const relatedReqRef = useRef(0); // 티커 빠른 전환 시 이전 요청의 늦은 응답 폐기(stale guard)

  const fetchRelatedPosts = async (tickerKey) => {
    const normalized = normalizeTickerKey(tickerKey);
    if (!normalized || !isValidTickerQuery(normalized)) {
      setRelatedPosts([]);
      setRelatedState("idle");
      return;
    }
    const reqId = ++relatedReqRef.current;
    setRelatedState("loading");
    try {
      const langQ = isEnglishLang() ? "?lang=en" : "";
      const res = await fetch(`${API_BASE}/api/hubs/${encodeURIComponent(normalized)}${langQ}`, { credentials: "same-origin" });
      if (reqId !== relatedReqRef.current) return; // 다른 티커로 전환됨 — 폐기
      if (!res.ok) {
        // 존재하지 않는 티커(404 포함)는 조용한 no-results — 분석 흐름을 방해하지 않는다.
        setRelatedPosts([]);
        setRelatedState(res.status === 404 ? "done" : "error");
        return;
      }
      const json = await res.json();
      if (reqId !== relatedReqRef.current) return; // 응답 사이 전환 — 폐기
      setRelatedPosts(Array.isArray(json.related) ? json.related : []);
      setRelatedState("done");
    } catch (_) {
      if (reqId !== relatedReqRef.current) return;
      setRelatedPosts([]);
      setRelatedState("error");
    }
  };

  // 정규화된 분석 티커가 바뀔 때만 조회 — 요청/분석 흐름과 독립적, 실패해도 분석을 막지 않는다.
  useEffect(() => {
    const normalized = normalizeTickerKey(selectedTicker || "");
    if (!normalized || !isValidTickerQuery(normalized)) {
      relatedReqRef.current += 1; // 진행 중 요청 폐기
      setRelatedPosts([]);
      setRelatedState("idle");
      return;
    }
    fetchRelatedPosts(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizeTickerKey(selectedTicker || "")]);

  // ===== 2026-10-05 확장 모듈 페처 (스냅샷/벤치마크/PER밴드/이벤트/재무/팔로우) =====
  const isKrTicker = (tickerKey) => /^\d{6}$/.test(String(tickerKey || ""));

  const guessBenchmarkFor = (tickerKey) => (isKrTicker(tickerKey) ? "^KS11" : "SPY");

  const fetchSnapshot = async (tickerKey, reqId) => {
    setSnapshotState("loading");
    setSnapshotError("");
    try {
      const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(tickerKey)}/snapshot`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "스냅샷을 불러오지 못했습니다.");
      }
      const json = await res.json();
      if (reqId !== expansionReqRef.current) return;
      // B workbench 정합성: 성공 응답은 반드시 "어느 티커의 스냅샷인지" 함께 기록한다.
      setSnapshotData(json.snapshot || null);
      setSnapshotTickerKey(tickerKey);
      setSnapshotState("done");
    } catch (exc) {
      if (reqId !== expansionReqRef.current) return;
      setSnapshotData(null);
      setSnapshotTickerKey("");
      setSnapshotError(exc.message || "스냅샷을 불러오지 못했습니다.");
      setSnapshotState("error");
    }
  };

  const fetchBenchmarkData = async (tickerKey, reqId, bench) => {
    const benchKey = bench || benchmarkTicker || guessBenchmarkFor(tickerKey);
    setBenchmarkState("loading");
    setBenchmarkError("");
    try {
      const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(tickerKey)}/benchmark?benchmark=${encodeURIComponent(benchKey)}`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "벤치마크 데이터를 불러오지 못했습니다.");
      }
      const json = await res.json();
      if (reqId !== expansionReqRef.current) return;
      setBenchmarkData(json);
      setBenchmarkState("done");
    } catch (exc) {
      if (reqId !== expansionReqRef.current) return;
      setBenchmarkData(null);
      setBenchmarkError(exc.message || "벤치마크 데이터를 불러오지 못했습니다.");
      setBenchmarkState("error");
    }
  };

  const fetchPerBand = async (tickerKey, reqId) => {
    setPerBandState("loading");
    try {
      const res = await fetch(`${API_BASE}/valuation/${encodeURIComponent(tickerKey)}`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        setPerBandData(null);
        setPerBandState("done");
        return; // EPS 시계열이 없는 종목 — PER 밴드는 조용히 미표시 (이유는 EPS 오버레이 노트가 담당)
      }
      const json = await res.json();
      if (reqId !== expansionRefSafe()) return;
      const series = Array.isArray(json.series) ? json.series : [];
      if (!series.length) {
        setPerBandData(null);
        setPerBandState("done");
        return;
      }
      const windowDays = perBandRange === "3y" ? 756 : perBandRange === "5y" ? 1260 : perBandRange === "10y" ? 2520 : 99999;
      const windowed = series.slice(-windowDays);
      const lastValid = [...windowed].reverse().find((p) => p.pe !== null && p.pe !== undefined);
      const band = buildPerBandStats(windowed, lastValid ? lastValid.pe : null, 60);
      setPerBandData(band);
      setPerBandState("done");
    } catch (_) {
      if (reqId !== expansionRefSafe()) return;
      setPerBandData(null);
      setPerBandState("done");
    }
  };

  const expansionRefSafe = () => expansionReqRef.current;

  // 클라이언트 PER 분포 통계 — 서버와 동일 규칙(음수 EPS 제외, 최근분위 nearest-rank)
  const buildPerBandStats = (points, currentPe, minSamples) => {
    const valid = [];
    let excludedNeg = 0;
    points.forEach((p) => {
      const pe = Number(p?.pe);
      if (Number.isFinite(pe) && pe > 0) {
        valid.push({ date: String(p.date || "").slice(0, 10), pe });
        return;
      }
      const eps = Number(p?.ttm_eps);
      if (Number.isFinite(eps) && eps <= 0) excludedNeg += 1;
    });
    if (valid.length < minSamples) return null;
    const values = valid.map((v) => v.pe).sort((a, b) => a - b);
    const n = values.length;
    const q = (qq) => values[Math.min(n - 1, Math.max(0, Math.round(qq * (n - 1))))];
    const percentile = (() => {
      if (currentPe === null || currentPe === undefined) return null;
      const below = values.filter((v) => v <= currentPe).length;
      return (below / n) * 100;
    })();
    return {
      sample_count: n,
      window_start: valid[0].date,
      window_end: valid[valid.length - 1].date,
      median: q(0.5), p25: q(0.25), p75: q(0.75),
      min: values[0], max: values[n - 1],
      current: Number.isFinite(Number(currentPe)) ? currentPe : null,
      percentile,
      excluded_negative_eps_points: excludedNeg,
    };
  };

  const fetchEvents = async (tickerKey, reqId) => {
    setEventsState("loading");
    setEventsError("");
    try {
      const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(tickerKey)}/events?limit=12`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "공시 이벤트를 불러오지 못했습니다.");
      }
      const json = await res.json();
      if (reqId !== expansionReqRef.current) return;
      setEventsData(json);
      setEventsState("done");
    } catch (exc) {
      if (reqId !== expansionReqRef.current) return;
      setEventsData(null);
      setEventsError(exc.message || "공시 이벤트를 불러오지 못했습니다.");
      setEventsState("error");
    }
  };

  const fetchFinancials = async (tickerKey, reqId) => {
    setFinancialsState("loading");
    setFinancialsError("");
    try {
      const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(tickerKey)}/financials?limit=8`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "공식 분기 재무 데이터를 불러오지 못했습니다.");
      }
      const json = await res.json();
      if (reqId !== expansionReqRef.current) return;
      setFinancialsData(json);
      setFinancialsState("done");
    } catch (exc) {
      if (reqId !== expansionReqRef.current) return;
      setFinancialsData(null);
      setFinancialsError(exc.message || "공식 분기 재무 데이터를 불러오지 못했습니다.");
      setFinancialsState("error");
    }
  };

  const fetchGuruOwners = async (tickerKey, reqId) => {
    setGuruOwnersState("loading");
    try {
      const res = await fetch(`${API_BASE}/api/analysis/${encodeURIComponent(tickerKey)}/guru-owners`);
      if (reqId !== expansionReqRef.current) return;
      if (!res.ok) {
        setGuruOwners([]);
        setGuruOwnersState("done");
        return; // 404 = 이 종목을 보유한 등록 거장 없음 — 조용히 카드 미표시
      }
      const json = await res.json();
      if (reqId !== expansionReqRef.current) return;
      setGuruOwners(Array.isArray(json.owners) ? json.owners : []);
      setGuruOwnersState("done");
    } catch (_) {
      if (reqId !== expansionReqRef.current) return;
      setGuruOwners([]);
      setGuruOwnersState("done");
    }
  };

  const checkFollowStatus = async (tickerKey) => {
    setFollowState("checking");
    try {
      const res = await fetch(`${API_BASE}/api/follows`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        setFollowState("not");
        return "anon";
      }
      if (!res.ok) { setFollowState("not"); return "error"; }
      const json = await res.json();
      const items = Array.isArray(json.follows) ? json.follows : Array.isArray(json.items) ? json.items : [];
      const followed = items.some((it) => it?.follow_type === "ticker" && normalizeTickerKey(it?.target) === tickerKey);
      setFollowState(followed ? "following" : "not");
      return "ok";
    } catch (_) {
      setFollowState("not");
      return "error";
    }
  };

  const toggleFollowTicker = async () => {
    const tickerKey = normalizeTickerKey(selectedTicker || "");
    if (!tickerKey || followState === "checking") return;
    if (followState === "following") {
      try {
        const res = await fetch(`${API_BASE}/api/follows/ticker/${encodeURIComponent(tickerKey)}`, {
          method: "DELETE", credentials: "same-origin",
        });
        if (res.ok) { setFollowState("not"); setFollowMessage(""); }
        else setFollowMessage(sfText("팔로우 해제에 실패했습니다."));
      } catch (_) { setFollowMessage(sfText("팔로우 해제에 실패했습니다.")); }
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/follows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ follow_type: "ticker", target: tickerKey }),
      });
      if (res.status === 401 || res.status === 403) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (res.ok) { setFollowState("following"); setFollowMessage(json.detail || ""); }
      else setFollowMessage(json.detail || sfText("팔로우 등록에 실패했습니다."));
    } catch (_) {
      setFollowMessage(sfText("팔로우 등록에 실패했습니다."));
    }
  };

  // 확장 모듈 일괄 로드: 정규화 티커가 바뀔 때. 각 모듈은 독립 실패(전체를 막지 않음).
  useEffect(() => {
    const tickerKey = normalizeTickerKey(selectedTicker || "");
    if (!tickerKey || !isValidTickerQuery(tickerKey)) {
      expansionReqRef.current += 1;
      setSnapshotData(null); setSnapshotState("idle");
      setSnapshotTickerKey(""); // 로드된 스냅샷 소유자 태그도 함께 초기화
      setBenchmarkData(null); setBenchmarkState("idle");
      setPerBandData(null); setPerBandState("idle");
      setEventsData(null); setEventsState("idle");
      setFinancialsData(null); setFinancialsState("idle");
      setGuruOwners(null); setGuruOwnersState("idle");
      setFollowState("idle");
      return;
    }
    const reqId = ++expansionReqRef.current;
    setBenchmarkTicker(guessBenchmarkFor(tickerKey));
    fetchSnapshot(tickerKey, reqId);
    fetchEvents(tickerKey, reqId);
    fetchFinancials(tickerKey, reqId);
    fetchPerBand(tickerKey, reqId);
    fetchGuruOwners(tickerKey, reqId);
    checkFollowStatus(tickerKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizeTickerKey(selectedTicker || "")]);

  // 벤치마크 토글 ON/기준변경 시에만 조회
  useEffect(() => {
    const tickerKey = normalizeTickerKey(selectedTicker || "");
    if (!tickerKey || !isValidTickerQuery(tickerKey) || !benchmarkEnabled) return;
    const reqId = expansionReqRef.current;
    fetchBenchmarkData(tickerKey, reqId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkEnabled, benchmarkTicker, normalizeTickerKey(selectedTicker || "")]);

  // PER 밴드 기간 변경 시 재집계
  useEffect(() => {
    const tickerKey = normalizeTickerKey(selectedTicker || "");
    if (!tickerKey || !isValidTickerQuery(tickerKey)) return;
    const reqId = expansionReqRef.current;
    fetchPerBand(tickerKey, reqId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perBandRange]);

  const fetchCompareData = async (startOverride = startDate, endOverride = endDate, tickerCandidates = null) => {
    const candidates = tickerCandidates || [selectedTicker, ...compareTickers.map((item) => item.ticker)];
    if (!isValidTickerQuery(candidates[0])) {
      return;
    }
    setLoadingCompare(true);
    setRangeSelection({ start: startOverride || "", end: endOverride || "" });
    setCompareError("");
    setCompareSaveMessage("");
    setCompareSaveError("");
    try {
      const seen = new Set();
      const selected = candidates.filter((ticker) => {
        if (!isValidTickerQuery(ticker)) return false;
        const key = normalizeTickerKey(ticker);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (selected.length < 2) {
        throw new Error("비교하려면 서로 다른 종목 2개 이상을 선택해주세요.");
      }

      const requestedTable = selectedTable || "일봉";
      const tableSelections = await Promise.all(
        selected.map(async (ticker) => {
          const encoded = encodeURIComponent(ticker);
          const res = await fetch(`${API_BASE}/tickers/${encoded}/tables`);
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `종목 데이터를 업데이트하지 못했습니다: ${formatTickerLabel(ticker)}`);
          }
          const json = await res.json().catch(() => ({}));
          const tickerTables = Array.isArray(json.tables) ? json.tables : [];
          return [normalizeTickerKey(ticker), pickDefaultInterval(tickerTables, requestedTable) || requestedTable];
        }),
      );
      const tableByTicker = new Map(tableSelections);

      const perTickerResults = await Promise.all(
        selected.map(async (ticker) => {
          const params = new URLSearchParams();
          params.append("table", tableByTicker.get(normalizeTickerKey(ticker)) || requestedTable);
          if (startOverride) params.append("start_date", startOverride);
          if (endOverride) params.append("end_date", endOverride);
          const query = params.toString();
          const encoded = encodeURIComponent(ticker);
          const [priceRes, metricResult] = await Promise.all([
            fetch(`${API_BASE}/prices/${encoded}?${query}`),
            fetch(`${API_BASE}/metrics/${encoded}?${query}`)
              .then(async (res) => {
                if (!res.ok) {
                  const err = await res.json().catch(() => ({}));
                  throw new Error(err.detail || `지표 데이터를 불러오지 못했습니다: ${formatTickerLabel(ticker)}`);
                }
                return res.json();
              })
              .catch(() => null),
          ]);
          if (!priceRes.ok) {
            const err = await priceRes.json().catch(() => ({}));
            throw new Error(err.detail || `가격 데이터를 불러오지 못했습니다: ${formatTickerLabel(ticker)}`);
          }
          const priceJson = await priceRes.json();
          const series = (priceJson.data || [])
            .map((row) => ({
              date: String(row.date || "").slice(0, 10),
              close: row.adj_close ?? row.close ?? row.open ?? row.high ?? row.low ?? null,
            }))
            .filter((row) => row.date && row.close !== null && row.close !== undefined && !Number.isNaN(Number(row.close)))
            .sort((a, b) => a.date.localeCompare(b.date));
          if (series.length < 2) {
            throw new Error(`비교 가능한 데이터가 부족합니다: ${formatTickerLabel(ticker)}`);
          }
          return {
            ticker,
            label: formatTickerLabel(ticker),
            series,
            metrics: Array.isArray(metricResult?.metrics) ? metricResult.metrics : null,
          };
        }),
      );

      const overlapStart = perTickerResults
        .map((item) => item.series[0]?.date || "")
        .filter(Boolean)
        .sort()
        .slice(-1)[0];
      if (!overlapStart) {
        throw new Error("공통 비교 시작 구간을 찾을 수 없습니다.");
      }

      const chartPoints = [];
      const comparisonItems = [];
      perTickerResults.forEach((item) => {
        const sliced = item.series.filter((point) => point.date >= overlapStart);
        if (!sliced.length) return;
        comparisonItems.push({
          ticker: item.ticker,
          metrics: computeSeriesMetrics(sliced),
        });
        const base = Number(sliced[0].close);
        if (!(base > 0)) return;
        sliced.forEach((point) => {
          const value = Number(point.close);
          if (!(value > 0)) return;
          chartPoints.push({
            date: point.date,
            ticker: item.label,
            totalReturnPct: ((value / base - 1) * 100),
          });
        });
      });

      if (!chartPoints.length) {
        throw new Error("비교 차트를 계산할 수 있는 데이터가 없습니다.");
      }

      const sortedDates = Array.from(new Set(chartPoints.map((point) => point.date))).sort();
      const chartStartDate = sortedDates[0] || "";
      const chartEndDate = sortedDates[sortedDates.length - 1] || "";
      setCompareChart(chartPoints);
      setCompareDetails({
        start: chartStartDate ? `${chartStartDate}T00:00:00` : null,
        end: chartEndDate ? `${chartEndDate}T00:00:00` : null,
        items: comparisonItems,
      });
      if (!startOverride && chartStartDate) {
        setStartDate(chartStartDate);
        setStartDateInput(isoDateToDisplay(chartStartDate));
      }
      if (chartEndDate) {
        setEndDate(chartEndDate);
        setEndDateInput(isoDateToDisplay(chartEndDate));
      }
      selected.forEach((ticker) => recordRecentTickerSearch(ticker));
    } catch (error) {
      setCompareChart([]);
      setCompareDetails(null);
      setCompareError(error.message);
    } finally {
      setLoadingCompare(false);
    }
  };

  const compareChartData = useMemo(
    () => buildCompareChartData(compareChart, tickerNameMap),
    [compareChart, tickerNameMap],
  );

  const resolveTickerFromInput = (value) => {
    const match = findTickerByInput(value, tickers, tickerNameMap, tickerAliasMap);
    if (match) return match;

    const parsed = tickerInputToQueryTicker(value);
    if (parsed) {
      return tickers.find((ticker) => normalizeTickerKey(ticker) === normalizeTickerKey(parsed)) || parsed;
    }

    const query = String(value || "").trim().toLowerCase();
    if (query.length < 2) return "";
    const partialMatches = tickers.filter((ticker) => {
      const tickerKey = normalizeTickerKey(ticker);
      const names = uniqueNonEmptyStrings([INDEX_TICKER_NAMES[tickerKey], tickerNameMap?.[tickerKey], ...(tickerAliasMap?.[tickerKey] || [])]);
      return tickerKey.toLowerCase().includes(query) || names.some((name) => name.toLowerCase().includes(query));
    });
    const uniqueMatches = Array.from(new Map(partialMatches.map((ticker) => [normalizeTickerKey(ticker), ticker])).values());
    return uniqueMatches.length === 1 ? uniqueMatches[0] : "";
  };

  const commitPrimaryTickerInput = () => {
    const resolved = resolveTickerFromInput(selectedTickerInput);
    if (!resolved) return "";
    setSelectedTicker(resolved);
    setSelectedTickerInput(tickerDisplayName(resolved, tickerNameMap));
    return resolved;
  };

  const handlePrimaryTickerInputChange = (value, meta = {}) => {
    setSelectedTickerInput(value);
    if (meta.commit) {
      const resolved = meta.tickerKey
        ? tickers.find((ticker) => normalizeTickerKey(ticker) === normalizeTickerKey(meta.tickerKey)) || meta.tickerKey
        : resolveTickerFromInput(value);
      if (resolved) {
        setSelectedTicker(resolved);
        setSelectedTickerInput(tickerDisplayName(resolved, tickerNameMap));
        setTagPanelOpen(false);
      }
      return;
    }
    setSelectedTicker("");
  };

  const addCompareTicker = () => {
    if (tickers.length < 2) {
      setPriceError("비교 기능을 사용하려면 최소 두 개의 티커가 필요합니다.");
      return;
    }
    setPriceError("");
    const id = `cmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setCompareTickers((prev) => [
      ...prev,
      {
        id,
        ticker: "",
        input: "",
      },
    ]);
  };

  const removeCompareTicker = (id) => {
    setCompareTickers((prev) => {
      const next = prev.filter((item) => item.id !== id);
      if (!next.length) {
        setCompareChart([]);
        setCompareDetails(null);
        setCompareError("");
      }
      return next;
    });
  };

  const handleCompareTickerInputChange = (id, value, meta = {}) => {
    setCompareTickers((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        if (meta.commit) {
          const resolved = meta.tickerKey
            ? tickers.find((ticker) => normalizeTickerKey(ticker) === normalizeTickerKey(meta.tickerKey)) || meta.tickerKey
            : resolveTickerFromInput(value);
          if (resolved) {
            return { ...item, ticker: resolved, input: tickerDisplayName(resolved, tickerNameMap) };
          }
        }
        return { ...item, ticker: "", input: value };
      }),
    );
    if (meta.commit) {
      setTagPanelOpen(false);
    }
  };

  const comparisonPortfolioPayload = () => {
    const items = Array.isArray(compareDetails?.items) ? compareDetails.items : [];
    const tickersForPortfolio = [];
    const seen = new Set();
    items.forEach((item) => {
      const ticker = String(item?.ticker || "").trim();
      const key = normalizeTickerKey(ticker);
      if (!isValidTickerQuery(ticker) || seen.has(key)) return;
      seen.add(key);
      tickersForPortfolio.push(ticker);
    });
    if (tickersForPortfolio.length < 2) {
      throw new Error("저장할 비교 종목이 2개 이상 필요합니다.");
    }

    const baseWeight = roundTo(100 / tickersForPortfolio.length, 4);
    const holdings = tickersForPortfolio.map((ticker) => ({ ticker, weight: baseWeight }));
    const currentSum = roundTo(holdings.reduce((acc, holding) => acc + holding.weight, 0), 4);
    const delta = roundTo(100 - currentSum, 4);
    if (holdings.length && delta !== 0) {
      holdings[holdings.length - 1] = {
        ...holdings[holdings.length - 1],
        weight: roundTo(holdings[holdings.length - 1].weight + delta, 4),
      };
    }

    const labels = tickersForPortfolio.map((ticker) => tickerDisplayName(ticker, tickerNameMap));
    const defaultName = `비교 포트폴리오 - ${labels.slice(0, 3).join(", ")}${labels.length > 3 ? ` 외 ${labels.length - 3}` : ""}`;
    const prompted = window.prompt("저장할 포트폴리오 이름을 입력하세요.", defaultName);
    if (prompted === null) return null;
    const name = prompted.trim() || defaultName;
    const start = String(compareDetails?.start || "").split("T")[0] || startDate || null;
    const end = String(compareDetails?.end || "").split("T")[0] || endDate || null;

    return {
      name,
      memo: "종목 분석 화면의 다중 종목 비교에서 동일 비중으로 저장됨",
      table: selectedTable || null,
      start_date: start,
      end_date: end,
      holdings,
    };
  };

  const saveComparisonAsPortfolio = async () => {
    setCompareSaveMessage("");
    setCompareSaveError("");
    let payload = null;
    try {
      payload = comparisonPortfolioPayload();
      if (!payload) return;
    } catch (exc) {
      setCompareSaveError(exc?.message || "저장할 비교 포트폴리오를 만들 수 없습니다.");
      return;
    }

    setSavingComparePortfolio(true);
    try {
      const res = await fetch(`${API_BASE}/my-portfolios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        throw new Error("로그인 후 포트폴리오로 저장할 수 있습니다.");
      }
      if (!res.ok) {
        throw new Error(json.detail || "포트폴리오 저장에 실패했습니다.");
      }
      const savedName = String(json.name || payload.name || "포트폴리오");
      setCompareSaveMessage(`'${savedName}' 저장 완료 · 포트폴리오 분석에서 불러올 수 있습니다.`);
    } catch (exc) {
      setCompareSaveError(exc?.message || "포트폴리오 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingComparePortfolio(false);
    }
  };

  const handlePrimaryAction = () => {
    const hasStartInput = !!startDateInput.trim();
    const hasEndInput = !!endDateInput.trim();
    const completedStart = completeDateInput(startDateInput, "start");
    const completedEnd = completeDateInput(endDateInput, "end");
    const defaultStart = defaultAnalysisStartDate();
    const nextStart = hasStartInput ? (completedStart ? completedStart.iso : "") : defaultStart;
    const nextEnd = hasEndInput && completedEnd ? completedEnd.iso : "";

    if (!hasStartInput) {
      setStartDate(defaultStart);
      setStartDateInput(isoDateToDisplay(defaultStart));
    } else if (completedStart) {
      setStartDate(completedStart.iso);
      setStartDateInput(completedStart.display);
    } else {
      setStartDate("");
    }

    if (!hasEndInput) {
      setEndDate("");
      setEndDateInput("");
    } else if (completedEnd) {
      setEndDate(completedEnd.iso);
      setEndDateInput(completedEnd.display);
    } else {
      setEndDate("");
    }

    const primaryTicker = commitPrimaryTickerInput();
    const resolvedCompareTickers = compareTickers.map((item) => ({
      ...item,
      ticker: item.ticker || resolveTickerFromInput(item.input),
    }));
    setCompareTickers((prev) =>
      prev.map((item) => {
        const resolved = item.ticker || resolveTickerFromInput(item.input);
        return resolved ? { ...item, ticker: resolved, input: tickerDisplayName(resolved, tickerNameMap) } : item;
      }),
    );
    const requestCandidates = [primaryTicker, ...resolvedCompareTickers.map((item) => item.ticker)];
    const hasUnresolvedCompareInput = resolvedCompareTickers.some(
      (item) => String(item.input || "").trim() && !isValidTickerQuery(item.ticker),
    );
    if (hasUnresolvedCompareInput) {
      setCompareChart([]);
      setCompareDetails(null);
      setCompareError("비교 종목을 선택 목록에서 고르거나 정확한 티커를 입력해주세요.");
      return;
    }
    const requestCompareCount = (() => {
      const seen = new Set();
      let count = 0;
      requestCandidates.forEach((ticker) => {
        if (!isValidTickerQuery(ticker)) return;
        const key = normalizeTickerKey(ticker);
        if (seen.has(key)) return;
        seen.add(key);
        count += 1;
      });
      return count;
    })();

    if (requestCompareCount >= 2) {
      fetchCompareData(nextStart, nextEnd, requestCandidates);
    } else {
      fetchPriceData(nextStart, nextEnd, primaryTicker);
    }
  };

  const applyAnalysisRange = (option) => {
    const nextStart = analysisRangeStartDate(option);
    const nextEnd = "";
    setRangeSelection({ start: nextStart, end: nextEnd });
    setStartDate(nextStart);
    setStartDateInput(nextStart ? isoDateToDisplay(nextStart) : "");
    setEndDate(nextEnd);
    setEndDateInput("");

    const primaryTicker = commitPrimaryTickerInput();
    const resolvedCompareTickers = compareTickers.map((item) => ({
      ...item,
      ticker: item.ticker || resolveTickerFromInput(item.input),
    }));
    setCompareTickers((prev) =>
      prev.map((item) => {
        const resolved = item.ticker || resolveTickerFromInput(item.input);
        return resolved ? { ...item, ticker: resolved, input: tickerDisplayName(resolved, tickerNameMap) } : item;
      }),
    );

    const requestCandidates = [primaryTicker, ...resolvedCompareTickers.map((item) => item.ticker)];
    const hasUnresolvedCompareInput = resolvedCompareTickers.some(
      (item) => String(item.input || "").trim() && !isValidTickerQuery(item.ticker),
    );
    if (hasUnresolvedCompareInput) {
      setCompareChart([]);
      setCompareDetails(null);
      setCompareError("비교 종목을 선택 목록에서 고르거나 정확한 티커를 입력해주세요.");
      return;
    }
    const uniqueValidTickers = new Set(
      requestCandidates
        .filter((ticker) => isValidTickerQuery(ticker))
        .map((ticker) => normalizeTickerKey(ticker)),
    );
    if (uniqueValidTickers.size >= 2) {
      fetchCompareData(nextStart, nextEnd, requestCandidates);
    } else if (isValidTickerQuery(primaryTicker)) {
      fetchPriceData(nextStart, nextEnd, primaryTicker);
    }
  };

  const applyChartDateRange = (nextStart, nextEnd) => {
    if (!nextStart || !nextEnd || nextStart >= nextEnd) return;
    setStartDate(nextStart);
    setStartDateInput(isoDateToDisplay(nextStart));
    setEndDate(nextEnd);
    setEndDateInput(isoDateToDisplay(nextEnd));
    const primaryTicker = selectedTicker || resolveTickerFromInput(selectedTickerInput);
    if (isValidTickerQuery(primaryTicker)) {
      fetchPriceData(nextStart, nextEnd, primaryTicker);
    }
  };

  // Highlight the requested window; response dates still describe actual trading data.
  const activeRangeKey = analysisRangeKeyForSelection(rangeSelection || { start: startDate, end: endDate });

  // 2026-09-19 UI 개선: 빠른 기간 칩을 분석 전에도 노출 — 칩 조각을 공유해
  // 차트 툴바(분석 후)와 분석 조건 영역(분석 전) 양쪽에서 같은 버튼을 쓴다.
  const rangeShortcutButtons = ANALYSIS_RANGE_OPTIONS.map((option) => (
    <button
      key={option.key}
      type="button"
      className={`range-shortcut-button${activeRangeKey === option.key ? " active" : ""}`}
      onClick={() => applyAnalysisRange(option)}
      aria-pressed={activeRangeKey === option.key}
      title={analysisRangeTitle(option)}
    >
      {option.label}
    </button>
  ));

  const primaryCandidate = selectedTicker || resolveTickerFromInput(selectedTickerInput);
  const primaryValid = isValidTickerQuery(primaryCandidate);
  const validCompareCount = (() => {
    const seen = new Set();
    let count = 0;
    [primaryCandidate, ...compareTickers.map((item) => item.ticker || resolveTickerFromInput(item.input))].forEach((ticker) => {
      if (!isValidTickerQuery(ticker)) return;
      const key = normalizeTickerKey(ticker);
      if (seen.has(key)) return;
      seen.add(key);
      count += 1;
    });
    return count;
  })();
  const hasComparison = validCompareCount >= 2;
  const hasCompareInput = compareTickers.some((item) => String(item.input || item.ticker || "").trim());
  const showingComparisonResults = (hasComparison || (!!compareError && hasCompareInput)) && (loadingCompare || compareChartData.labels.length > 0 || !!compareDetails || !!compareError);
  const priceChartLabels = useMemo(() => priceLabels(priceSeries), [priceSeries]);
  const priceCandles = useMemo(() => candlePoints(priceSeries), [priceSeries]);
  // ===== 지표 계산 (표시 중인 동일 시계열 기준 — 혼용 금지, warmup 구간은 N/A) =====
  const analysisCloses = useMemo(
    () => priceSeries.map((p) => Number(p?.close)).filter((v) => Number.isFinite(v)),
    [priceSeries],
  );

  const smaSeries = (values, window) => {
    const out = new Array(values.length).fill(null);
    let running = 0;
    const queue = [];
    values.forEach((v, i) => {
      queue.push(v); running += v;
      if (queue.length > window) running -= queue.shift();
      if (queue.length === window) out[i] = running / window;
    });
    return out;
  };

  const emaSeries = (values, window) => {
    const out = new Array(values.length).fill(null);
    const k = 2 / (window + 1);
    let prev = null;
    let running = 0;
    const queue = [];
    values.forEach((v, i) => {
      if (prev === null) {
        queue.push(v); running += v;
        if (queue.length === window) { prev = running / window; out[i] = prev; }
      } else {
        prev = prev + k * (v - prev);
        out[i] = prev;
      }
    });
    return out;
  };

  const rsiSeries = (values, period) => {
    const out = new Array(values.length).fill(null);
    if (values.length <= period) return out;
    let avgGain = 0; let avgLoss = 0;
    for (let i = 1; i <= period; i += 1) {
      const diff = values[i] - values[i - 1];
      if (diff >= 0) avgGain += diff; else avgLoss -= diff;
    }
    avgGain /= period; avgLoss /= period;
    out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    for (let i = period + 1; i < values.length; i += 1) {
      const diff = values[i] - values[i - 1];
      const gain = diff > 0 ? diff : 0;
      const loss = diff < 0 ? -diff : 0;
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
      out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    }
    return out;
  };

  const macdSeries = (values) => {
    const emaFast = emaSeries(values, 12);
    const emaSlow = emaSeries(values, 26);
    const macdLine = values.map((_, i) => (emaFast[i] !== null && emaSlow[i] !== null ? emaFast[i] - emaSlow[i] : null));
    const signal = new Array(values.length).fill(null);
    const k = 2 / 10;
    let prev = null;
    const queue = [];
    let running = 0;
    macdLine.forEach((v, i) => {
      if (v === null) return;
      if (prev === null) {
        queue.push(v); running += v;
        if (queue.length === 9) { prev = running / 9; signal[i] = prev; }
      } else {
        prev = prev + k * (v - prev);
        signal[i] = prev;
      }
    });
    const hist = macdLine.map((v, i) => (v !== null && signal[i] !== null ? v - signal[i] : null));
    return { macdLine, signal, hist };
  };

  const bollingerSeries = (values, window, numStd) => {
    const mid = smaSeries(values, window);
    const upper = new Array(values.length).fill(null);
    const lower = new Array(values.length).fill(null);
    for (let i = window - 1; i < values.length; i += 1) {
      if (mid[i] === null) continue;
      const w = values.slice(i - window + 1, i + 1);
      const mean = mid[i];
      const variance = w.reduce((acc, v) => acc + (v - mean) ** 2, 0) / w.length;
      const sd = Math.sqrt(variance);
      upper[i] = mean + numStd * sd;
      lower[i] = mean - numStd * sd;
    }
    return { upper, mid, lower };
  };

  const indicatorOverlayData = useMemo(() => {
    if (!priceSeries.length || !analysisCloses.length) return null;
    const data = {};
    const has = (key) => selectedIndicators.includes(key);
    if (has("sma20")) data.sma20 = smaSeries(analysisCloses, 20);
    if (has("sma50")) data.sma50 = smaSeries(analysisCloses, 50);
    if (has("sma200")) data.sma200 = smaSeries(analysisCloses, 200);
    if (has("ema20")) data.ema20 = emaSeries(analysisCloses, 20);
    if (has("bollinger")) data.bollinger = bollingerSeries(analysisCloses, 20, 2);
    if (has("rsi")) data.rsi = rsiSeries(analysisCloses, 14);
    if (has("macd")) data.macd = macdSeries(analysisCloses);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceSeries, analysisCloses, selectedIndicators]);

  const volumeSubData = useMemo(
    () => priceSeries.map((p) => ({ date: String(p?.date || "").slice(0, 10), volume: p?.volume === null || p?.volume === undefined ? null : Number(p.volume) })),
    [priceSeries],
  );
  const effectiveChartMode = chartMode === "candle" && !showingComparisonResults && !benchmarkEnabled ? "candle" : "line";
  const valuationPointByDate = useMemo(() => {
    const lookup = new Map();
    valuationSeries.forEach((point) => {
      if (point?.date) lookup.set(point.date, point);
    });
    return lookup;
  }, [valuationSeries]);
  const benchmarkOverlay = useMemo(() => buildBenchmarkOverlay(priceSeries, benchmarkData), [priceSeries, benchmarkData]);
  const priceChartDatasets = useMemo(() => {
    const baseDataset = {
      label: tickerDisplayName(selectedTicker, tickerNameMap),
      data: priceValues(priceSeries),
      borderColor: "#111827",
    };
    const shownAnomalyPeriods = new Set();
    const epsValues = [];
    const anomalyNotes = [];
    const epsPointRadius = [];
    const epsPointBackgroundColor = [];
    priceSeries.forEach((pricePoint) => {
      const date = new Date(pricePoint.date).toISOString().slice(0, 10);
      const valuationPoint = valuationPointByDate.get(date);
      epsValues.push(valuationPoint?.ttm_eps ?? null);
      const periodEnd = valuationPoint?.eps_as_of_period_end || "";
      const notes = Array.isArray(valuationPoint?.eps_anomaly_notes) ? valuationPoint.eps_anomaly_notes : [];
      const showMarker = notes.length > 0 && periodEnd && !shownAnomalyPeriods.has(periodEnd);
      if (showMarker) shownAnomalyPeriods.add(periodEnd);
      anomalyNotes.push(showMarker ? notes : null);
      epsPointRadius.push(showMarker ? 2.6 : 1.4);
      epsPointBackgroundColor.push(showMarker ? "#f59e0b" : "#059669");
    });
    const indicatorColors = {
      sma20: "#0ea5e9", sma50: "#8b5cf6", sma200: "#f97316", ema20: "#14b8a6",
    };
    const datasets = [baseDataset];
    // 지표 오버레이 (선택 시) — 동일 시계열, warmup null = Chart.js spanGaps 미적용 구간
    const addOverlay = (key, values, color, dash) => {
      if (!values) return;
      datasets.push({
        label: key.toUpperCase(),
        data: values,
        borderColor: color,
        borderDash: dash || [],
        borderWidth: 1.4,
        pointRadius: 0,
        pointHitRadius: 6,
        spanGaps: false,
        fill: false,
      });
    };
    if (indicatorOverlayData) {
      addOverlay("sma20", indicatorOverlayData.sma20, indicatorColors.sma20);
      addOverlay("sma50", indicatorOverlayData.sma50, indicatorColors.sma50);
      addOverlay("sma200", indicatorOverlayData.sma200, indicatorColors.sma200);
      addOverlay("ema20", indicatorOverlayData.ema20, indicatorColors.ema20);
      if (indicatorOverlayData.bollinger) {
        addOverlay("bb_upper", indicatorOverlayData.bollinger.upper, "#94a3b8", [4, 3]);
        addOverlay("bb_lower", indicatorOverlayData.bollinger.lower, "#94a3b8", [4, 3]);
      }
      // RSI/MACD는 가격 축과 스케일이 달라 2차 축(y1)에 중첩 표시 — 값 참고용 선만,
      // 과매수/과매도 판정 문구 없음(제안 안전장치 2).
      if (indicatorOverlayData.rsi) {
        datasets.push({
          label: "RSI-14",
          data: indicatorOverlayData.rsi,
          borderColor: "#a855f7",
          borderWidth: 1.4,
          pointRadius: 0,
          pointHitRadius: 6,
          spanGaps: false,
          fill: false,
          yAxisID: "y1",
          yAxisLabel: "RSI",
        });
      }
      if (indicatorOverlayData.macd) {
        datasets.push({
          label: "MACD",
          data: indicatorOverlayData.macd.macdLine,
          borderColor: "#0d9488",
          borderWidth: 1.4,
          pointRadius: 0,
          pointHitRadius: 6,
          spanGaps: false,
          fill: false,
          yAxisID: "y1",
          yAxisLabel: "MACD",
        });
        datasets.push({
          label: "Signal",
          data: indicatorOverlayData.macd.signal,
          borderColor: "#f59e0b",
          borderDash: [4, 3],
          borderWidth: 1.2,
          pointRadius: 0,
          pointHitRadius: 6,
          spanGaps: false,
          fill: false,
          yAxisID: "y1",
        });
      }
    }
    // Compare index returns on the displayed stock's price scale, not raw normalized-100 values.
    if (benchmarkEnabled && benchmarkOverlay) {
      datasets.push({
        label: `${benchmarkTicker || "Benchmark"} · ${sfText("시작주가 환산")}`,
        data: benchmarkOverlay.values,
        borderColor: "#dc2626",
        borderDash: [6, 4],
        borderWidth: 1.6,
        pointRadius: 0,
        pointHitRadius: 6,
        spanGaps: true,
        fill: false,
      });
    }
    if (!showEpsOverlay || !valuationSeries.length) {
      return datasets;
    }
    return [
      ...datasets,
      {
        label: sfText("12개월 EPS"),
        data: epsValues,
        borderColor: "#059669",
        borderWidth: 4,
        stepped: "before",
        spanGaps: true,
        pointRadius: epsPointRadius,
        pointHoverRadius: 4.5,
        pointHitRadius: 18,
        pointBackgroundColor: epsPointBackgroundColor,
        pointBorderColor: "rgba(255,255,255,0)",
        pointBorderWidth: 0,
        yAxisID: "y1",
        yAxisLabel: "EPS",
        axisColor: "#047857",
        anomalyNotes,
      },
    ];
  }, [priceSeries, selectedTicker, tickerNameMap, showEpsOverlay, valuationSeries.length, valuationPointByDate, indicatorOverlayData, benchmarkEnabled, benchmarkData, benchmarkTicker, benchmarkOverlay]);

  const actionLabel =
    hasComparison
      ? loadingCompare
        ? "비교 분석 중..."
        : "분석 업데이트"
      : loadingPrice
        ? "불러오는 중..."
        : "가격 & 지표 불러오기";

  const actionDisabled = hasComparison
    ? loadingCompare || !primaryValid
    : loadingPrice || !primaryValid;

  const handleAnalysisInputKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (actionDisabled) return;
    handlePrimaryAction();
  };

  // B workbench 정합성: 식별 헤더가 근거로 쓰는 "마지막으로 실제 로드된 가격 시계열의 티커".
  // priceInteractionBounds.ticker는 fetchPriceData 성공 시에만 갱신되므로 입력 편집 중에는 불변.
  const loadedPriceTickerKey = normalizeTickerKey(priceInteractionBounds.ticker || "");

  return (
    <div className="analysis-page analysis-workbench">
      <WorkbenchIdentity
        snapshot={snapshotData}
        snapshotTickerKey={snapshotTickerKey}
        priceTickerKey={loadedPriceTickerKey}
        loading={snapshotState === "loading"}
        error={snapshotError}
        tickerNameMap={tickerNameMap}
      />
      <nav className="wb-toplevel" aria-label={sfText("관련 링크")}>
        <a href={isEnglishLang() ? "/portfolio-analysis?lang=en" : "/portfolio-analysis"}>
          {sfText("포트폴리오 분석 시작")} <span aria-hidden="true">→</span>
        </a>
        {isAdmin && (
          <button
            type="button"
            className="wb-bare-button admin-button"
            onClick={() => {
              window.location.href = "/db-admin";
            }}
          >
            DB관리
          </button>
        )}
      </nav>

      <div className="wb-setup">
        <div className="wb-setup-inputs">
          <TickerAutocompleteInput
            label="종목 선택"
            inputValue={selectedTickerInput}
            onInputChange={handlePrimaryTickerInputChange}
            tickers={tickers}
            tickerNameMap={tickerNameMap}
            tickerAliasMap={tickerAliasMap}
            tickerTagMap={tickerTagMap}
            selectedTags={selectedTags}
            tagMode={tagMode}
            recentTickerKeys={recentTickerKeys}
            recentOnlyWhenEmpty={true}
            savedPortfolioItems={analysisSavedPortfolios}
            portfolioTickerMembership={portfolioTickerMembership}
            activePortfolioStarTicker={activePortfolioStarTicker}
            portfolioQuickAddMessage={portfolioQuickAddMessage}
            portfolioQuickAddError={portfolioQuickAddError}
            loadingSavedPortfolios={loadingAnalysisSavedPortfolios}
            onPortfolioStarClick={handlePortfolioStarClick}
            onPortfolioQuickAdd={addTickerToSavedPortfolio}
            onOpenTagSearch={() => {
              setTagPanelTarget("primary");
              setTagPanelOpen(true);
            }}
            placeholder="티커 또는 종목명을 입력하세요"
            onEnterKey={handlePrimaryAction}
          />
          <label>
            시작일
            <input
              type="text"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              onKeyDown={handleAnalysisInputKeyDown}
              onBlur={() => {
                const completed = completeDateInput(startDateInput, "start");
                if (!startDateInput.trim()) {
                  setStartDateInput("");
                  return;
                }
                if (completed) {
                  setStartDateInput(completed.display);
                }
              }}
              placeholder="YYYY. MM. DD"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
          <label>
            종료일
            <input
              type="text"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              onKeyDown={handleAnalysisInputKeyDown}
              onBlur={() => {
                const completed = completeDateInput(endDateInput, "end");
                if (!endDateInput.trim()) {
                  setEndDateInput("");
                  return;
                }
                if (completed) {
                  setEndDateInput(completed.display);
                }
              }}
              placeholder="YYYY. MM. DD"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
        </div>
        {compareTickers.length > 0 && (
          <div className="compare-ticker-list">
            {compareTickers.map((item, idx) => (
              <div key={item.id} className="compare-ticker-row">
                <TickerAutocompleteInput
                  label={`비교 종목 ${idx + 1}`}
                  inputValue={item.input}
                  onInputChange={(value, meta) => handleCompareTickerInputChange(item.id, value, meta)}
                  tickers={tickers}
                  tickerNameMap={tickerNameMap}
                  tickerAliasMap={tickerAliasMap}
                  tickerTagMap={tickerTagMap}
                  selectedTags={selectedTags}
                  tagMode={tagMode}
                  recentTickerKeys={recentTickerKeys}
                  recentOnlyWhenEmpty={true}
                  savedPortfolioItems={analysisSavedPortfolios}
                  portfolioTickerMembership={portfolioTickerMembership}
                  activePortfolioStarTicker={activePortfolioStarTicker}
                  portfolioQuickAddMessage={portfolioQuickAddMessage}
                  portfolioQuickAddError={portfolioQuickAddError}
                  loadingSavedPortfolios={loadingAnalysisSavedPortfolios}
                  onPortfolioStarClick={handlePortfolioStarClick}
                  onPortfolioQuickAdd={addTickerToSavedPortfolio}
                  onOpenTagSearch={() => {
                    setTagPanelTarget(`compare:${item.id}`);
                    setTagPanelOpen(true);
                  }}
                  placeholder="티커 또는 종목명을 입력하세요"
                  onEnterKey={handlePrimaryAction}
                />
                <button
                  type="button"
                  className="ghost-button compare-remove-button"
                  onClick={() => removeCompareTicker(item.id)}
                >
                  제거
                </button>
              </div>
            ))}
          </div>
        )}
        <TagSearchPanel
          open={tagPanelOpen}
          tagsByCategory={tagList}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          mode={tagMode}
          setMode={setTagMode}
          recommended={recommendedTickerKeys}
          tickerNameMap={tickerNameMap}
          onPickTickerKey={(tickerKey) => pickTickerKey(tickerKey, tagPanelTarget)}
          onClose={() => setTagPanelOpen(false)}
        />
        <div className="wb-actions">
          <button onClick={handlePrimaryAction} disabled={actionDisabled}>
            {actionLabel}
          </button>
          <button type="button" className="ghost-button" onClick={addCompareTicker}>
            비교 종목 추가
          </button>
          {hasComparison && (
            <span className="wb-compare-hint">{sfText("비교 분석 모드")}</span>
          )}
        </div>
        {/* B workbench: 기간 선택은 화면에서 단 한 인스턴스 — 첫 조회 전에도 항상 접근 가능. */}
        <div className="wb-range" role="group" aria-label={sfText("빠른 기간 선택")}>
          <span className="wb-range-label">{sfText("빠른 기간 선택")}</span>
          <div className="range-shortcuts">{rangeShortcutButtons}</div>
        </div>
      </div>

      <div className="wb-columns">
        <div className="wb-main">
          {(priceSeries.length > 0 || showingComparisonResults) && (
            <div className="chart-toolbar">
              <div className="chart-primary-controls">
                {priceSeries.length > 0 && !showingComparisonResults && (
                  <button
                    type="button"
                    className={`ghost-button eps-toggle-button${showEpsOverlay ? " active" : ""}`}
                    onClick={() => setShowEpsOverlay((prev) => !prev)}
                    disabled={!valuationSeries.length || effectiveChartMode === "candle"}
                    aria-pressed={showEpsOverlay}
                  >
                    {showEpsOverlay ? "EPS 숨기기" : "EPS 표시"}
                  </button>
                )}
              </div>
              <div className="chart-secondary-controls">
                <div className="chart-mode-toggle" role="group" aria-label={sfText("차트 유형")}>
                  <button
                    type="button"
                    className={`chart-mode-button${effectiveChartMode === "line" ? " active" : ""}`}
                    onClick={() => setChartMode("line")}
                    aria-pressed={effectiveChartMode === "line"}
                    title={sfText("라인 차트")}
                  >
                    {sfText("라인")}
                  </button>
                  <button
                    type="button"
                    className={`chart-mode-button${effectiveChartMode === "candle" ? " active" : ""}`}
                    onClick={() => setChartMode("candle")}
                    disabled={showingComparisonResults || benchmarkEnabled}
                    aria-pressed={effectiveChartMode === "candle"}
                    title={benchmarkEnabled ? sfText("벤치마크를 끄면 캔들 차트를 선택할 수 있습니다.") : sfText("캔들 차트")}
                  >
                    {sfText("캔들")}
                  </button>
                </div>
                <label>
                  인터벌
                  <select
                    value={selectedTable}
                    onChange={(e) => setSelectedTable(e.target.value)}
                    disabled={!tables.length}
                  >
                    {tables.map((table) => (
                      <option key={table} value={table}>
                        {intervalDisplayName(table)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
          {!showingComparisonResults ? (
            <>
              {priceSeries.length > 0 && !showingComparisonResults && (
                <details className="analysis-indicator-panel">
                  <summary>
                    <span>{sfText("보조지표")}{selectedIndicators.length > 0 ? ` (${selectedIndicators.length})` : ""}</span>
                    <span className="indicator-expand-label">{sfText("펼치기")}</span>
                    <span className="indicator-collapse-label">{sfText("접기")}</span>
                  </summary>
                  <div className="indicator-chip-group" role="group" aria-label={sfText("보조지표 선택")} title={ANALYSIS_INDICATOR_DISCLAIMER}>
                    {ANALYSIS_INDICATOR_DEFS.map((def) => {
                      const active = selectedIndicators.includes(def.key);
                      return (
                        <button key={def.key} type="button" className={`indicator-chip${active ? " active" : ""}`}
                          onClick={() => setSelectedIndicators((prev) => active ? prev.filter((k) => k !== def.key) : [...prev, def.key])}
                          aria-pressed={active}>{def.label}</button>
                      );
                    })}
                    <button type="button" className={`indicator-chip volume-chip${showVolume ? " active" : ""}`}
                      onClick={() => setShowVolume((prev) => !prev)} aria-pressed={showVolume}
                      title={sfText("거래량 서브차트 표시")}>{sfText("거래량")}</button>
                    <button type="button" className={`indicator-chip benchmark-chip${benchmarkEnabled ? " active" : ""}`}
                      onClick={() => setBenchmarkEnabled((prev) => !prev)} aria-pressed={benchmarkEnabled}
                      title={sfText("기준지수는 라인 차트에 시작주가 기준으로 환산해 표시합니다.")}>
                      {sfText(`벤치마크 ${benchmarkTicker || ""}`)}
                    </button>
                  </div>
                </details>
              )}
              {priceError && <div className="error-box">{priceError}</div>}
              {!priceSeries.length && !priceError && !loadingPrice && (
                <div className="chart-wrapper analysis-empty-state" aria-disabled="true">
                  <div className="analysis-empty-chart-title">{sfText("가격 추세")}</div>
                  <div className="analysis-empty-chart-grid" aria-hidden="true" />
                  <strong className="analysis-empty-chart-message">{sfText("분석할 종목을 선택하세요")}</strong>
                </div>
              )}
              {loadingPrice && !priceSeries.length && !priceError && (
                <div className="chart-wrapper analysis-empty-state loading" aria-live="polite" aria-busy="true">
                  <div className="analysis-empty-chart-title">{sfText("가격 추세")}</div>
                  <div className="analysis-empty-chart-grid" aria-hidden="true" />
                  <strong className="analysis-empty-chart-message">{sfText("불러오는 중...")}</strong>
                </div>
              )}
              {priceSeries.length > 0 && (
                <>
                  {showEpsOverlay && valuationError && <div className="valuation-inline-note">{valuationError}</div>}
                  {effectiveChartMode === "candle" ? (
                    <CandlestickChart
                      data={priceCandles}
                      title={sfText("가격 추세")}
                      yLabel={sfText("가격")}
                    />
                  ) : (
                    <LineChart
                      labels={priceChartLabels}
                      datasets={priceChartDatasets}
                      title={sfText("가격 추세")}
                      yLabel={sfText("가격")}
                      onRangeChange={applyChartDateRange}
                      rangeBounds={priceInteractionBounds}
                    />
                  )}
                  {benchmarkEnabled && benchmarkOverlay && (
                    <p className="analysis-basis-note benchmark-overlay-note">
                      {sfText("기준지수는 첫 공통 거래일의 종목 주가로 환산한 비교선입니다. 실제 지수 가격이 아닙니다.")} ({benchmarkOverlay.baseDate})
                    </p>
                  )}
                  {benchmarkEnabled && benchmarkState === "done" && !benchmarkOverlay && (
                    <p className="analysis-basis-note">{sfText("선택 구간에 기준지수와 겹치는 가격 데이터가 부족해 비교선을 표시하지 않습니다.")}</p>
                  )}
                  {effectiveChartMode === "candle" && (
                    <p className="analysis-basis-note">{sfText("캔들 차트는 원문 가격 기준(분할 조정 포함, 배당 미반영)입니다.")}</p>
                  )}
                  {(selectedIndicators.length > 0 || showEpsOverlay) && (
                    <p className="indicator-disclaimer">{ANALYSIS_INDICATOR_DISCLAIMER}</p>
                  )}
                  {showVolume && priceSeries.length > 0 && (
                    <div className="volume-subchart" aria-label={sfText("거래량")}>
                      <h4>{sfText("거래량")}</h4>
                      <div className="volume-bars" role="img">
                        {(() => {
                          const vols = volumeSubData.map((v) => v.volume);
                          const maxV = Math.max(...vols.filter((v) => v !== null && Number.isFinite(v)), 1);
                          const upColor = "var(--chart-rise, #16a34a)";
                          const downColor = "var(--chart-fall, #dc2626)";
                          return volumeSubData.map((v, i) => {
                            if (v.volume === null || !Number.isFinite(v.volume)) {
                              return <span key={v.date || i} className="volume-bar is-null" title={`${v.date} N/A`} />;
                            }
                            const prevClose = i > 0 ? Number(priceSeries[i - 1]?.close) : null;
                            const curClose = Number(priceSeries[i]?.close);
                            const rising = prevClose !== null && Number.isFinite(prevClose) ? curClose >= prevClose : true;
                            return (
                              <span
                                key={v.date || i}
                                className="volume-bar"
                                style={{ height: `${Math.max(1, (v.volume / maxV) * 100)}%`, backgroundColor: rising ? upColor : downColor }}
                                title={`${v.date} ${Math.round(v.volume).toLocaleString()}`}
                              />
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                  <AnalysisSummary metrics={metrics} />
                  <p className="analysis-basis-note">{sfText("수익률·통계는 선택 기간 종가(adj_close 우선, 없으면 close) 기준입니다. 샤프/소르티노는 무위험 수익률 0% 가정입니다.")}</p>
                  {benchmarkEnabled && (
                    <AnalysisModuleCard
                      id="analysis-benchmark"
                      title={sfText(`벤치마크 대비 (${benchmarkTicker})`)}
                      state={benchmarkState}
                      errorText={benchmarkError}
                      reason={benchmarkData && !benchmarkData.sufficient ? sfText(`공통 거래일 ${benchmarkData.common_days}개로 표본 부족(최소 ${benchmarkData.min_days_required}개) — 상관·베타 미표시`) : ""}
                    >
                      {benchmarkData?.sufficient && (
                        <div className="benchmark-grid">
                          <table className="benchmark-table">
                            <tbody>
                              <tr><th scope="row">{sfText("구간 수익률 (종목)")}</th><td>{formatSnapPct(benchmarkData.ticker_return_pct)}</td></tr>
                              <tr><th scope="row">{sfText("구간 수익률 (벤치마크)")}</th><td>{formatSnapPct(benchmarkData.benchmark_return_pct)}</td></tr>
                              <tr><th scope="row">{sfText("상관계수")}</th><td>{benchmarkData.correlation !== null ? benchmarkData.correlation.toFixed(3) : "N/A"}</td></tr>
                              <tr><th scope="row">{sfText("베타")}</th><td>{benchmarkData.beta !== null ? benchmarkData.beta.toFixed(3) : "N/A"}</td></tr>
                              <tr><th scope="row">{sfText("공통 거래일")}</th><td>{benchmarkData.common_days}</td></tr>
                            </tbody>
                          </table>
                          <p className="benchmark-note">
                            {benchmarkData.dates?.length > 0 && <span>{benchmarkData.dates[0]} ~ {benchmarkData.dates[benchmarkData.dates.length - 1]} · {sfText("일봉 공통 데이터 기준")}<br /></span>}
                            {sfText("상관·베타는 선택 구간 의존적입니다. KR 종목 vs 미국 벤치마크 비교 시 통화가 다릅니다(환율 미반영).")}
                          </p>
                        </div>
                      )}
                    </AnalysisModuleCard>
                  )}
                  {perBandData && (
                    <AnalysisModuleCard id="analysis-per-band" title={sfText("밸류에이션 밴드 (PER 분포)")} state={perBandState}>
                      <PerBandCard band={perBandData} />
                    </AnalysisModuleCard>
                  )}
                  <AnalysisModuleCard
                    id="analysis-financials"
                    title={sfText("공식 분기 재무 (확정 데이터)")}
                    state={financialsState}
                    errorText={financialsError}
                    reason={null}
                  >
                    {financialsData && <FinancialsCard financials={financialsData} />}
                  </AnalysisModuleCard>
                  {guruOwnersState === "done" && Array.isArray(guruOwners) && guruOwners.length > 0 && (
                    <AnalysisModuleCard
                      id="analysis-guru-owners"
                      title={sfText("거장 13F 보유 현황")}
                      state="done"
                      errorText={null}
                      reason={null}
                    >
                      <GuruOwnersCard guruOwners={guruOwners} />
                    </AnalysisModuleCard>
                  )}
                  <AnalysisModuleCard
                    id="analysis-drawdown"
                    title={sfText("드로다운 (선택 기간)")}
                    state="done"
                    errorText={null}
                    reason={null}
                  >
                    <DrawdownCard priceSeries={priceSeries} />
                  </AnalysisModuleCard>
                  <AnalysisModuleCard
                    id="analysis-monthly"
                    title={sfText("월수익 히트맵")}
                    state="done"
                    errorText={null}
                    reason={null}
                  >
                    <MonthlyReturnsCard priceSeries={priceSeries} />
                  </AnalysisModuleCard>
                  <MetricsGrid metrics={metrics} />
                </>
              )}
              <RelatedPostsCard state={relatedState} posts={relatedPosts} />
            </>
          ) : (
            <>
              {compareError && <div className="error-box">{compareError}</div>}
              {loadingCompare && !compareChartData.labels.length && !compareError && (
                <div className="admin-status">비교 차트를 계산하는 중입니다...</div>
              )}
              {compareChartData.labels.length > 0 && (
                <LineChart
                  labels={compareChartData.labels}
                  datasets={compareChartData.datasets}
                  title={sfText("토탈 리턴 (%)")}
                  yLabel="%"
                />
              )}
              {compareDetails && (
                <>
                  <div className="compare-result-actions">
                    <p className="compare-range-label">
                      {compareDetails.start?.split("T")[0]} ~ {compareDetails.end?.split("T")[0]}
                    </p>
                    <button
                      type="button"
                      className="ghost-button compare-save-button"
                      onClick={saveComparisonAsPortfolio}
                      disabled={savingComparePortfolio || !compareDetails?.items?.length}
                    >
                      {savingComparePortfolio ? "저장 중..." : "포트폴리오로 저장"}
                    </button>
                    <a className="ghost-button compare-lab-link" href="/portfolio-analysis">
                      포트폴리오 분석 열기
                    </a>
                  </div>
                  {compareSaveMessage && <div className="success-box compare-save-status">{compareSaveMessage}</div>}
                  {compareSaveError && <div className="error-box compare-save-status">{compareSaveError}</div>}
                  <CompareMetricsTable items={compareDetails.items} tickerNameMap={tickerNameMap} />
                </>
              )}
            </>
          )}
        </div>

        <aside className="wb-side wb-facts">
          {!showingComparisonResults && (
            <>
              <section className="wb-facts-section wb-facts-snapshot" aria-label={sfText("종목 스냅샷")}>
                <h2 className="wb-facts-title">{sfText("종목 스냅샷")}</h2>
                {/* 기존 스냅샷 모듈을 사이드바로 이동(삭제 아님). 배당 주의 문구는 식별 헤더가
                    로드된 스냅샷 티커 기준으로 한 번만 표시하므로 여기서는 비활성화. */}
                <SnapshotHeader
                  snapshot={snapshotData}
                  loading={snapshotState === "loading"}
                  error={snapshotError}
                  krNoDividend={false}
                />
                <div className="analysis-top-actions">
                  <button
                    type="button"
                    className={`ghost-button follow-ticker-button${followState === "following" ? " active" : ""}`}
                    onClick={toggleFollowTicker}
                    disabled={followState === "checking" || followState === "idle"}
                  >
                    {followState === "following" ? sfText("★ 팔로우 중") : sfText("☆ 이 종목 팔로우")}
                  </button>
                  {followMessage && <span className="follow-message">{followMessage}</span>}
                  {followState !== "following" && followState !== "checking" && (
                    <a className="follow-login-hint" href={`/login?next=${encodeURIComponent("/analysis?t=" + normalizeTickerKey(selectedTicker || ""))}`}>
                      {sfText("로그인 후 팔로우 알림 받기")}
                    </a>
                  )}
                </div>
              </section>
              <AnalysisModuleCard
                id="analysis-events"
                title={sfText("공시·실적 접수 이력")}
                state={eventsState}
                errorText={eventsError}
                reason={null}
              >
                {eventsData && (
                  <EventsCard
                    events={eventsData.events}
                    sourceNote={sfText(eventsData.note || "")}
                  />
                )}
              </AnalysisModuleCard>
            </>
          )}
          {showingComparisonResults && (
            <section className="wb-facts-section" aria-label={sfText("비교 분석")}>
              <h2 className="wb-facts-title">{sfText("비교 분석")}</h2>
              <p className="wb-facts-empty">{sfText("여러 종목 비교 결과는 왼쪽 차트 영역에 표시됩니다.")}</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

const DEFAULT_PORTFOLIO = (idx) => ({
  id: `p${idx}-${Date.now()}`,
  name: defaultPortfolioName(idx),
  holdings: [
    {
      id: `h${idx}-0-${Date.now()}`,
      ticker: "",
      tickerInput: "",
      weight: 100,
    },
  ],
});

const clampNumber = (value, { min = -Infinity, max = Infinity } = {}) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return null;
  return Math.min(max, Math.max(min, numberValue));
};

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const formatAmount = (value, digits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return number.toLocaleString("ko-KR", { maximumFractionDigits: digits });
};

const isNearlyEqual = (a, b, eps = 0.001) => Math.abs(a - b) <= eps;

const computePortfolioMetrics = (dates, values, periodsPerYear) => {
  if (!dates.length || values.length !== dates.length) return [];
  if (values.length < 2) {
    return [
      { metric: "Total Return", value: 0 },
      { metric: "CAGR", value: null },
      { metric: "Volatility", value: null },
      { metric: "Max Drawdown", value: null },
      { metric: "Sharpe Ratio", value: null },
      { metric: "Sortino Ratio", value: null },
    ];
  }

  const first = values[0];
  const last = values[values.length - 1];
  const totalReturn = first > 0 ? (last / first - 1) : null;

  const firstDate = new Date(dates[0]);
  const lastDate = new Date(dates[dates.length - 1]);
  const years = Math.max(0, (lastDate - firstDate) / (1000 * 60 * 60 * 24)) / 365.25;
  const cagr = years > 0 && first > 0 ? Math.pow(last / first, 1 / years) - 1 : null;

  const returns = [];
  for (let i = 1; i < values.length; i += 1) {
    const prev = values[i - 1];
    const cur = values[i];
    if (!(prev > 0) || !(cur > 0)) continue;
    returns.push(cur / prev - 1);
  }

  const mean = returns.length ? returns.reduce((a, b) => a + b, 0) / returns.length : null;
  const variance =
    returns.length > 1
      ? returns.reduce((sum, r) => sum + (r - mean) ** 2, 0) / (returns.length - 1)
      : null;
  const std = variance !== null ? Math.sqrt(variance) : null;
  const volatility = std !== null ? std * Math.sqrt(periodsPerYear) : null;
  const sharpe =
    std && std > 0 && mean !== null ? (mean * periodsPerYear) / (std * Math.sqrt(periodsPerYear)) : null;
  const downside = returns.filter((r) => r < 0);
  const downsideMean = downside.length ? downside.reduce((a, b) => a + b, 0) / downside.length : null;
  const downsideVariance =
    downside.length > 1
      ? downside.reduce((sum, r) => sum + (r - downsideMean) ** 2, 0) / (downside.length - 1)
      : null;
  const downsideStd = downsideVariance !== null ? Math.sqrt(downsideVariance) : null;
  const sortino =
    downsideStd && downsideStd > 0 && mean !== null
      ? (mean * periodsPerYear) / (downsideStd * Math.sqrt(periodsPerYear))
      : null;

  let peak = values[0];
  let maxDrawdown = 0;
  for (let i = 1; i < values.length; i += 1) {
    const v = values[i];
    if (v > peak) peak = v;
    if (peak > 0) {
      const dd = v / peak - 1;
      if (dd < maxDrawdown) maxDrawdown = dd;
    }
  }

  return [
    { metric: "Total Return", value: totalReturn },
    { metric: "CAGR", value: cagr },
    { metric: "Volatility", value: volatility },
    { metric: "Max Drawdown", value: maxDrawdown },
    { metric: "Sharpe Ratio", value: sharpe },
    { metric: "Sortino Ratio", value: sortino },
  ];
};

const inferPeriodsPerYearFromDates = (dates) => {
  if (!Array.isArray(dates) || dates.length < 2) return 252;
  const first = new Date(dates[0]);
  const last = new Date(dates[dates.length - 1]);
  const ms = last - first;
  if (!(ms > 0)) return 252;
  const years = ms / (1000 * 60 * 60 * 24) / 365.25;
  if (!(years > 0)) return 252;
  const periods = dates.length / years;
  return Math.max(1, periods);
};

const PortfolioMetricPanel = ({ portfolio, metrics }) => (
  <div className="portfolio-metric-panel">
    <div className="portfolio-metric-panel-title">
      <h3>{portfolio.name}</h3>
      <p>포트폴리오를 하나의 합성 자산으로 보고 계산한 가격 기반 핵심 지표입니다.</p>
    </div>
    <MetricsGrid metrics={metrics || []} mode="performanceOnly" />
  </div>
);

const PortfolioMetricsCompareTable = ({ portfolios, metricsById }) => {
  const sections = METRIC_SECTIONS
    .filter((section) => section.key === "performance")
    .map((section) => ({
      ...section,
      groups: section.groupKeys
        .map((key) => METRIC_GROUPS.find((group) => group.key === key))
        .filter(Boolean),
    }));

  const hasMetric = (portfolio, key) => metricValueMap(metricsById?.[portfolio.id] || []).has(key);
  const sectionsWithRows = sections.map((section) => ({
    ...section,
    groups: section.groups.map((group) => ({
      ...group,
      metrics: group.metrics.filter((definition) => portfolios.some((portfolio) => hasMetric(portfolio, definition.key))),
    })).filter((group) => group.metrics.length),
  })).filter((section) => section.groups.length);

  if (!sectionsWithRows.length) return null;

  return (
    <div className="portfolio-metric-panel portfolio-metric-compare-panel">
      <div className="portfolio-metric-panel-title">
        <h3>포트폴리오 비교 핵심 지표</h3>
        <p>각 포트폴리오를 하나의 합성 자산으로 보고 계산한 가격 기반 지표를 한 표에서 비교합니다.</p>
      </div>
      <div className="metrics-panel portfolio-compare-metrics-panel" aria-label="포트폴리오 비교 핵심 지표">
        <div className="metrics-panel-header">
          <div>
            <p className="eyebrow">Key Metrics</p>
            <h3>{sfText("핵심 지표 요약")}</h3>
          </div>
          <p>선택한 기간의 포트별 성과와 위험 지표를 같은 행에서 나란히 비교합니다.</p>
        </div>
        <div className="metric-section-stack">
          {sectionsWithRows.map((section) => (
            <div key={section.key} className={`metric-section metric-section-${section.key}`}>
              <div className="metric-section-heading">
                <h4>{sfText(section.title)}</h4>
                <p>{sfText(section.subtitle)}</p>
              </div>
              <div className="metric-group-stack portfolio-compare-metric-group-stack">
                {section.groups.map((group) => (
                  <div key={group.key} className="metric-group" role="group" aria-labelledby={`portfolio-compare-metric-group-${group.key}`}>
                    <div className="metric-group-heading">
                      <h4 id={`portfolio-compare-metric-group-${group.key}`}>{group.title}</h4>
                      <p>{group.subtitle}</p>
                    </div>
                    <div className="metrics-table-wrap portfolio-compare-metrics-table-wrap">
                      <table className="metrics-table portfolio-compare-metrics-table">
                        <thead>
                          <tr>
                            <th scope="col">지표</th>
                            {portfolios.map((portfolio) => (
                              <th key={portfolio.id} scope="col">{portfolio.name}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {group.metrics.map((definition) => (
                            <tr key={definition.key}>
                              <th scope="row">
                                <span className="metric-table-label">{sfText(definition.label)}</span>
                                <span className="metric-table-key">{definition.key}</span>
                              </th>
                              {portfolios.map((portfolio) => {
                                const metric = metricValueMap(metricsById?.[portfolio.id] || []).get(definition.key);
                                const hasValue = metric && metric.value !== null && metric.value !== undefined && !Number.isNaN(Number(metric.value));
                                return (
                                  <td key={`${portfolio.id}-${definition.key}`} className={`metric-table-value ${!hasValue ? "is-muted" : ""}`}>
                                    {hasValue ? formatMetric(definition.key, metric.value) : "N/A"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AGGREGATE_PORTFOLIO_ID = "__AGGREGATE_PORTFOLIO__";

const PortfolioMetricsSummary = ({ portfolios, metricsById }) => {
  const visiblePortfolios = portfolios.filter((p) => Array.isArray(metricsById?.[p.id]));
  if (!visiblePortfolios.length) return null;
  if (visiblePortfolios.length === 1) {
    const portfolio = visiblePortfolios[0];
    return (
      <div className="portfolio-metrics-summary">
        <PortfolioMetricPanel portfolio={portfolio} metrics={metricsById[portfolio.id]} />
      </div>
    );
  }
  return (
    <div className="portfolio-metrics-summary portfolio-metrics-summary-combined">
      <PortfolioMetricsCompareTable portfolios={visiblePortfolios} metricsById={metricsById} />
    </div>
  );
};

function PortfolioCompareApp() {
  const [tickers, setTickers] = useState([]);
  const [tickerNameMap, setTickerNameMap] = useState({});
  const [tickerAliasMap, setTickerAliasMap] = useState({});
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  const [portfolios, setPortfolios] = useState([DEFAULT_PORTFOLIO(0)]);
  const [portfolioInputMode, setPortfolioInputMode] = useState("weight");
  const [analysisMode, setAnalysisMode] = useState("compare");
  const [portfolioMixWeights, setPortfolioMixWeights] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [valueChartData, setValueChartData] = useState({ labels: [], datasets: [] });
  const [metricsById, setMetricsById] = useState({});
  const [resultPortfolios, setResultPortfolios] = useState([]);
  const [rangeLabel, setRangeLabel] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedSavedTemplateId, setSelectedSavedTemplateId] = useState("");
  const [selectedComparePortfolioId, setSelectedComparePortfolioId] = useState("");
  const [loadingSavedTemplates, setLoadingSavedTemplates] = useState(false);
  const [savedTemplateError, setSavedTemplateError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/ticker-metadata`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((json) => {
        const { names, aliases } = buildTickerMetadataMaps(json.items || []);
        setTickerNameMap(names);
        setTickerAliasMap(aliases);
      })
      .catch(() => {
        setTickerNameMap({});
        setTickerAliasMap({});
      });
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/tickers`)
      .then((res) => (res.ok ? res.json() : { tickers: [] }))
      .then((json) => setTickers(resolveTickerListFromApi(json.tickers || [], tickerNameMap)))
      .catch(() => setTickers([]));
  }, [tickerNameMap]);

  useEffect(() => {
    setTickers((prev) => {
      const next = resolveTickerListFromApi(prev, tickerNameMap);
      if (next.length === prev.length && next.every((value, idx) => value === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [tickerNameMap]);

  useEffect(() => {
    if (!tickers.length) return;
    const sampleTicker = tickers[0];
    fetch(`${API_BASE}/tickers/${encodeURIComponent(sampleTicker)}/tables`)
      .then((res) => (res.ok ? res.json() : { tables: [] }))
      .then((json) => {
        const nextTables = json.tables || [];
        setTables(nextTables);
        setSelectedTable((prev) => prev || nextTables[0] || "");
      })
      .catch(() => {
        setTables([]);
        setSelectedTable("");
      });
  }, [tickers]);

  const loadSavedTemplates = async () => {
    setLoadingSavedTemplates(true);
    setSavedTemplateError("");
    try {
      const res = await fetch(`${API_BASE}/my-portfolios`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        setSavedTemplates([]);
        setSelectedSavedTemplateId("");
        setSavedTemplateError(sfText("로그인 후 사용 가능합니다."));
        return;
      }
      const json = res.ok ? await res.json() : { items: [] };
      const items = Array.isArray(json.items) ? json.items : [];
      setSavedTemplates(items);
      setSelectedSavedTemplateId((prev) => {
        if (prev && items.some((item) => item.id === prev)) return prev;
        return items[0]?.id || "";
      });
    } catch (_) {
      setSavedTemplateError(sfText("저장 포트폴리오를 불러오지 못했습니다."));
    } finally {
      setLoadingSavedTemplates(false);
    }
  };

  useEffect(() => {
    loadSavedTemplates();
  }, []);

  const resolvePortfolioComparisonStartIso = (items, fallbackStartIso) => {
    const savedStarts = (items || [])
      .map((item) => String(item?.start_date || "").trim())
      .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value));
    if (!savedStarts.length) return fallbackStartIso;
    const latestSavedStart = savedStarts.sort().slice(-1)[0];
    if (!fallbackStartIso) return latestSavedStart;
    return latestSavedStart > fallbackStartIso ? latestSavedStart : fallbackStartIso;
  };

  const toComparePortfolio = (savedItem, indexHint, existingNames = []) => {
    const uniqueName = makeUniqueName(savedItem?.name || defaultPortfolioName(indexHint), existingNames);
    const rawHoldings = Array.isArray(savedItem?.holdings) ? savedItem.holdings : [];
    const holdings = rawHoldings
      .map((holding, idx) => {
        const ticker = String(holding?.ticker || "").trim();
        const isCash = normalizeTickerKey(ticker) === CASH_TICKER;
        return {
          id: `h-${savedItem?.id || "saved"}-${idx}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          ticker,
          tickerInput: isCash ? CASH_DISPLAY_NAME : (ticker ? tickerInputDisplayName(ticker, tickerNameMap) : ""),
          weight: Number(holding?.weight) || 0,
          shares: holding?.shares ?? "",
          amount: Number(holding?.amount) || 0,
          isCash,
        };
      })
      .filter((holding) => !!holding.ticker && holding.weight > 0);

    return {
      id: `saved-${savedItem?.id || indexHint}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      savedId: String(savedItem?.id || ""),
      name: uniqueName,
      start_date: savedItem?.start_date || "",
      end_date: savedItem?.end_date || "",
      input_mode: savedItem?.input_mode || "weight",
      total_amount: Number(savedItem?.total_amount) || 0,
      cash_amount: Number(savedItem?.cash_amount) || 0,
      holdings: holdings.length ? holdings : [createEmptyHolding(indexHint)],
    };
  };

  const applySavedMeta = (savedItem) => {
    if (!savedItem || typeof savedItem !== "object") return;
    const savedTable = String(savedItem.table || "").trim();
    const savedStart = String(savedItem.start_date || "").trim();
    const savedEnd = String(savedItem.end_date || "").trim();

    if (savedTable && tables.includes(savedTable)) {
      setSelectedTable(savedTable);
    }
    if (savedStart) {
      setStartDate(savedStart);
      setStartDateInput(isoDateToDisplay(savedStart));
    }
    if (savedEnd) {
      setEndDate(savedEnd);
      setEndDateInput(isoDateToDisplay(savedEnd));
    }
  };

  /* 저장 포트를 분석 캔버스에 새 카드로 추가 (통합 매니저에서 호출) */
  const loadSavedPortfolioIntoCanvas = (itemId, item) => {
    const source = item || savedTemplates.find((template) => template.id === itemId);
    if (!source) return;
    setPortfolios((prev) => {
      const names = prev.map((portfolio) => portfolio.name);
      const next = toComparePortfolio(source, prev.length, names);
      return [...prev, next];
    });
    applySavedMeta(source);
  };

  const savedManager = useSavedPortfolioManager({
    tickers,
    tickerNameMap,
    tickerAliasMap,
    onSaved: async () => {
      await loadSavedTemplates();
    },
    loadIntoCanvasById: loadSavedPortfolioIntoCanvas,
  });

  const isEmptyComparePortfolio = (portfolio) => {
    if (!portfolio) return false;
    const holdings = Array.isArray(portfolio.holdings) ? portfolio.holdings : [];
    return holdings.length > 0 && holdings.every((holding) => {
      const ticker = String(holding?.ticker || "").trim();
      const tickerInput = String(holding?.tickerInput || "").trim();
      return !ticker && !tickerInput;
    });
  };

  const appendSavedPortfolio = () => {
    const savedItem = savedTemplates.find((item) => item.id === selectedSavedTemplateId);
    if (!savedItem) return;

    const targetIdx = portfolios.findIndex((p) => p.id === selectedComparePortfolioId && isEmptyComparePortfolio(p));
    if (targetIdx >= 0) {
      const nextNames = portfolios.filter((_, idx) => idx !== targetIdx).map((p) => p.name);
      const replacement = toComparePortfolio(savedItem, targetIdx, nextNames);
      const next = [...portfolios];
      next[targetIdx] = replacement;
      setPortfolios(next);
      setSelectedComparePortfolioId(replacement.id);
    } else {
      const nextNames = portfolios.map((p) => p.name);
      const appended = toComparePortfolio(savedItem, portfolios.length, nextNames);
      setPortfolios([...portfolios, appended]);
      setSelectedComparePortfolioId(appended.id);
    }
    applySavedMeta(savedItem);
    setError("");
  };

  const replaceWithAllSaved = () => {
    if (!savedTemplates.length) return;
    const next = [];
    savedTemplates.forEach((item, idx) => {
      const existingNames = next.map((p) => p.name);
      next.push(toComparePortfolio(item, idx, existingNames));
    });
    setPortfolios(next);
    setSelectedComparePortfolioId(next[0]?.id || "");
    applySavedMeta(savedTemplates[0]);
    setError("");
  };

  const portfolioWeights = useMemo(() => {
    const weights = {};
    portfolios.forEach((p) => {
      const sum = (p.holdings || []).reduce((acc, h) => acc + (Number(h.weight) || 0), 0);
      weights[p.id] = sum;
    });
    return weights;
  }, [portfolios]);

  useEffect(() => {
    setPortfolioMixWeights((prev) => {
      const equalWeight = portfolios.length ? roundTo(100 / portfolios.length, 2) : 0;
      const next = {};
      portfolios.forEach((portfolio) => {
        const current = Number(prev?.[portfolio.id]);
        next[portfolio.id] = Number.isFinite(current) ? current : equalWeight;
      });
      return next;
    });
  }, [portfolios]);

  const effectiveAnalysisMode = portfolios.length >= 2 ? analysisMode : "single";
  const isAggregateMode = effectiveAnalysisMode === "aggregate";

  const aggregateWeightSum = useMemo(() => {
    if (!isAggregateMode) return 0;
    return portfolios.reduce((acc, portfolio) => acc + (Number(portfolioMixWeights[portfolio.id]) || 0), 0);
  }, [isAggregateMode, portfolios, portfolioMixWeights]);

  const normalizePortfolioMixWeights = () => {
    const sum = portfolios.reduce((acc, portfolio) => acc + (Number(portfolioMixWeights[portfolio.id]) || 0), 0);
    if (!(sum > 0)) return;
    const next = {};
    portfolios.forEach((portfolio, idx) => {
      next[portfolio.id] = roundTo(((Number(portfolioMixWeights[portfolio.id]) || 0) / sum) * 100, 2);
    });
    const adjustedSum = portfolios.reduce((acc, portfolio) => acc + (Number(next[portfolio.id]) || 0), 0);
    const delta = roundTo(100 - adjustedSum, 2);
    if (portfolios.length && delta !== 0) {
      const targetId = portfolios[0].id;
      next[targetId] = roundTo((Number(next[targetId]) || 0) + delta, 2);
    }
    setPortfolioMixWeights(next);
  };

  const allValid = useMemo(() => {
    if (!selectedTable) return false;
    if (portfolios.length < 1) return false;
    if (isAggregateMode) {
      const hasPositiveMix = portfolios.some((p) => (Number(portfolioMixWeights[p.id]) || 0) > 0);
      if (!hasPositiveMix || !isNearlyEqual(aggregateWeightSum, 100)) return false;
    }
    return portfolios.every((p) => {
      const sum = portfolioWeights[p.id] ?? 0;
      // 카드별 input_mode 우선(통합 저장포트는 주식수/비율이 섞일 수 있음) — 전역 모드는 기본값 역할만
      const mode = p.input_mode === "shares" || p.input_mode === "weight" ? p.input_mode : portfolioInputMode;
      if (mode === "weight" && !isNearlyEqual(sum, 100)) return false;
      if (mode === "shares" && !(sum > 0)) return false;
      return (p.holdings || []).every((h) => !!h.ticker && (Number(h.weight) || 0) > 0);
    });
  }, [portfolios, portfolioWeights, portfolioInputMode, selectedTable, isAggregateMode, portfolioMixWeights, aggregateWeightSum]);

  const updatePortfolio = (portfolioId, updater) => {
    setPortfolios((prev) =>
      prev.map((p) => {
        if (p.id !== portfolioId) return p;
        return updater(p);
      }),
    );
  };

  const addPortfolio = () => {
    const nextPortfolio = DEFAULT_PORTFOLIO(portfolios.length);
    setPortfolios([...portfolios, nextPortfolio]);
    setSelectedComparePortfolioId(nextPortfolio.id);
  };

  const removePortfolio = (portfolioId) => {
    const next = portfolios.filter((p) => p.id !== portfolioId);
    setPortfolios(next);
    if (selectedComparePortfolioId === portfolioId) {
      setSelectedComparePortfolioId(next[0]?.id || "");
    }
  };

  const addHolding = (portfolioId) => {
    updatePortfolio(portfolioId, (p) => {
      const sum = (p.holdings || []).reduce((acc, h) => acc + (Number(h.weight) || 0), 0);
      const remaining = Math.max(0, 100 - sum);
      const nextHolding = {
        id: `h${p.id}-${Date.now()}`,
        ticker: "",
        tickerInput: "",
        weight: portfolioInputMode === "shares" ? 1 : (remaining > 0 ? roundTo(remaining, 2) : 0),
      };
      return { ...p, holdings: [...(p.holdings || []), nextHolding] };
    });
  };

  const normalizeWeightsTo100 = (portfolioId) => {
    updatePortfolio(portfolioId, (p) => {
      const sum = (p.holdings || []).reduce((acc, h) => acc + (Number(h.weight) || 0), 0);
      if (!(sum > 0)) return p;
      const scaled = (p.holdings || []).map((h) => ({
        ...h,
        weight: roundTo(((Number(h.weight) || 0) / sum) * 100, 2),
      }));
      const adjusted = [...scaled];
      const after = adjusted.reduce((acc, h) => acc + (Number(h.weight) || 0), 0);
      const delta = roundTo(100 - after, 2);
      if (adjusted.length && delta !== 0) {
        adjusted[0] = { ...adjusted[0], weight: roundTo((Number(adjusted[0].weight) || 0) + delta, 2) };
      }
      return { ...p, holdings: adjusted };
    });
  };

  const removeHolding = (portfolioId, holdingId) => {
    updatePortfolio(portfolioId, (p) => {
      const nextHoldings = (p.holdings || []).filter((h) => h.id !== holdingId);
      return { ...p, holdings: nextHoldings.length ? nextHoldings : p.holdings };
    });
  };

  const resetAll = () => {
    setError("");
    setChartData({ labels: [], datasets: [] });
    setValueChartData({ labels: [], datasets: [] });
    setMetricsById({});
    setResultPortfolios([]);
    setRangeLabel("");
    setStartDate("");
    setEndDate("");
    setStartDateInput("");
    setEndDateInput("");
    setPortfolios([DEFAULT_PORTFOLIO(0)]);
    setSelectedComparePortfolioId("");
  };

  const fetchHoldingPrices = async (ticker, table, startIso, endIso) => {
    const params = new URLSearchParams({ table });
    if (startIso) params.set("start_date", startIso);
    if (endIso) params.set("end_date", endIso);
    const res = await fetch(`${API_BASE}/prices/${encodeURIComponent(ticker)}?${params.toString()}`);
    if (!res.ok) {
      const message = await res.text().catch(() => "");
      throw new Error(message || `가격 데이터를 불러오지 못했습니다: ${ticker}`);
    }
    const json = await res.json();
    return (json.data || [])
      .map((row) => ({
        date: String(row.date || ""),
        close: row.close ?? row.adj_close ?? null,
      }))
      .filter((row) => !!row.date && row.close !== null && row.close !== undefined && !Number.isNaN(Number(row.close)));
  };

  const buildPortfolioSeries = (portfolio, priceSeriesByHolding, inputMode = "weight") => {
    const holdings = portfolio.holdings || [];
    const cashHoldings = holdings.filter((h) => normalizeTickerKey(h.ticker) === CASH_TICKER || h.isCash);
    const stockHoldings = holdings.filter((h) => normalizeTickerKey(h.ticker) !== CASH_TICKER && !h.isCash);
    const seriesList = stockHoldings
      .map((h) => ({ holding: h, series: priceSeriesByHolding[h.id] || [] }))
      .filter((item) => item.series.length > 0);

    if (seriesList.length !== stockHoldings.length) {
      throw new Error(`${portfolio.name}: 일부 종목의 가격 데이터가 없습니다.`);
    }
    if (!seriesList.length) {
      throw new Error(`${portfolio.name}: 현금만 있는 포트폴리오는 분석할 가격 구간이 없습니다.`);
    }

    const dateSets = seriesList.map((item) => new Set(item.series.map((p) => String(p.date).slice(0, 10))));
    let commonDates = null;
    dateSets.forEach((set) => {
      if (!commonDates) {
        commonDates = new Set(set);
        return;
      }
      commonDates = new Set([...commonDates].filter((d) => set.has(d)));
    });
    const dates = [...(commonDates || [])].sort();
    if (dates.length < 2) {
      throw new Error(`${portfolio.name}: 공통 구간 데이터가 부족합니다.`);
    }

    const holdingMaps = {};
    seriesList.forEach(({ holding, series }) => {
      const map = {};
      series.forEach((p) => {
        const key = String(p.date).slice(0, 10);
        map[key] = Number(p.close);
      });
      holdingMaps[holding.id] = map;
    });

    const startKey = dates[0];
    const base = {};
    seriesList.forEach(({ holding }) => {
      base[holding.id] = holdingMaps[holding.id][startKey];
    });
    const cashWeight = cashHoldings.reduce((acc, holding) => acc + (Number(holding.weight) || 0) / 100, 0);

    const values = dates.map((dateKey) => {
      let total = 0;
      if (inputMode === "shares") {
        seriesList.forEach(({ holding }) => {
          const shares = Number(holding.weight) || 0;
          const close = holdingMaps[holding.id][dateKey];
          if (!(shares > 0) || !(close > 0)) return;
          total += shares * close;
        });
        return total;
      }
      total += cashWeight;
      seriesList.forEach(({ holding }) => {
        const w = (Number(holding.weight) || 0) / 100;
        const close = holdingMaps[holding.id][dateKey];
        const baseClose = base[holding.id];
        if (!(baseClose > 0) || !(close > 0) || !(w >= 0)) return;
        total += w * (close / baseClose);
      });
      return total;
    });

    return { dates, values };
  };

  const inferLatestPortfolioValueFromCash = (portfolio) => {
    const holdings = Array.isArray(portfolio?.holdings) ? portfolio.holdings : [];
    const cashRows = holdings.filter((holding) => normalizeTickerKey(holding?.ticker) === CASH_TICKER || holding?.isCash);
    const cashAmount = cashRows.reduce((acc, holding) => acc + (Number(holding?.amount) || 0), 0) || Number(portfolio?.cash_amount) || 0;
    const cashWeight = cashRows.reduce((acc, holding) => acc + (Number(holding?.weight) || 0), 0);
    if (cashAmount > 0 && cashWeight > 0) {
      return cashAmount / (cashWeight / 100);
    }
    const totalAmount = Number(portfolio?.total_amount) || 0;
    return totalAmount > 0 ? totalAmount : null;
  };

  const buildValueChartDatasets = (portfolioSeriesList, dates) => {
    const datasets = [];
    portfolioSeriesList.forEach(({ portfolio, series }, idx) => {
      const latestValue = inferLatestPortfolioValueFromCash(portfolio);
      if (!(latestValue > 0)) return;
      const map = {};
      series.dates.forEach((d, i) => {
        map[d] = series.values[i];
      });
      const latestIndexValue = map[dates[dates.length - 1]];
      if (!(latestIndexValue > 0)) return;
      datasets.push({
        label: portfolio.name,
        data: dates.map((d) => {
          const v = map[d];
          if (!(v > 0)) return null;
          return (v / latestIndexValue) * latestValue;
        }),
        borderColor: [`#2563eb`, `#7c3aed`, `#22d3ee`, `#fb7185`, `#0ea5e9`][idx % 5],
      });
    });
    return datasets;
  };

  const handleCompare = async () => {
    setError("");
    setLoading(true);
    try {
      const startIso = startDate || "";
      const endIso = endDate || "";
      const comparisonStartIso = resolvePortfolioComparisonStartIso(portfolios, startIso);
      const seriesByPortfolioId = {};

      for (const portfolio of portfolios) {
        const portfolioStartIso = portfolios.length === 1 ? (portfolio.start_date || startIso) : comparisonStartIso;
        const portfolioEndIso = portfolios.length === 1 ? (portfolio.end_date || endIso) : endIso;
        const priceSeriesByHolding = {};
        for (const holding of portfolio.holdings || []) {
          if (normalizeTickerKey(holding.ticker) === CASH_TICKER || holding.isCash) {
            priceSeriesByHolding[holding.id] = [];
            continue;
          }
          const series = await fetchHoldingPrices(holding.ticker, selectedTable, portfolioStartIso, portfolioEndIso);
          priceSeriesByHolding[holding.id] = series;
        }
        seriesByPortfolioId[portfolio.id] = buildPortfolioSeries(
          portfolio,
          priceSeriesByHolding,
          portfolio.input_mode === "shares" || portfolio.input_mode === "weight" ? portfolio.input_mode : portfolioInputMode,
        );
      }

      const portfolioSeriesList = portfolios.map((p) => ({
        portfolio: p,
        series: seriesByPortfolioId[p.id],
      }));

      let commonDates = null;
      portfolioSeriesList.forEach(({ series }) => {
        const set = new Set(series.dates);
        if (!commonDates) {
          commonDates = new Set(set);
          return;
        }
        commonDates = new Set([...commonDates].filter((d) => set.has(d)));
      });
      const dates = [...(commonDates || [])].sort();
      if (dates.length < 2) {
        throw new Error("포트폴리오 분석에 사용할 공통 구간 데이터가 부족합니다.");
      }

      const labels = dates;
      const periodsPerYear = inferPeriodsPerYearFromDates(dates);

      if (isAggregateMode) {
        const normalizedSeries = portfolioSeriesList.map(({ portfolio, series }) => {
          const map = {};
          series.dates.forEach((d, i) => {
            map[d] = series.values[i];
          });
          const baseValue = map[dates[0]];
          return { portfolio, map, baseValue };
        });
        const aggregateValues = dates.map((d) => {
          let total = 0;
          normalizedSeries.forEach(({ portfolio, map, baseValue }) => {
            const mix = (Number(portfolioMixWeights[portfolio.id]) || 0) / 100;
            const value = map[d];
            if (!(mix > 0) || !(baseValue > 0) || !(value > 0)) return;
            total += mix * (value / baseValue);
          });
          return total;
        });
        const aggregateBase = aggregateValues[0];
        const aggregateReturnData = aggregateValues.map((value) => {
          if (!(aggregateBase > 0) || !(value > 0)) return null;
          return (value / aggregateBase - 1) * 100;
        });
        const aggregatePortfolio = { id: AGGREGATE_PORTFOLIO_ID, name: "합산 포트" };
        setChartData({
          labels,
          datasets: [{
            label: "합산 포트",
            data: aggregateReturnData,
            borderColor: "#2563eb",
          }],
        });
        setValueChartData({ labels: [], datasets: [] });
        setMetricsById({ [AGGREGATE_PORTFOLIO_ID]: computePortfolioMetrics(dates, aggregateValues, periodsPerYear) });
        setResultPortfolios([aggregatePortfolio]);
        setRangeLabel(`${dates[0]} ~ ${dates[dates.length - 1]} · 합산 모드`);
        return;
      }

      const datasets = portfolioSeriesList.map(({ portfolio, series }, idx) => {
        const map = {};
        series.dates.forEach((d, i) => {
          map[d] = series.values[i];
        });
        const baseValue = map[dates[0]];
        const data = dates.map((d) => {
          const v = map[d];
          if (!(baseValue > 0) || !(v > 0)) return null;
          return (v / baseValue - 1) * 100;
        });
        return {
          label: portfolio.name,
          data,
          borderColor: [`#2563eb`, `#7c3aed`, `#22d3ee`, `#fb7185`, `#0ea5e9`][idx % 5],
        };
      });

      const nextMetrics = {};
      portfolioSeriesList.forEach(({ portfolio, series }) => {
        const map = {};
        series.dates.forEach((d, i) => {
          map[d] = series.values[i];
        });
        const alignedValues = dates.map((d) => map[d]);
        nextMetrics[portfolio.id] = computePortfolioMetrics(dates, alignedValues, periodsPerYear);
      });

      setChartData({ labels, datasets });
      const valueDatasets = buildValueChartDatasets(portfolioSeriesList, dates);
      setValueChartData({ labels, datasets: valueDatasets });
      setMetricsById(nextMetrics);
      setResultPortfolios(portfolios);
      setRangeLabel(`${dates[0]} ~ ${dates[dates.length - 1]}`);
    } catch (exc) {
      setError(exc?.message || "포트폴리오 분석 중 오류가 발생했습니다.");
      setResultPortfolios([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="page-hero compact-hero">
        <div className="hero-copy">
          <span className="eyebrow">Portfolio Analysis</span>
          <h1>포트폴리오 분석</h1>
          <p className="subtitle">
            여러 종목을 주식수 또는 비율로 묶어 하나의 포트폴리오 흐름을 만들고, 단일 포트 분석과 포트 간 비교를 함께 제공합니다.
          </p>
        </div>
      </header>
      <section className="workspace-card">
        <div className="control-row portfolio-date-row">
          <label>
            시작일
            <input
              type="text"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              onBlur={() => {
                const completed = completeDateInput(startDateInput, "start");
                if (!startDateInput.trim()) {
                  setStartDate("");
                  setStartDateInput("");
                  return;
                }
                if (!completed) {
                  setStartDate("");
                  return;
                }
                setStartDate(completed.iso);
                setStartDateInput(completed.display);
              }}
              placeholder="YYYY. MM. DD"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
          <label>
            종료일
            <input
              type="text"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              onBlur={() => {
                const completed = completeDateInput(endDateInput, "end");
                if (!endDateInput.trim()) {
                  setEndDate("");
                  setEndDateInput("");
                  return;
                }
                if (!completed) {
                  setEndDate("");
                  return;
                }
                setEndDate(completed.iso);
                setEndDateInput(completed.display);
              }}
              placeholder="YYYY. MM. DD"
              inputMode="numeric"
              autoComplete="off"
            />
          </label>
          <label>
            인터벌
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              disabled={!tables.length}
            >
              {tables.map((table) => (
                <option key={table} value={table}>
                  {intervalDisplayName(table)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="portfolio-mode-grid">
          <div className="portfolio-input-mode-row">
            <label>
              입력 방식
              <select value={portfolioInputMode} onChange={(e) => setPortfolioInputMode(e.target.value)}>
                <option value="weight">비율(%)</option>
                <option value="shares">주식수</option>
              </select>
            </label>
            <p>{portfolioInputMode === "weight" ? "각 포트의 비율 합계가 100%가 되도록 입력합니다." : "각 종목의 보유 주식수를 입력하면 종목별 가격×주식수 합계로 포트폴리오 값을 계산합니다."}</p>
          </div>

          <div className="portfolio-analysis-mode-row">
            <label>
              분석 모드
              <select
                value={effectiveAnalysisMode === "single" ? "compare" : analysisMode}
                onChange={(e) => setAnalysisMode(e.target.value)}
                disabled={portfolios.length < 2}
              >
                <option value="compare">비교 · 불러온 각 포트 비교</option>
                <option value="aggregate">합산 · 비율로 합친 포트 분석</option>
              </select>
            </label>
            <p>
              {portfolios.length < 2
                ? "포트가 1개일 때는 모드 선택을 무시하고 단일 포트 분석으로 계산합니다."
                : (isAggregateMode
                  ? "아래 합산 비율대로 각 포트를 하나의 합산 포트로 묶어 성과와 위험 지표를 계산합니다."
                  : "불러온 각 포트를 각각 하나의 합성 자산으로 보고 성과를 나란히 비교합니다.")}
            </p>
          </div>
        </div>

        {isAggregateMode && (
          <div className="portfolio-mix-panel">
            <div className="portfolio-mix-header">
              <strong>합산 비율</strong>
              <span className={isNearlyEqual(aggregateWeightSum, 100) ? "ok" : "bad"}>합계 {roundTo(aggregateWeightSum, 2)}%</span>
              {!isNearlyEqual(aggregateWeightSum, 100) && (
                <button type="button" className="ghost-button" onClick={normalizePortfolioMixWeights}>
                  100%로 정규화
                </button>
              )}
            </div>
            <div className="portfolio-mix-grid">
              {portfolios.map((portfolio) => (
                <label key={portfolio.id}>
                  <span>{portfolio.name}</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={portfolioMixWeights[portfolio.id] ?? 0}
                    onChange={(e) => {
                      const next = clampNumber(e.target.value, { min: 0, max: 100 });
                      setPortfolioMixWeights((prev) => ({ ...prev, [portfolio.id]: next ?? 0 }));
                    }}
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        <SavedPortfolioManager manager={{ ...savedManager, tickers, tickerNameMap, tickerAliasMap }} />

        <div className="saved-portfolio-import">
          <button type="button" className="ghost-button" onClick={loadSavedTemplates} disabled={loadingSavedTemplates}>
            {loadingSavedTemplates ? "불러오는 중..." : "저장 포트 새로고침"}
          </button>
          <label className="saved-portfolio-picker">
            저장 포트
            <select
              value={selectedSavedTemplateId}
              onChange={(e) => setSelectedSavedTemplateId(e.target.value)}
              disabled={!savedTemplates.length}
            >
              {(savedTemplates.length ? savedTemplates : [{ id: "", name: sfText("저장 포트 없음") }]).map((item) => (
                <option key={item.id || "empty"} value={item.id || ""}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="ghost-button"
            onClick={appendSavedPortfolio}
            disabled={!selectedSavedTemplateId}
          >
            선택 포트 불러오기
          </button>
          <button type="button" className="ghost-button" onClick={replaceWithAllSaved} disabled={!savedTemplates.length}>
            전체 불러오기
          </button>
        </div>
        {savedTemplateError && <p className="saved-portfolio-error">{savedTemplateError}</p>}

        <div className="portfolio-grid">
          {portfolios.map((portfolio) => {
            const sum = portfolioWeights[portfolio.id] ?? 0;
            const cardMode = portfolio.input_mode === "shares" || portfolio.input_mode === "weight" ? portfolio.input_mode : portfolioInputMode;
            const sumOk = cardMode === "shares" ? sum > 0 : isNearlyEqual(sum, 100);
            return (
              <div
                key={portfolio.id}
                className={`portfolio-card${portfolio.id === selectedComparePortfolioId ? " selected" : ""}`}
                onClick={() => setSelectedComparePortfolioId(portfolio.id)}
                onFocusCapture={() => setSelectedComparePortfolioId(portfolio.id)}
              >
                <div className="portfolio-card-header">
                  <input
                    className="portfolio-name"
                    value={portfolio.name}
                    onChange={(e) =>
                      updatePortfolio(portfolio.id, (p) => ({ ...p, name: e.target.value || p.name }))
                    }
                    aria-label="Portfolio name"
                  />
                  <span className={`portfolio-sum ${sumOk ? "ok" : "bad"}`}>{(portfolio.input_mode || portfolioInputMode) === "weight" ? `${sfText("합계")} ${roundTo(sum, 2)}%` : `총 ${roundTo(sum, 4)}주`}</span>
                  {portfolioInputMode === "weight" && !sumOk && (
                    <button
                      type="button"
                      className="ghost-button portfolio-normalize"
                      onClick={() => normalizeWeightsTo100(portfolio.id)}
                    >
                      {sfText("100%로 정규화")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="ghost-button portfolio-edit-saved"
                    data-saved-id={portfolio.savedId || ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      const savedId = portfolio.savedId || portfolio.id?.replace(/^saved-\d+-\d+-[0-9a-z]+$/i, "") || "";
                      if (!savedId) return;
                      window.dispatchEvent(new CustomEvent("sf:edit-saved-portfolio", { detail: { id: savedId } }));
                    }}
                    style={portfolio.savedId ? undefined : { display: "none" }}
                    title={isEnglishLang() ? "Edit this saved portfolio" : "이 저장 포트 편집"}
                  >
                    {isEnglishLang() ? "Edit saved" : "포트 편집"}
                  </button>
                  <button
                    type="button"
                    className="danger-button portfolio-delete"
                    onClick={() => {
                      if (portfolios.length <= 1) return;
                      if (!window.confirm(isEnglishLang() ? `'${portfolio.name}' ${sfText("포트를 삭제할까요?")}` : `'${portfolio.name}' 포트를 삭제할까요?`)) return;
                      removePortfolio(portfolio.id);
                    }}
                    disabled={portfolios.length <= 1}
                    aria-label="Delete portfolio"
                  >
                    {sfText("포트 삭제")}
                  </button>
                </div>

                <div className="portfolio-holdings">
                  {(portfolio.holdings || []).map((holding) => (
                    <div key={holding.id} className="holding-row">
                      <TickerAutocompleteInput
                        label="종목 선택"
                        inputValue={holding.tickerInput}
                        onInputChange={(nextInput) => {
                          updatePortfolio(portfolio.id, (p) => ({
                            ...p,
                            holdings: (p.holdings || []).map((h) => {
                              if (h.id !== holding.id) return h;
                              const match = findTickerByInput(nextInput, tickers, tickerNameMap, tickerAliasMap);
                              return {
                                ...h,
                                tickerInput: nextInput,
                                ticker: match || "",
                              };
                            }),
                          }));
                        }}
                        tickers={tickers}
                        tickerNameMap={tickerNameMap}
                        tickerAliasMap={tickerAliasMap}
                        tickerTagMap={{}}
                        selectedTags={[]}
                        tagMode="AND"
                        onOpenTagSearch={() => {}}
                        placeholder="티커 또는 종목명을 입력하세요"
                        className="holding-ticker"
                        showFooter={false}
                      />
                      <label className="holding-weight">
                        {portfolioInputMode === "weight" ? sfText("비율(%)") : "주식수"}
                        <input
                          type="number"
                          min="0"
                          max={portfolioInputMode === "weight" ? "100" : undefined}
                          step={portfolioInputMode === "weight" ? "0.1" : "any"}
                          value={holding.weight ?? ""}
                          onChange={(e) => {
                            const next = clampNumber(e.target.value, { min: 0, max: portfolioInputMode === "weight" ? 100 : Infinity });
                            updatePortfolio(portfolio.id, (p) => ({
                              ...p,
                              holdings: (p.holdings || []).map((h) =>
                                h.id === holding.id ? { ...h, weight: next ?? 0 } : h,
                              ),
                            }));
                          }}
                          placeholder={portfolioInputMode === "weight" ? "0" : "주식수"}
                        />
                      </label>
                      <button
                        type="button"
                        className="ghost-button holding-remove"
                        onClick={() => removeHolding(portfolio.id, holding.id)}
                        disabled={(portfolio.holdings || []).length <= 1}
                        aria-label="Remove holding"
                      >
                        삭제
                      </button>
                    </div>
                  ))}
                </div>

                <div className="portfolio-actions">
                  <button type="button" className="ghost-button" onClick={() => addHolding(portfolio.id)}>
                    종목 추가
                  </button>
                  <button type="button" className="ghost-button" onClick={addPortfolio}>
                    포트 추가
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="actions portfolio-compare-actions">
          <button className="primary-button" onClick={handleCompare} disabled={loading || !allValid}>
            {loading ? "계산 중..." : "포트폴리오 분석"}
          </button>
          <button
            type="button"
            className="ghost-button"
            onClick={() => {
              if (!window.confirm(sfText("입력한 포트/종목/비중/기간 선택을 모두 초기화할까요?"))) return;
              resetAll();
            }}
          >
            전체 초기화
          </button>
          {!allValid && <span className="hint">{portfolioInputMode === "weight" ? "각 포트의 비중 합계가 100%가 되도록 맞춰주세요." : "각 포트에 종목과 0보다 큰 주식수를 입력해주세요."}</span>}
        </div>

        {error && <div className="error-box">{error}</div>}

        {chartData.labels.length > 0 && (
          <>
            {rangeLabel && <p className="range-label">{rangeLabel}</p>}
            <LineChart labels={chartData.labels} datasets={chartData.datasets} title="토탈 리턴 (%)" yLabel="%" />
            {valueChartData.datasets.length > 0 && (
              <>
                <LineChart labels={valueChartData.labels} datasets={valueChartData.datasets} title="평가액 변동" yLabel="평가액" />
                <p className="hint portfolio-value-chart-note">
                  내 포트폴리오에 저장된 현금 금액을 최근 평가액의 현금 부분으로 보고, 현금 비중으로 전체 최근 평가액을 역산한 뒤 과거 포트폴리오 지수에 맞춰 되돌려 계산했습니다.
                </p>
              </>
            )}
            <PortfolioMetricsSummary portfolios={resultPortfolios.length ? resultPortfolios : portfolios} metricsById={metricsById} />
          </>
        )}
      </section>
    </>
  );
}

const createEmptyHolding = (key) => ({
  id: `mh-${key}-${Date.now()}`,
  ticker: "",
  tickerInput: "",
  weight: 0,
  shares: "",
  amount: 0,
  isCash: false,
});

/* ── 포트폴리오 분석 ↔ 내 포트폴리오 통합 (2026-09-25) ─────────────── */

const SAVED_PORTFOLIO_FORM_DEFAULTS = {
  name: "",
  memo: "",
  start_date: "",
  input_mode: "weight",
  total_amount: "",
  holdings: [],
};

function buildSavedPortfolioPayload(manager) {
  const source = manager.draft;
  const trim = (value) => String(value || "").trim();
  const name = trim(source.name) || (isEnglishLang() ? `Portfolio ${Date.now() % 1000}` : `포트 ${Date.now() % 1000}`);
  const inputMode = source.input_mode === "shares" ? "shares" : "weight";
  const totalAmount = Number(String(source.total_amount).replace(/,/g, "")) || 0;
  const holdings = (source.holdings || [])
    .map((holding) => {
      const ticker = String(holding.ticker || "").trim();
      if (!ticker || normalizeTickerKey(ticker) === CASH_TICKER) return null;
      const weight = Number(holding.weight) || 0;
      const shares = holding.shares === "" || holding.shares == null ? null : Number(holding.shares) || 0;
      // shares 모드: weight 필드에 주식수가 담기므로 shares 필드에도 동일 값을 기록(입력 그대로 보존)
      const sharesValue = inputMode === "shares" ? weight : (holding.shares === "" || holding.shares == null ? null : Number(holding.shares) || 0);
      return { ticker, weight, shares: sharesValue, amount: Number(holding.amount) || 0 };
    })
    .filter(Boolean);
  if (!holdings.length) {
    throw new Error(isEnglishLang() ? "Add at least one ticker." : "티커를 1개 이상 입력하세요.");
  }
  if (inputMode === "weight" && !isNearlyEqual(holdings.reduce((acc, h) => acc + h.weight, 0), 100, 0.05)) {
    throw new Error(isEnglishLang() ? "Ticker weights must add up to 100%." : "종목 비중 합계를 100%로 맞춰주세요.");
  }
  const trimmedStart = trim(source.start_date);
  const startIso = /^\d{4}-\d{2}-\d{2}$/.test(trimmedStart) ? trimmedStart : (completeDateInput(trimmedStart, "start")?.iso || null);
  return {
    name,
    memo: String(source.memo || ""),
    table: null,
    start_date: startIso,
    end_date: null,
    input_mode: inputMode,
    total_amount: inputMode === "weight" && totalAmount > 0 ? roundTo(totalAmount, 4) : null,
    cash_amount: null,
    holdings,
  };
}

function SavedPortfolioManager({ manager }) {
  const { state, actions } = manager;
  const {
    open, editingId, saving, error, message,
    draft, savedItems, loading,
  } = state;
  const en = isEnglishLang();

  const sum = (draft.holdings || []).reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
  const sumOk = draft.input_mode === "shares" ? sum > 0 : isNearlyEqual(sum, 100, 0.05);
  const weightDelta = roundTo(100 - sum, 2);

  return (
    <div className={`saved-manager-card${open ? " open" : ""}`}>
      <button
        type="button"
        className="saved-manager-toggle"
        aria-expanded={open ? "true" : "false"}
        aria-controls="savedManagerPanel"
        onClick={() => actions.toggleOpen()}
      >
        <span className="saved-manager-toggle-chevron" aria-hidden="true">{open ? "▾" : "▸"}</span>
        <span className="saved-manager-toggle-label">
          {en ? (editingId ? "Edit saved portfolio" : "Saved portfolios") : (editingId ? "저장 포트 편집" : "내 포트폴리오")}
        </span>
        {!open && !editingId && savedItems.length > 0 ? (
          <span className="saved-manager-count">{savedItems.length}{en ? " saved" : "개 저장됨"}</span>
        ) : null}
        {editingId ? (
          <span className="saved-manager-badge">
            {en ? "editing" : "편집중"}: {(savedItems.find((item) => item.id === editingId) || {}).name || ""}
          </span>
        ) : null}
      </button>

      {open && (
        <div className="saved-manager-panel" id="savedManagerPanel">
          <div className="saved-manager-body">
            <div className="saved-manager-side">
              <div className="saved-manager-side-head">
                <strong>{en ? "Saved portfolios" : "저장 목록"}</strong>
                <button type="button" className="ghost-button" onClick={actions.newDraft} disabled={saving}>
                  {en ? "New" : "새 포트"}
                </button>
              </div>
              {loading ? (
                <p className="saved-manager-empty">{en ? "Loading..." : "불러오는 중..."}</p>
              ) : savedItems.length ? (
                <div className="saved-manager-items">
                  {savedItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`saved-manager-item${item.id === editingId ? " active" : ""}`}
                      onClick={() => actions.editItem(item.id)}
                      disabled={saving}
                      title={en ? "Load into the form below" : "아래 폼으로 불러오기"}
                    >
                      <strong>{item.name}</strong>
                      <span>
                        {(item.holdings || []).length}
                        {en ? " tickers" : "종목"}
                        {item.updated_at ? ` · ${String(item.updated_at).slice(0, 10)}` : ""}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="saved-manager-empty">{en ? "No saved portfolios yet." : "저장된 포트폴리오가 없습니다."}</p>
              )}
            </div>

            <div className="saved-manager-form">
              <div className="saved-manager-row">
                <label>
                  {en ? "Portfolio name" : "포트폴리오 이름"}
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => actions.setDraftField("name", e.target.value)}
                    placeholder={en ? "e.g. Dividend portfolio" : "예: 배당 포트"}
                  />
                </label>
                <label>
                  {en ? "Memo" : "메모"}
                  <input
                    type="text"
                    value={draft.memo}
                    onChange={(e) => actions.setDraftField("memo", e.target.value)}
                    placeholder={en ? "Strategy notes" : "전략 메모"}
                  />
                </label>
              </div>

              <div className="saved-manager-row">
                <label>
                  {en ? "Portfolio start date" : "포트 시작일"}
                  <input
                    type="text"
                    value={isoDateToDisplay(draft.start_date)}
                    onChange={(e) => actions.setDraftField("start_date", e.target.value)}
                    onBlur={() => {
                      const raw = String(draft.start_date || "").trim();
                      if (!raw) {
                        actions.setDraftField("start_date", "");
                        return;
                      }
                      const completed = completeDateInput(raw, "start");
                      if (completed) actions.setDraftField("start_date", completed.iso);
                    }}
                    placeholder="YYYY. MM. DD"
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </label>
                <label>
                  {en ? "Input mode" : "입력 방식"}
                  <select
                    value={draft.input_mode}
                    onChange={(e) => actions.setDraftField("input_mode", e.target.value)}
                  >
                    <option value="weight">{en ? "Weight (%)" : "비율(%)"}</option>
                    <option value="shares">{en ? "Shares" : "주식수"}</option>
                  </select>
                </label>
                {draft.input_mode === "weight" ? (
                  <label>
                    {en ? "Total amount" : "총 금액"}
                    <input
                      type="number"
                      min="0"
                      step="10000"
                      value={draft.total_amount}
                      onChange={(e) => actions.setDraftField("total_amount", e.target.value)}
                      placeholder={en ? "e.g. 10000000" : "예: 10000000"}
                    />
                  </label>
                ) : null}
                <div className={`saved-manager-sum ${sumOk ? "ok" : "bad"}`}>
                  {draft.input_mode === "weight"
                    ? `${en ? "Weight sum" : "비중 합계"} ${roundTo(sum, 2)}%`
                    : `${en ? "Shares" : "주식수"} ${roundTo(sum, 2)}`}
                  {draft.input_mode === "weight" && !sumOk && Math.abs(weightDelta) <= 5 && (
                    <button type="button" className="ghost-button" onClick={actions.normalizeWeights} disabled={saving}>
                      100%
                    </button>
                  )}
                </div>
              </div>

              <div className="saved-manager-holdings" onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.tagName === "INPUT") {
                  // 입력 행에서 Enter → 새 종목 행 추가
                  e.preventDefault();
                  actions.addHolding();
                }
              }}>
                {(draft.holdings || []).map((holding, hIdx) => {
                  const isLast = hIdx === (draft.holdings || []).length - 1;
                  return (
                  <div key={holding.id} className={`saved-manager-holding${normalizeTickerKey(holding.ticker) === CASH_TICKER || holding.isCash ? " cash-row" : ""}`}>
                    <TickerAutocompleteInput
                      label={null}
                      inputValue={holding.tickerInput}
                      onInputChange={(nextInput) => actions.updateHolding(holding.id, nextInput)}
                      tickers={manager.tickers}
                      tickerNameMap={manager.tickerNameMap}
                      tickerAliasMap={manager.tickerAliasMap}
                      tickerTagMap={{}}
                      selectedTags={[]}
                      tagMode="AND"
                      onOpenTagSearch={() => {}}
                      placeholder={en ? "Ticker" : "티커"}
                      className="saved-manager-holding-ticker"
                      showFooter={false}
                    />
                    {draft.input_mode === "weight" ? (
                      <div className={`saved-manager-weight-wrap ${Number(holding.weight) > 0 ? "has-value" : ""}`}>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={holding.weight ?? ""}
                          onChange={(e) => actions.updateHoldingWeight(holding.id, e.target.value)}
                          placeholder="0"
                          aria-label={en ? `Weight for ${holding.tickerInput || "ticker"}` : `${holding.tickerInput || "종목"} 비중`}
                        />
                        <span className="saved-manager-weight-suffix" aria-hidden="true">%</span>
                      </div>
                    ) : (
                      <div className={`saved-manager-weight-wrap ${Number(holding.weight) > 0 ? "has-value" : ""}`}>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={holding.weight ?? ""}
                          onChange={(e) => actions.updateHoldingWeight(holding.id, e.target.value)}
                          placeholder="0"
                          aria-label={en ? `Shares for ${holding.tickerInput || "ticker"}` : `${holding.tickerInput || "종목"} 주식수`}
                        />
                        <span className="saved-manager-weight-suffix" aria-hidden="true">{en ? "sh" : "주"}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="saved-manager-row-delete"
                      onClick={() => actions.removeHolding(holding.id)}
                      disabled={(draft.holdings || []).length <= 1 || saving}
                      aria-label={en ? "Remove holding" : "종목 삭제"}
                      title={en ? "Remove holding" : "종목 삭제"}
                    >
                      ✕
                    </button>
                  </div>
                  );
                })}
              </div>

              {draft.input_mode === "weight" ? (
                <div className="saved-manager-composition">
                  <div className="saved-manager-composition-bar" aria-hidden="true">
                    {(draft.holdings || [])
                      .filter((h) => Number(h.weight) > 0)
                      .map((h) => (
                        <span
                          key={h.id}
                          className="saved-manager-composition-seg"
                          style={{ flexGrow: Number(h.weight) }}
                          title={`${h.tickerInput || "?"} ${h.weight}%`}
                        />
                      ))}
                  </div>
                  <div className={`saved-manager-composition-legend${sumOk ? "" : " over"}`}>
                    {en ? "Total" : "합계"}{" "}
                    <strong>{roundTo(sum, 2)}%</strong>
                    {sumOk ? (
                      <span className="saved-manager-composition-hint">
                        {en ? " · balanced" : " · 100% 충족"}
                      </span>
                    ) : Math.abs(weightDelta) <= 10 ? (
                      <button type="button" className="ghost-button" onClick={actions.normalizeWeights} disabled={saving}>
                        {en ? `Normalize to 100% (+${weightDelta}%)` : `100%로 맞추기 (${weightDelta > 0 ? "+" : ""}${weightDelta}%)`}
                      </button>
                    ) : (
                      <span className="saved-manager-composition-hint">
                        {en ? ` · ${weightDelta > 0 ? "+" : ""}${weightDelta}% to 100` : ` · 100%까지 ${weightDelta > 0 ? "+" : ""}${weightDelta}%`}
                      </span>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="saved-manager-form-actions">
                <button type="button" className="ghost-button" onClick={actions.addHolding} disabled={saving}>
                  {en ? "Add ticker" : "종목 추가"}
                </button>
                <button type="button" className="primary-button" onClick={actions.save} disabled={saving}>
                  {saving ? (en ? "Saving..." : "저장 중...") : editingId ? (en ? "Update" : "수정 저장") : en ? "Save portfolio" : "포트 저장"}
                </button>
                {editingId ? (
                  <>
                    <button type="button" className="ghost-button" onClick={actions.loadIntoCanvas} disabled={saving}>
                      {en ? "Load to canvas" : "분석 캔버스로 불러오기"}
                    </button>
                    <button
                      type="button"
                      className="danger-button"
                      onClick={() => {
                        const target = savedItems.find((item) => item.id === editingId);
                        if (!target) return;
                        if (!window.confirm(en ? `Delete '${target.name}'?` : `'${target.name}' 포트를 삭제할까요?`)) return;
                        actions.deleteItem(editingId);
                      }}
                      disabled={saving}
                    >
                      {en ? "Delete" : "삭제"}
                    </button>
                    <button type="button" className="ghost-button" onClick={actions.newDraft} disabled={saving}>
                      {en ? "Cancel edit" : "편집 취소"}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {message ? <p className="saved-manager-msg ok">{message}</p> : null}
          {error ? <p className="saved-manager-msg bad">{error}</p> : null}
        </div>
      )}
    </div>
  );
}

function useSavedPortfolioManager({ onSaved, loadIntoCanvasById, tickers: mgrTickers, tickerNameMap: mgrNameMap, tickerAliasMap: mgrAliasMap }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState(() => ({ ...SAVED_PORTFOLIO_FORM_DEFAULTS, holdings: [] }));
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadList = async (preferId = "") => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/my-portfolios`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        setSavedItems([]);
        setError(isEnglishLang() ? "Available after login." : "로그인 후 사용 가능합니다.");
        return;
      }
      const json = res.ok ? await res.json() : { items: [] };
      const items = Array.isArray(json.items) ? json.items : [];
      setSavedItems(items);
      setError("");
      if (preferId && items.some((item) => item.id === preferId)) {
        const item = items.find((entry) => entry.id === preferId);
        setEditingId(item.id);
        setDraft({
          name: item.name || "",
          memo: item.memo || "",
          start_date: item.start_date || "",
          input_mode: item.input_mode === "shares" ? "shares" : "weight",
          total_amount: String(item.total_amount ?? ""),
          holdings: (item.holdings || []).map((holding, idx) => ({
            id: `sm-${item.id}-${idx}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            ticker: holding?.ticker || "",
            tickerInput: holding?.ticker ? tickerInputDisplayName(holding.ticker, {}) : "",
            weight: Number(holding?.weight) || 0,
            shares: holding?.shares ?? "",
            amount: Number(holding?.amount) || 0,
            isCash: normalizeTickerKey(holding?.ticker) === CASH_TICKER,
          })),
        });
      }
    } catch (_) {
      setError(isEnglishLang() ? "Couldn't load saved portfolios." : "저장 포트폴리오를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadList();
  }, [open]);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      setEditingId(detail.id || "");
      setOpen(true);
      setError("");
      setMessage("");
      loadList(detail.id || "");
    };
    window.addEventListener("sf:edit-saved-portfolio", handler);
    return () => window.removeEventListener("sf:edit-saved-portfolio", handler);
  }, []);

  const actions = {
    toggleOpen: () => setOpen((prev) => !prev),
    setDraftField: (field, value) => setDraft((prev) => ({ ...prev, [field]: value })),
    newDraft: () => {
      setEditingId("");
      setDraft({ ...SAVED_PORTFOLIO_FORM_DEFAULTS, holdings: [] });
      setError("");
      setMessage("");
    },
    editItem: (itemId) => loadList(itemId),
    addHolding: () => {
      setDraft((prev) => ({
        ...prev,
        holdings: [...(prev.holdings || []), { ...createEmptyHolding(`sm-${Date.now()}`), weight: prev.input_mode === "weight" ? 0 : 0 }],
      }));
    },
    removeHolding: (holdingId) => {
      setDraft((prev) => ({ ...prev, holdings: (prev.holdings || []).filter((holding) => holding.id !== holdingId) }));
    },
    updateHolding: (holdingId, nextInput) => {
      setDraft((prev) => ({
        ...prev,
        holdings: (prev.holdings || []).map((holding) => {
          if (holding.id !== holdingId) return holding;
          const match = findTickerByInput(nextInput, mgrTickers, mgrNameMap, mgrAliasMap);
          if (match) {
            return {
              ...holding,
              tickerInput: tickerInputDisplayName(match, mgrNameMap),
              ticker: match,
              isCash: false,
            };
          }
          return {
            ...holding,
            tickerInput: nextInput,
            ticker: normalizeTickerKey(nextInput) === CASH_TICKER ? CASH_TICKER : "",
            isCash: normalizeTickerKey(nextInput) === CASH_TICKER,
          };
        }),
      }));
    },
    updateHoldingWeight: (holdingId, raw) => {
      const next = clampNumber(raw, { min: 0, max: draft.input_mode === "weight" ? 100 : Infinity });
      setDraft((prev) => ({
        ...prev,
        holdings: (prev.holdings || []).map((holding) =>
          holding.id === holdingId ? { ...holding, weight: next ?? 0 } : holding,
        ),
      }));
    },
    normalizeWeights: () => {
      setDraft((prev) => {
        const rows = (prev.holdings || []).map((holding) => ({ ...holding }));
        const sum = rows.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
        if (sum > 0) {
          rows.forEach((holding) => {
            holding.weight = roundTo((Number(holding.weight) || 0) * (100 / sum), 2);
          });
        }
        const delta = roundTo(100 - rows.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0), 2);
        if (rows.length && Math.abs(delta) > 0) {
          const idx = rows.length - 1;
          rows[idx] = { ...rows[idx], weight: roundTo((Number(rows[idx].weight) || 0) + delta, 2) };
        }
        return { ...prev, holdings: rows };
      });
    },
    save: async () => {
      setSaving(true);
      setError("");
      setMessage("");
      try {
        const payload = buildSavedPortfolioPayload({ draft });
        const isEdit = !!editingId;
        const res = await fetch(
          isEdit ? `${API_BASE}/my-portfolios/${encodeURIComponent(editingId)}` : `${API_BASE}/my-portfolios`,
          {
            method: isEdit ? "PUT" : "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify(payload),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (res.status === 401 || res.status === 403) {
          throw new Error(isEnglishLang() ? "Available after login." : "로그인 후 사용 가능합니다.");
        }
        if (!res.ok) {
          throw new Error(json.detail || (isEnglishLang() ? "Save failed." : "저장에 실패했습니다."));
        }
        const savedId = json.id || editingId;
        setMessage(isEnglishLang() ? "Saved." : "저장되었습니다.");
        await loadList(savedId);
        if (typeof onSaved === "function") await onSaved(savedId);
      } catch (exc) {
        setError(exc?.message || (isEnglishLang() ? "An error occurred while saving." : "저장 중 오류가 발생했습니다."));
      } finally {
        setSaving(false);
      }
    },
    deleteItem: async (itemId) => {
      if (!itemId) return;
      setSaving(true);
      setError("");
      setMessage("");
      try {
        const res = await fetch(`${API_BASE}/my-portfolios/${encodeURIComponent(itemId)}`, {
          method: "DELETE",
          credentials: "same-origin",
        });
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.detail || (isEnglishLang() ? "Delete failed." : "삭제에 실패했습니다."));
        }
        setMessage(isEnglishLang() ? "Deleted." : "삭제되었습니다.");
        setEditingId("");
        setDraft({ ...SAVED_PORTFOLIO_FORM_DEFAULTS, holdings: [] });
        await loadList();
        if (typeof onSaved === "function") await onSaved("");
      } catch (exc) {
        setError(exc?.message || (isEnglishLang() ? "An error occurred while deleting." : "삭제 중 오류가 발생했습니다."));
      } finally {
        setSaving(false);
      }
    },
    loadIntoCanvas: async () => {
      if (!editingId || typeof loadIntoCanvasById !== "function") return;
      const target = savedItems.find((item) => item.id === editingId);
      if (target) loadIntoCanvasById(editingId, target);
    },
  };

  return {
    state: { open, editingId, saving, error, message, draft, savedItems, loading },
    actions,
  };
}


const createCashHolding = (amount = 0, weight = 0) => ({
  id: `cash-${Date.now()}`,
  ticker: CASH_TICKER,
  tickerInput: CASH_DISPLAY_NAME,
  weight,
  shares: "",
  amount,
  isCash: true,
});

const createEmptyMyPortfolioDraft = () => ({
  id: "",
  name: "",
  memo: "",
  table: "",
  start_date: "",
  end_date: "",
  holdings: [{ ...createEmptyHolding("0"), weight: 100 }],
});

const toDraftFromSaved = (item, tickerNameMap) => ({
  id: item.id || "",
  name: item.name || "",
  memo: item.memo || "",
  table: item.table || "",
  start_date: item.start_date || "",
  end_date: item.end_date || "",
  holdings: Array.isArray(item.holdings) && item.holdings.length
    ? item.holdings.map((holding, idx) => {
        const ticker = holding?.ticker || "";
        const isCash = normalizeTickerKey(ticker) === CASH_TICKER;
        return {
          id: `mh-${item.id || "saved"}-${idx}-${Date.now()}`,
          ticker,
          tickerInput: isCash ? CASH_DISPLAY_NAME : (ticker ? tickerInputDisplayName(ticker, tickerNameMap) : ""),
          weight: Number(holding?.weight) || 0,
          shares: holding?.shares ?? "",
          amount: Number(holding?.amount) || 0,
          isCash,
        };
      })
    : [{ ...createEmptyHolding("0"), weight: 100 }],
});

function MyPortfolioApp() {
  const [tickers, setTickers] = useState([]);
  const [tickerNameMap, setTickerNameMap] = useState({});
  const [tickerAliasMap, setTickerAliasMap] = useState({});
  const [tickerMetadataReady, setTickerMetadataReady] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [draft, setDraft] = useState(createEmptyMyPortfolioDraft());
  const [myPortfolioInputMode, setMyPortfolioInputMode] = useState("weight");
  const [totalAmountInput, setTotalAmountInput] = useState("");
  const [latestPriceMap, setLatestPriceMap] = useState({});
  const [priceLoadingMap, setPriceLoadingMap] = useState({});
  const [pendingTickerFocusId, setPendingTickerFocusId] = useState("");
  const [draggedHoldingId, setDraggedHoldingId] = useState("");
  const tickerInputRefs = React.useRef({});
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setTickerMetadataReady(false);
    fetch(`${API_BASE}/ticker-metadata`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((json) => {
        const { names, aliases } = buildTickerMetadataMaps(json.items || []);
        setTickerNameMap(names);
        setTickerAliasMap(aliases);
      })
      .catch(() => {
        setTickerNameMap({});
        setTickerAliasMap({});
      })
      .finally(() => setTickerMetadataReady(true));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/tickers`)
      .then((res) => (res.ok ? res.json() : { tickers: [] }))
      .then((json) => setTickers(resolveTickerListFromApi(json.tickers || [], tickerNameMap)))
      .catch(() => setTickers([]));
  }, [tickerNameMap]);

  useEffect(() => {
    setTickers((prev) => {
      const next = resolveTickerListFromApi(prev, tickerNameMap);
      if (next.length === prev.length && next.every((value, idx) => value === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [tickerNameMap]);


  const loadSavedPortfolios = async (nextSelectId = null) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/my-portfolios`, { credentials: "same-origin" });
      if (res.status === 401 || res.status === 403) {
        window.location.href = "/login";
        return;
      }
      const json = res.ok ? await res.json() : { items: [] };
      const items = Array.isArray(json.items) ? json.items : [];
      setSavedItems(items);

      const preferredId = nextSelectId !== null ? nextSelectId : selectedId;
      const target =
        items.find((item) => item.id === preferredId) ||
        items.find((item) => item.id === selectedId) ||
        items[0] ||
        null;

      if (target) {
        setSelectedId(target.id);
        const savedInputMode = target.input_mode === "shares" ? "shares" : "weight";
        setMyPortfolioInputMode(savedInputMode);
        setTotalAmountInput(savedInputMode === "shares" ? String(target.cash_amount ?? "") : String(target.total_amount ?? ""));
        setDraft(toDraftFromSaved(target, tickerNameMap));
      } else {
        setSelectedId("");
        setMyPortfolioInputMode("weight");
        setTotalAmountInput("");
        setDraft(createEmptyMyPortfolioDraft());
      }
    } catch (_) {
      setError(isEnglishLang() ? "Couldn’t load saved portfolios." : "저장된 포트폴리오를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!tickerMetadataReady) return;
    loadSavedPortfolios();
  }, [tickerMetadataReady]);

  useEffect(() => {
    setDraft((prev) => ({
      ...prev,
      holdings: (prev.holdings || []).map((holding) => ({
        ...holding,
        tickerInput: holding.ticker ? tickerInputDisplayName(holding.ticker, tickerNameMap) : holding.tickerInput,
      })),
    }));
  }, [tickerNameMap]);

  const enteredAmount = useMemo(() => {
    const parsed = Number(String(totalAmountInput || "").replace(/,/g, ""));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [totalAmountInput]);

  const stockInvestmentAmount = useMemo(() => {
    if (myPortfolioInputMode !== "shares") return 0;
    return (draft.holdings || [])
      .filter((holding) => normalizeTickerKey(holding.ticker) !== CASH_TICKER)
      .reduce((acc, holding) => {
        const price = latestPriceMap?.[normalizeTickerKey(holding.ticker)]?.close;
        const shares = Number(holding.shares ?? holding.weight);
        return acc + (price > 0 && shares > 0 ? price * shares : 0);
      }, 0);
  }, [draft.holdings, myPortfolioInputMode, latestPriceMap]);

  const cashAmountInputValue = myPortfolioInputMode === "shares" ? enteredAmount : 0;
  const totalAmount = myPortfolioInputMode === "shares" ? stockInvestmentAmount + cashAmountInputValue : enteredAmount;

  const holdingAmount = (holding) => {
    if (holding.isCash || normalizeTickerKey(holding.ticker) === CASH_TICKER) {
      return myPortfolioInputMode === "weight" && totalAmount > 0
        ? (totalAmount * (Number(holding.weight) || 0)) / 100
        : cashAmountInputValue;
    }
    if (myPortfolioInputMode === "weight") {
      return totalAmount > 0 ? (totalAmount * (Number(holding.weight) || 0)) / 100 : 0;
    }
    const price = latestPriceMap?.[normalizeTickerKey(holding.ticker)]?.close;
    const shares = Number(holding.shares ?? holding.weight);
    return price > 0 && shares > 0 ? price * shares : 0;
  };

  const displayedHoldings = useMemo(() => {
    const holdings = draft.holdings || [];
    const stockRows = holdings.filter((holding) => normalizeTickerKey(holding.ticker) !== CASH_TICKER);
    const baseRows = stockRows.map((holding) => {
      const amount = holdingAmount(holding);
      const weight = myPortfolioInputMode === "shares"
        ? (totalAmount > 0 ? (amount / totalAmount) * 100 : 0)
        : (Number(holding.weight) || 0);
      return { ...holding, amount, displayWeight: weight, isCash: false };
    });
    if (myPortfolioInputMode !== "shares") {
      return holdings.map((holding) => ({
        ...holding,
        amount: holdingAmount(holding),
        displayWeight: Number(holding.weight) || 0,
        isCash: normalizeTickerKey(holding.ticker) === CASH_TICKER || holding.isCash,
      }));
    }
    if (cashAmountInputValue <= 0.0001) return baseRows;
    return baseRows.concat({
      ...createCashHolding(cashAmountInputValue, totalAmount > 0 ? roundTo((cashAmountInputValue / totalAmount) * 100, 4) : 0),
      displayWeight: totalAmount > 0 ? (cashAmountInputValue / totalAmount) * 100 : 0,
    });
  }, [draft, myPortfolioInputMode, totalAmount, latestPriceMap, cashAmountInputValue]);

  const weightSum = useMemo(
    () => roundTo(displayedHoldings.reduce((acc, holding) => acc + (Number(holding.displayWeight ?? holding.weight) || 0), 0), 4),
    [displayedHoldings],
  );

  const selectSaved = (id) => {
    const target = savedItems.find((item) => item.id === id);
    if (!target) return;
    setSelectedId(target.id);
    const savedInputMode = target.input_mode === "shares" ? "shares" : "weight";
    setMyPortfolioInputMode(savedInputMode);
    setTotalAmountInput(savedInputMode === "shares" ? String(target.cash_amount ?? "") : String(target.total_amount ?? ""));
    setDraft(toDraftFromSaved(target, tickerNameMap));
    setMessage("");
    setError("");
  };

  const newDraft = () => {
    const suggestedName = makeUniqueName(isEnglishLang() ? "New portfolio" : "새 포트폴리오", savedItems.map((item) => item.name));
    setSelectedId("");
    setMyPortfolioInputMode("weight");
    setTotalAmountInput("");
    setDraft({
      ...createEmptyMyPortfolioDraft(),
      name: suggestedName,
    });
    setMessage("");
    setError("");
  };

  useEffect(() => {
    if (!pendingTickerFocusId) return;
    const timer = window.setTimeout(() => {
      const input = tickerInputRefs.current?.[pendingTickerFocusId];
      if (input && typeof input.focus === "function") {
        input.focus();
        if (typeof input.select === "function") input.select();
      }
      setPendingTickerFocusId("");
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pendingTickerFocusId, draft.holdings]);

  const updateHolding = (holdingId, updater) => {
    setDraft((prev) => ({
      ...prev,
      holdings: (prev.holdings || []).map((holding) => (holding.id === holdingId ? updater(holding) : holding)),
    }));
  };

  const addHolding = () => {
    const nextHolding = createEmptyHolding(Math.random().toString(36).slice(2, 7));
    setDraft((prev) => ({
      ...prev,
      holdings: [...(prev.holdings || []), nextHolding],
    }));
    setPendingTickerFocusId(nextHolding.id);
  };

  const handleHoldingEnter = (event) => {
    if (event) event.preventDefault();
    addHolding();
  };

  const moveHolding = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setDraft((prev) => {
      const holdings = [...(prev.holdings || [])];
      const fromIdx = holdings.findIndex((holding) => holding.id === fromId);
      const toIdx = holdings.findIndex((holding) => holding.id === toId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = holdings.splice(fromIdx, 1);
      holdings.splice(toIdx, 0, moved);
      return { ...prev, holdings };
    });
  };

  const removeHolding = (holdingId) => {
    setDraft((prev) => {
      const next = (prev.holdings || []).filter((holding) => holding.id !== holdingId);
      if (!next.length) return prev;
      return { ...prev, holdings: next };
    });
  };

  const applyEqualWeights = () => {
    setDraft((prev) => {
      const holdings = prev.holdings || [];
      const count = holdings.length;
      if (!count) return prev;

      const baseWeight = roundTo(100 / count, 2);
      const next = holdings.map((holding) => ({ ...holding, weight: baseWeight }));
      const sum = next.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
      const delta = roundTo(100 - sum, 2);
      if (next.length && !isNearlyEqual(delta, 0, 0.0001)) {
        const lastIdx = next.length - 1;
        next[lastIdx] = {
          ...next[lastIdx],
          weight: roundTo((Number(next[lastIdx].weight) || 0) + delta, 2),
        };
      }
      return { ...prev, holdings: next };
    });
  };

  const fetchLatestCloseForHolding = async (ticker) => {
    const res = await fetch(`${API_BASE}/prices/${encodeURIComponent(ticker)}?table=${encodeURIComponent(DEFAULT_DAILY_INTERVAL)}`);
    if (!res.ok) {
      const message = await res.text().catch(() => "");
      throw new Error(message || `${isEnglishLang() ? "Couldn’t load the latest price" : "현재가를 불러오지 못했습니다"}: ${ticker}`);
    }
    const json = await res.json();
    const rows = Array.isArray(json.data) ? json.data : [];
    for (let idx = rows.length - 1; idx >= 0; idx -= 1) {
      const row = rows[idx] || {};
      const close = Number(row.close ?? row.adj_close);
      if (Number.isFinite(close) && close > 0) {
        return { close, date: String(row.date || "").slice(0, 10) };
      }
    }
    throw new Error(`${isEnglishLang() ? "No usable latest price is available" : "사용 가능한 현재가가 없습니다"}: ${ticker}`);
  };

  useEffect(() => {
    if (myPortfolioInputMode !== "shares") return;
    const tickersToFetch = Array.from(new Set((draft.holdings || [])
      .map((holding) => normalizeTickerKey(holding.ticker))
      .filter((ticker) => ticker && ticker !== CASH_TICKER && !latestPriceMap[ticker] && !priceLoadingMap[ticker])));
    tickersToFetch.forEach((ticker) => {
      setPriceLoadingMap((prev) => ({ ...prev, [ticker]: true }));
      fetchLatestCloseForHolding(ticker)
        .then((latest) => {
          setLatestPriceMap((prev) => ({ ...prev, [ticker]: latest }));
        })
        .catch((exc) => {
          setError(exc?.message || `${isEnglishLang() ? "Couldn’t load the latest price" : "현재가를 불러오지 못했습니다"}: ${ticker}`);
        })
        .finally(() => {
          setPriceLoadingMap((prev) => ({ ...prev, [ticker]: false }));
        });
    });
  }, [myPortfolioInputMode, draft.holdings, latestPriceMap, priceLoadingMap]);

  const calculateWeightsFromShares = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const holdings = draft.holdings || [];
      if (!holdings.length) throw new Error(isEnglishLang() ? "There are no tickers to calculate shares for." : "수량을 계산할 종목이 없습니다.");
      const valued = [];
      for (const holding of holdings) {
        if (!holding.ticker) throw new Error(isEnglishLang() ? "Please select every ticker." : "모든 종목을 선택해주세요.");
        const shares = Number(holding.weight);
        if (!(shares > 0)) throw new Error(isEnglishLang() ? "Shares must be greater than 0." : "수량은 0보다 커야 합니다.");
        const latest = await fetchLatestCloseForHolding(holding.ticker);
        valued.push({ ...holding, shares, close: latest.close, date: latest.date, amount: shares * latest.close });
      }
      const total = valued.reduce((acc, item) => acc + item.amount, 0);
      if (!(total > 0)) throw new Error(isEnglishLang() ? "Couldn’t calculate value from latest prices." : "현재가 기준 평가금액을 계산하지 못했습니다.");
      const nextHoldings = valued.map((item) => ({
        ...item,
        weight: roundTo((item.amount / total) * 100, 4),
      }));
      const after = nextHoldings.reduce((acc, holding) => acc + (Number(holding.weight) || 0), 0);
      const delta = roundTo(100 - after, 4);
      if (nextHoldings.length && delta !== 0) {
        const maxIdx = nextHoldings.reduce((bestIdx, item, idx, arr) => (item.amount > arr[bestIdx].amount ? idx : bestIdx), 0);
        nextHoldings[maxIdx] = {
          ...nextHoldings[maxIdx],
          weight: roundTo((Number(nextHoldings[maxIdx].weight) || 0) + delta, 4),
        };
      }
      setDraft((prev) => ({ ...prev, holdings: nextHoldings }));
      setMyPortfolioInputMode("weight");
      const latestDate = valued.map((item) => item.date).filter(Boolean).sort().slice(-1)[0] || (isEnglishLang() ? "latest price" : "최근 가격");
      setMessage(isEnglishLang() ? `Weights were calculated from values based on ${latestDate}. You can save now or load it in Portfolio Analysis.` : `${latestDate} 현재가 기준 평가금액으로 비율을 계산했습니다. 필요하면 바로 저장하거나 포트폴리오 분석에서 불러오세요.`);
    } catch (exc) {
      setError(exc?.message || (isEnglishLang() ? "An error occurred while calculating weights from shares." : "수량 기준 비율 계산 중 오류가 발생했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const buildPayload = () => {
    const name = String(draft.name || "").trim();
    if (!name) {
      throw new Error(isEnglishLang() ? "Please enter a portfolio name." : "포트폴리오 이름을 입력해주세요.");
    }
    if (myPortfolioInputMode === "shares" && !(totalAmount > 0)) {
      throw new Error(isEnglishLang() ? "In shares mode, enter ticker shares or a cash amount." : "수량 입력 방식에서는 종목 수량 또는 현금값을 입력해주세요.");
    }
    const sourceRows = myPortfolioInputMode === "shares" ? displayedHoldings : (draft.holdings || []);
    const holdings = sourceRows.map((holding) => {
      const ticker = normalizeTickerKey(holding.ticker);
      if (!ticker) {
        throw new Error(isEnglishLang() ? "Please select every ticker." : "모든 종목을 선택해주세요.");
      }
      const weight = myPortfolioInputMode === "shares"
        ? Number(holding.displayWeight ?? holding.weight)
        : Number(holding.weight);
      if (!(weight > 0)) {
        throw new Error(isEnglishLang() ? `${ticker === CASH_TICKER ? "Cash" : ticker} weight must be greater than 0.` : `${ticker === CASH_TICKER ? CASH_DISPLAY_NAME : ticker} 비중은 0보다 커야 합니다.`);
      }
      const holdingPayload = {
        ticker,
        weight: roundTo(weight, 4),
        amount: roundTo(Number(holding.amount) || 0, 4),
      };
      if (myPortfolioInputMode === "shares" && ticker !== CASH_TICKER) {
        holdingPayload.shares = roundTo(Number(holding.shares ?? holding.weight) || 0, 6);
      }
      return holdingPayload;
    });
    const sum = holdings.reduce((acc, holding) => acc + holding.weight, 0);
    const delta = roundTo(100 - sum, 4);
    if (holdings.length && Math.abs(delta) <= 0.05 && delta !== 0) {
      const targetIdx = holdings.findIndex((holding) => holding.ticker === CASH_TICKER);
      const idx = targetIdx >= 0 ? targetIdx : holdings.length - 1;
      holdings[idx] = { ...holdings[idx], weight: roundTo(holdings[idx].weight + delta, 4) };
    }
    const adjustedSum = holdings.reduce((acc, holding) => acc + holding.weight, 0);
    if (!isNearlyEqual(adjustedSum, 100, 0.01)) {
      throw new Error(isEnglishLang() ? "Please make ticker weights add up to 100%." : "종목 비중 합계가 100%가 되도록 맞춰주세요.");
    }

    return {
      name,
      memo: String(draft.memo || ""),
      table: null,
      start_date: draft.start_date || null,
      end_date: null,
      input_mode: myPortfolioInputMode,
      total_amount: totalAmount > 0 ? roundTo(totalAmount, 4) : null,
      cash_amount: myPortfolioInputMode === "shares" ? roundTo(cashAmountInputValue, 4) : null,
      holdings,
    };
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = buildPayload();
      const isEdit = !!selectedId;
      const endpoint = isEdit
        ? `${API_BASE}/my-portfolios/${encodeURIComponent(selectedId)}`
        : `${API_BASE}/my-portfolios`;
      const res = await fetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || (isEnglishLang() ? "Save failed." : "저장에 실패했습니다."));
      }
      const savedId = json.id || selectedId;
      const requestedName = String(payload.name || "").trim();
      const actualName = String(json.name || "").trim();
      if (requestedName && actualName && requestedName !== actualName) {
        setMessage(isEnglishLang() ? `Because the name already existed, it was saved as '${actualName}'.` : `이름 중복으로 '${actualName}' 이름으로 저장되었습니다.`);
      } else {
        setMessage(isEnglishLang() ? "Saved." : "저장되었습니다.");
      }
      await loadSavedPortfolios(savedId);
    } catch (exc) {
      setError(exc?.message || (isEnglishLang() ? "An error occurred while saving." : "저장 중 오류가 발생했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async () => {
    if (!selectedId) return;
    if (!window.confirm(isEnglishLang() ? "Delete the selected portfolio?" : "선택한 포트폴리오를 삭제할까요?")) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/my-portfolios/${encodeURIComponent(selectedId)}`, {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.detail || (isEnglishLang() ? "Delete failed." : "삭제에 실패했습니다."));
      }
      setMessage(isEnglishLang() ? "Deleted." : "삭제되었습니다.");
      setSelectedId("");
      await loadSavedPortfolios("");
    } catch (exc) {
      setError(exc?.message || (isEnglishLang() ? "An error occurred while deleting." : "삭제 중 오류가 발생했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const duplicateDraft = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/my-portfolios/${encodeURIComponent(selectedId)}/duplicate`, {
        method: "POST",
        credentials: "same-origin",
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.detail || (isEnglishLang() ? "Duplicate failed." : "복제에 실패했습니다."));
      }
      setMessage(isEnglishLang() ? `'${json.name || "Copy"}' portfolio was created.` : `'${json.name || "복제본"}' 포트가 생성되었습니다.`);
      await loadSavedPortfolios(json.id || "");
    } catch (exc) {
      setError(exc?.message || (isEnglishLang() ? "An error occurred while duplicating." : "복제 중 오류가 발생했습니다."));
    } finally {
      setSaving(false);
    }
  };

  const sumOk = isNearlyEqual(weightSum, 100, 0.01);

  return (
    <>
      <header className="page-hero compact-hero">
        <div className="hero-copy">
          <span className="eyebrow">My Workspace</span>
          <h1>{isEnglishLang() ? "My Portfolio" : "내 포트폴리오"}</h1>
          <p className="subtitle">{isEnglishLang() ? "Save and manage multiple portfolios for your account." : "로그인 사용자 기준으로 포트폴리오를 여러 개 저장하고 관리합니다."}</p>
        </div>
      </header>
      <section className="workspace-card">
        <div className="my-portfolio-layout">
          <aside className="my-portfolio-list">
            <div className="my-portfolio-list-header">
              <h3>{isEnglishLang() ? "Saved portfolios" : "저장 목록"}</h3>
              <button type="button" className="ghost-button" onClick={newDraft}>
                {isEnglishLang() ? "New portfolio" : "새 포트"}
              </button>
            </div>
            {loading ? (
              <p className="my-portfolio-empty">{isEnglishLang() ? "Loading..." : "불러오는 중..."}</p>
            ) : savedItems.length ? (
              <div className="my-portfolio-items">
                {savedItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`my-portfolio-item${item.id === selectedId ? " active" : ""}`}
                    onClick={() => selectSaved(item.id)}
                  >
                    <strong>{item.name}</strong>
                    <span>{item.updated_at ? String(item.updated_at).slice(0, 19).replace("T", " ") : ""}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="my-portfolio-empty">{isEnglishLang() ? "No saved portfolios yet." : "저장된 포트폴리오가 없습니다."}</p>
            )}
          </aside>

          <div className="my-portfolio-editor">
            <div className="control-row">
              <label>
                {isEnglishLang() ? "Portfolio name" : "포트폴리오 이름"}
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={isEnglishLang() ? "e.g. Aggressive growth portfolio" : "예: 공격형 성장 포트"}
                />
              </label>
            </div>

            <label>
              {isEnglishLang() ? "Memo" : "메모"}
              <input
                type="text"
                value={draft.memo}
                onChange={(e) => setDraft((prev) => ({ ...prev, memo: e.target.value }))}
                placeholder={isEnglishLang() ? "Record strategy, rebalancing plans, etc." : "전략, 리밸런싱 계획 등을 기록하세요"}
              />
            </label>

            <div className="control-row my-portfolio-date-row">
              <label>
                {isEnglishLang() ? "Portfolio start date" : "포트 시작일"}
                <input
                  type="text"
                  value={isoDateToDisplay(draft.start_date)}
                  onChange={(e) => setDraft((prev) => ({ ...prev, start_date: e.target.value }))}
                  onBlur={() => {
                    const raw = String(draft.start_date || "").trim();
                    if (!raw) {
                      setDraft((prev) => ({ ...prev, start_date: "" }));
                      return;
                    }
                    const completed = completeDateInput(raw, "start");
                    if (completed) {
                      setDraft((prev) => ({ ...prev, start_date: completed.iso }));
                    }
                  }}
                  placeholder="YYYY. MM. DD"
                  inputMode="numeric"
                  autoComplete="off"
                />
              </label>
              <p className="hint">{isEnglishLang() ? "This start date is used automatically when analyzing the saved portfolio on its own." : "저장 포트를 단독으로 분석할 때 이 시작일을 자동 사용합니다."}</p>
            </div>

            <div className="portfolio-input-mode-row my-portfolio-input-mode-row">
              <label>
                <span className="input-mode-label-text">
                  {isEnglishLang() ? "Input mode" : "입력 방식"}
                  <span className="input-mode-help">
                    <button type="button" className="input-mode-help-trigger" aria-label={isEnglishLang() ? "Input mode help" : "입력 방식 도움말"}>?</button>
                    <span className="input-mode-help-floating" role="tooltip">
                      {isEnglishLang()
                        ? "Weight mode only converts estimated value from the total amount. Shares and ticker prices are not used in the calculation."
                        : "비율을 입력하면 총금액 기준 평가액만 환산합니다. 수량과 종목 가격은 계산에 반영하지 않습니다."}
                    </span>
                  </span>
                </span>
                <select
                  value={myPortfolioInputMode}
                  onChange={(e) => {
                    setMyPortfolioInputMode(e.target.value);
                    setMessage("");
                    setError("");
                  }}
                >
                  <option value="weight">{isEnglishLang() ? "Weight (%)" : "비율(%)"}</option>
                  <option value="shares">{isEnglishLang() ? "Shares" : "보유 수량"}</option>
                </select>
              </label>
              <label>
                {myPortfolioInputMode === "shares" ? (isEnglishLang() ? "Cash" : "현금") : (isEnglishLang() ? "Total amount" : "총 금액")}
                <input
                  type="number"
                  min="0"
                  step="10000"
                  value={totalAmountInput}
                  onChange={(e) => setTotalAmountInput(e.target.value)}
                  placeholder={myPortfolioInputMode === "shares" ? (isEnglishLang() ? "e.g. 2000000" : "예: 2000000") : (isEnglishLang() ? "e.g. 10000000" : "예: 10000000")}
                />
              </label>
              <p>
                {myPortfolioInputMode === "shares"
                  ? (isEnglishLang() ? "Enter a cash amount to combine it with shares × latest close and automatically calculate total amount and weights." : "현금값을 입력하면 보유 수량×최근 종가 평가액과 합산해 총금액·비중을 자동 계산합니다.")
                  : (isEnglishLang() ? "Enter a total amount to display estimated values from weights." : "총 금액을 입력하면 비중 기준 평가액을 표시합니다.")}
              </p>
            </div>

            <div className="my-holdings my-holdings-table">
              <div className="my-holding-row my-holding-header">
                <span className="drag-column" aria-hidden="true"></span>
                <span>{isEnglishLang() ? "Ticker" : "종목"}</span>
                <span>{isEnglishLang() ? "Shares" : "수량"}</span>
                <span>{isEnglishLang() ? "Value" : "평가액"}</span>
                <span>{isEnglishLang() ? "Weight (%)" : "비중(%)"}</span>
                <span>{isEnglishLang() ? "Actions" : "관리"}</span>
              </div>
              {displayedHoldings.map((holding) => {
                const isCash = normalizeTickerKey(holding.ticker) === CASH_TICKER || holding.isCash;
                const sourceHolding = (draft.holdings || []).find((item) => item.id === holding.id) || holding;
                const price = latestPriceMap?.[normalizeTickerKey(holding.ticker)]?.close;
                const isPriceLoading = !!priceLoadingMap?.[normalizeTickerKey(holding.ticker)];
                const isDragging = draggedHoldingId === sourceHolding.id;
                return (
                  <div
                    key={holding.id}
                    className={`my-holding-row${isCash ? " cash-row" : ""}${isDragging ? " dragging" : ""}`}
                    draggable={!isCash}
                    onDragStart={(e) => {
                      if (isCash) return;
                      setDraggedHoldingId(sourceHolding.id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", sourceHolding.id);
                    }}
                    onDragOver={(e) => {
                      if (isCash || !draggedHoldingId || draggedHoldingId === sourceHolding.id) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => {
                      if (isCash) return;
                      e.preventDefault();
                      const fromId = e.dataTransfer.getData("text/plain") || draggedHoldingId;
                      moveHolding(fromId, sourceHolding.id);
                      setDraggedHoldingId("");
                    }}
                    onDragEnd={() => setDraggedHoldingId("")}
                  >
                    <button
                      type="button"
                      className="drag-handle"
                      title={isEnglishLang() ? "Drag to reorder" : "드래그해서 순서 변경"}
                      aria-label={isEnglishLang() ? "Drag to reorder" : "드래그해서 순서 변경"}
                      disabled={isCash}
                    >
                      ⋮⋮
                    </button>
                    <div className="holding-ticker-cell">
                      {isCash ? (
                        <label aria-label={isEnglishLang() ? "Cash" : "현금"}>
                          <input type="text" value={isEnglishLang() ? "Cash" : CASH_DISPLAY_NAME} disabled />
                        </label>
                      ) : (
                        <TickerAutocompleteInput
                          label={null}
                          inputRef={(node) => {
                            if (node) tickerInputRefs.current[sourceHolding.id] = node;
                            else delete tickerInputRefs.current[sourceHolding.id];
                          }}
                          inputValue={sourceHolding.tickerInput}
                          onInputChange={(value) => {
                            updateHolding(sourceHolding.id, (prev) => {
                              const match = findTickerByInput(value, tickers, tickerNameMap, tickerAliasMap);
                              if (match) {
                                return {
                                  ...prev,
                                  tickerInput: tickerInputDisplayName(match, tickerNameMap),
                                  ticker: match,
                                  isCash: false,
                                };
                              }
                              return {
                                ...prev,
                                tickerInput: value,
                                ticker: match || "",
                                isCash: false,
                              };
                            });
                          }}
                          tickers={tickers}
                          tickerNameMap={tickerNameMap}
                          tickerAliasMap={tickerAliasMap}
                          tickerTagMap={{}}
                          selectedTags={[]}
                          tagMode="AND"
                          onOpenTagSearch={() => {}}
                          placeholder={isEnglishLang() ? "Enter ticker or company name" : "티커 또는 종목명을 입력하세요"}
                          showFooter={false}
                          className="holding-ticker"
                          onEnterKey={handleHoldingEnter}
                        />
                      )}
                    </div>
                    <label className="holding-shares" aria-label={isEnglishLang() ? "Shares" : "수량"}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={isCash ? "" : (sourceHolding.shares ?? (myPortfolioInputMode === "shares" ? sourceHolding.weight : ""))}
                        disabled={myPortfolioInputMode === "weight" || isCash}
                        onChange={(e) => {
                          const next = clampNumber(e.target.value, { min: 0, max: Infinity });
                          updateHolding(sourceHolding.id, (prev) => ({ ...prev, shares: next ?? "" }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isCash) handleHoldingEnter(e);
                        }}
                        placeholder={myPortfolioInputMode === "shares" ? (isEnglishLang() ? "Shares" : "수량") : (isEnglishLang() ? "Disabled" : "비활성")}
                      />
                    </label>
                    <label className="holding-amount" aria-label={isEnglishLang() ? "Value" : "평가액"}>
                      <input
                        type="text"
                        value={isPriceLoading ? (isEnglishLang() ? "Loading price..." : "가격 로딩...") : (holding.amount > 0 ? formatAmount(holding.amount) : "")}
                        disabled
                        placeholder={myPortfolioInputMode === "shares" ? "0" : (totalAmount > 0 ? "0" : (isEnglishLang() ? "Total amount needed" : "총 금액 필요"))}
                      />
                      {myPortfolioInputMode === "shares" && !isCash && price > 0 && (
                        <small>{isEnglishLang() ? "Latest price" : "최근가"} {formatAmount(price)}</small>
                      )}
                    </label>
                    <label className="holding-weight" aria-label={isEnglishLang() ? "Weight (%)" : "비중(%)"}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={myPortfolioInputMode === "shares" ? roundTo(holding.displayWeight || 0, 4) : (sourceHolding.weight ?? "")}
                        disabled={myPortfolioInputMode === "shares" || isCash}
                        onChange={(e) => {
                          const next = clampNumber(e.target.value, { min: 0, max: 100 });
                          updateHolding(sourceHolding.id, (prev) => ({ ...prev, weight: next ?? 0 }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isCash) handleHoldingEnter(e);
                        }}
                        placeholder="0"
                      />
                    </label>
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={() => removeHolding(sourceHolding.id)}
                      disabled={isCash || (draft.holdings || []).length <= 1}
                    >
                      {isEnglishLang() ? "Delete" : "삭제"}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="actions">
              <button type="button" className="ghost-button" onClick={addHolding}>
                {isEnglishLang() ? "Add ticker" : "종목 추가"}
              </button>
              <button type="button" className="ghost-button" onClick={applyEqualWeights} disabled={myPortfolioInputMode === "shares"}>
                {isEnglishLang() ? "Equal weight" : "균등 배분"}
              </button>
              <span className={`portfolio-sum ${sumOk ? "ok" : "bad"}`}>
                {isEnglishLang() ? `Total ${roundTo(weightSum, 2)}%` : `합계 ${roundTo(weightSum, 2)}%`}
              </span>
              {myPortfolioInputMode === "shares" && (
                <>
                  <span className="portfolio-sum ok">{isEnglishLang() ? "Stock value" : "주식 평가액"} {formatAmount(stockInvestmentAmount)}{isEnglishLang() ? " KRW" : "원"}</span>
                  <span className="portfolio-sum ok">{isEnglishLang() ? "Cash" : "현금"} {formatAmount(cashAmountInputValue)}{isEnglishLang() ? " KRW" : "원"}</span>
                  <span className="portfolio-sum ok">{isEnglishLang() ? "Total amount" : "총금액"} {formatAmount(totalAmount)}{isEnglishLang() ? " KRW" : "원"}</span>
                </>
              )}
            </div>

            <div className="actions">
              <button type="button" onClick={saveDraft} disabled={saving}>
                {saving ? (isEnglishLang() ? "Saving..." : "저장 중...") : selectedId ? (isEnglishLang() ? "Save changes" : "수정 저장") : (isEnglishLang() ? "Save new" : "새로 저장")}
              </button>
              <button type="button" className="ghost-button" onClick={duplicateDraft} disabled={!selectedId || saving}>
                {isEnglishLang() ? "Duplicate" : "복제"}
              </button>
              <button type="button" className="danger-button" onClick={deleteDraft} disabled={!selectedId || saving}>
                {isEnglishLang() ? "Delete selected" : "선택 포트 삭제"}
              </button>
            </div>

            {message && <div className="success-box">{message}</div>}
            {error && <div className="error-box">{error}</div>}
          </div>
        </div>
      </section>
    </>
  );
}


const formatLandingPostDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  return date.toLocaleDateString(isEnglishLang() ? "en-US" : "ko-KR", { month: "short", day: "numeric" });
};

const landingPostHref = (type, item) => {
  const slug = encodeURIComponent(String(item?.slug || "").trim());
  const suffix = isEnglishLang() ? "?lang=en" : "";
  if (!slug) return (type === "blog" ? "/blog" : "/board") + suffix;
  return (type === "blog" ? `/blog/${slug}` : `/board/${slug}`) + suffix;
};

const LatestLandingCard = ({ title, eyebrow, href, items, loading, type }) => (
  <article className="landing-latest-card">
    <div className="landing-latest-card-head">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h3>{title}</h3>
      </div>
      <a href={isEnglishLang() ? `${href}?lang=en` : href} aria-label={`${title} ${isEnglishLang() ? "more" : "더보기"}`}>More »</a>
    </div>
    <ol className="landing-latest-list">
      {loading ? (
        Array.from({ length: 5 }).map((_, idx) => (
          <li key={`loading-${idx}`} className="landing-latest-item loading">
            <span className="landing-latest-rank">{idx + 1}</span>
            <span className="landing-latest-title">{isEnglishLang() ? "Loading..." : "불러오는 중..."}</span>
          </li>
        ))
      ) : items.length ? (
        items.map((item, idx) => (
          <li key={item.id || item.slug || idx} className="landing-latest-item">
            <a href={landingPostHref(type, item)}>
              <span className="landing-latest-rank">{idx + 1}</span>
              <span className="landing-latest-body">
                <strong>{isEnglishLang() && item.title_en ? item.title_en : (item.title || (isEnglishLang() ? "Untitled" : "제목 없음"))}</strong>
                <small>
                  {formatLandingPostDate(item.created_at || item.updated_at)}
                  {item.author ? ` · ${item.author}` : ""}
                  {type === "board" && Number.isFinite(Number(item.comment_count)) ? ` · ${isEnglishLang() ? "Comments" : "댓글"} ${Number(item.comment_count)}` : ""}
                </small>
              </span>
            </a>
          </li>
        ))
      ) : (
        <li className="landing-latest-empty">{isEnglishLang() ? "No posts to show yet." : "아직 표시할 글이 없습니다."}</li>
      )}
    </ol>
    <a className="landing-latest-more" href={isEnglishLang() ? `${href}?lang=en` : href}>More »</a>
  </article>
);

function LandingPage() {
  const [latestBoardPosts, setLatestBoardPosts] = useState([]);
  const [latestBlogPosts, setLatestBlogPosts] = useState([]);
  const [loadingLatestLanding, setLoadingLatestLanding] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchLatest = async () => {
      setLoadingLatestLanding(true);
      try {
        const [boardRes, blogRes] = await Promise.all([
          fetch(`${API_BASE}/api/board/posts?page=1&limit=5${isEnglishLang() ? "&lang=en" : ""}`, { credentials: "same-origin" }),
          fetch(`${API_BASE}/api/blog/posts?page=1&limit=5`, { credentials: "same-origin" }),
        ]);
        const [boardJson, blogJson] = await Promise.all([
          boardRes.ok ? boardRes.json() : Promise.resolve({ items: [] }),
          blogRes.ok ? blogRes.json() : Promise.resolve({ items: [] }),
        ]);
        if (!alive) return;
        setLatestBoardPosts(Array.isArray(boardJson.items) ? boardJson.items.slice(0, 5) : []);
        setLatestBlogPosts(Array.isArray(blogJson.items) ? blogJson.items.slice(0, 5) : []);
      } catch (_) {
        if (!alive) return;
        setLatestBoardPosts([]);
        setLatestBlogPosts([]);
      } finally {
        if (alive) setLoadingLatestLanding(false);
      }
    };
    fetchLatest();
    return () => {
      alive = false;
    };
  }, []);

  const featuresBase = isEnglishLang() ? [
    {
      title: "Stock Analysis",
      text: "View core price metrics such as return, volatility, MDD, Sharpe Ratio, and Sortino Ratio in one place.",
      href: "/analysis?lang=en",
    },
    {
      title: "Portfolio Analysis",
      text: "Enter weights or share counts across holdings to compare and manage portfolio performance and risk.",
      href: "/portfolio-analysis?lang=en",
    },
    {
      title: "Macro Analysis",
      text: "Track FX, rates, inflation, and market indicators together to diagnose changes in the investment environment.",
      href: "/macro?lang=en",
    },
    {
      title: "SPY Model",
      text: "Diagnose the S&P 500 risk-on / risk-off regime daily with Minervini's 8-criteria trend template.",
      href: "/spy-model?lang=en",
    },
    {
      title: "Community",
      text: "Share watchlist ideas, market trends, and investment views through the board and blog.",
      href: "/board?lang=en",
    },
    {
      title: "Guru Portfolios",
      text: "Compare the latest SEC 13F portfolios of legendary US investors by value with top holdings.",
      href: "/en/gurus",
    },
  ] : [
    {
      title: "종목 분석",
      text: "종목별 수익률, 변동성, MDD, Sharpe Ratio, Sortino Ratio 등 핵심 가격 지표를 한 화면에서 확인합니다.",
      href: "/analysis",
    },
    {
      title: "포트폴리오 분석",
      text: "여러 종목의 비중이나 보유 주식수를 입력해 포트폴리오 성과와 리스크를 비교하고 관리합니다.",
      href: "/portfolio-analysis",
    },
    {
      title: "매크로 분석",
      text: "환율, 금리, 물가, 시장 지표 흐름을 함께 확인해 투자환경 변화를 진단합니다.",
      href: "/macro",
    },
    {
      title: "SPY 타이밍 모델",
      text: "미너비니 트렌드 템플릿 8가지 조건으로 S&P 500 리스크 온/오프 국면을 매일 자동 판정합니다.",
      href: "/spy-model",
    },
    {
      title: "커뮤니티",
      text: "관심 종목, 시장 흐름, 투자 아이디어를 게시판과 블로그를 통해 함께 나눕니다.",
      href: "/board",
    },
    {
      title: "거장의 포트폴리오",
      text: "버핏·드러컨밀러 등 투자 거장들의 최신 SEC 13F 포트폴리오를 평가액 순으로 비교합니다.",
      href: "/gurus",
    },
  ];
  // SPY 타이밍 모델은 로그인 회원 전용 (2026-09-10) — 비로그인 시 랜딩 카드에서 제외.
  const [spyMembersOnly, setSpyMembersOnly] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/auth/me`, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : { username: null }))
      .then((json) => {
        if (alive) setSpyMembersOnly(!!(json && json.username));
      })
      .catch(() => {
        if (alive) setSpyMembersOnly(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  const features = featuresBase.filter((feature) => {
    const isSpyCard = (feature.href || "").startsWith("/spy-model");
    return !isSpyCard || spyMembersOnly;
  });
  const useCases = isEnglishLang()
    ? ["Korean and U.S. stock analysis", "Compare returns and risk together", "Aggregate and compare portfolios", "Track macro indicator trends"]
    : ["국내·미국 주식 종목 분석", "수익률과 리스크 동시 비교", "포트폴리오 합산/비교 분석", "매크로 지표 흐름 확인"];
  const faq = isEnglishLang() ? [
    ["What is StockForum?", "It is an investment research platform for stock performance analytics, portfolio management, and sharing investment views."],
    ["What metrics can I check?", "You can review price-based performance and risk metrics such as return, annualized volatility, MDD, Sharpe Ratio, and Sortino Ratio."],
    ["Can I analyze portfolios too?", "Yes. You can compare multiple portfolios or analyze an aggregate portfolio using weights or share counts."],
  ] : [
    ["스톡포럼은 어떤 서비스인가요?", "주식 성과 지표 분석, 포트폴리오 관리, 투자 의견 공유를 한 곳에서 제공하는 투자 리서치 플랫폼입니다."],
    ["어떤 지표를 확인할 수 있나요?", "수익률, 연환산 변동성, MDD, Sharpe Ratio, Sortino Ratio 등 가격 기반 성과·위험 지표를 확인할 수 있습니다."],
    ["포트폴리오 분석도 가능한가요?", "네. 종목 비중 또는 보유 주식수 기반으로 여러 포트폴리오를 비교하거나 합산 포트폴리오를 분석할 수 있습니다."],
  ];

  return (
    <div className="landing-page">
      <header className="landing-hero-v2">
        <div className="landing-hero-copy">
          <span className="eyebrow">StockForum Research</span>
          <h1>Stock Forum</h1>
          <p>
            {isEnglishLang()
              ? "Analyze stocks and portfolios with performance, risk, and market context in one focused workspace."
              : "종목과 포트폴리오의 성과·위험을 분석하고, 시장 환경과 투자 의견까지 한곳에서 확인하세요."}
          </p>
          <div className="hero-actions">
            <a className="hero-link primary" href={isEnglishLang() ? "/analysis?lang=en" : "/analysis"}>{isEnglishLang() ? "Analyze a stock" : "종목 분석 시작"}</a>
            <a className="hero-link" href={isEnglishLang() ? "/portfolio-analysis?lang=en" : "/portfolio-analysis"}>{isEnglishLang() ? "Build a portfolio" : "포트폴리오 분석"}</a>
          </div>
          <p className="landing-hero-note">{isEnglishLang() ? "Korean and U.S. equities · Price-based performance and risk · Fundamental reference data" : "국내·미국 주식 · 가격 기반 성과·위험 지표 · 펀더멘털 참고 데이터"}</p>
        </div>
      </header>

      <section className="landing-section-v2" aria-labelledby="landingFeaturesTitle">
        <div className="landing-section-head">
          <div><span className="eyebrow">Workspace</span><h2 id="landingFeaturesTitle">{isEnglishLang() ? "Start with the question you need to answer" : "필요한 분석에서 바로 시작하세요"}</h2></div>
          <p>{isEnglishLang() ? "Focused tools for a single ticker, a portfolio, and the wider market." : "종목, 포트폴리오, 시장 환경을 각각의 목적에 맞는 화면에서 확인할 수 있습니다."}</p>
        </div>
        <div className="landing-product-grid">
          {features.map((feature, index) => (
            <a className={`landing-product-card${index < 2 ? " primary" : ""}`} href={feature.href} key={feature.title}>
              <span className="landing-product-index">0{index + 1}</span><div><h3>{feature.title}</h3><p>{feature.text}</p></div><span className="landing-product-arrow" aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </section>

      <section className="landing-section-v2 landing-latest-section" aria-label={isEnglishLang() ? "Latest community and blog posts" : "커뮤니티와 블로그 최신글"}>
        <div className="landing-section-head compact"><div><span className="eyebrow">Perspectives</span><h2>{isEnglishLang() ? "Recent market perspectives" : "최근 시장 의견"}</h2></div><p>{isEnglishLang() ? "Read what the community is discussing and publishing." : "커뮤니티의 토론과 블로그 리서치를 한 번에 살펴보세요."}</p></div>
        <div className="landing-latest-grid">
          <LatestLandingCard title={isEnglishLang() ? "Latest community posts" : "커뮤니티 최신글"} eyebrow="Community" href="/board" items={latestBoardPosts} loading={loadingLatestLanding} type="board" />
          <LatestLandingCard title={isEnglishLang() ? "Latest blog posts" : "블로그 최신글"} eyebrow="Blog" href="/blog" items={latestBlogPosts} loading={loadingLatestLanding} type="blog" />
        </div>
      </section>

      <section className="landing-method-strip" aria-label={isEnglishLang() ? "Data and methodology" : "데이터와 분석 기준"}>
        <div><span>01</span><strong>{isEnglishLang() ? "Performance & risk" : "성과·위험"}</strong><p>{isEnglishLang() ? "Calculated from the selected price period." : "선택한 기간의 가격 데이터로 계산합니다."}</p></div>
        <div><span>02</span><strong>{isEnglishLang() ? "Fundamentals" : "펀더멘털"}</strong><p>{isEnglishLang() ? "Shown separately as company-data references." : "기업 재무 데이터는 참고 지표로 구분합니다."}</p></div>
        <div><span>03</span><strong>{isEnglishLang() ? "Research, not advice" : "투자 참고용"}</strong><p>{isEnglishLang() ? "Review sources and assumptions before decisions." : "투자 판단 전 데이터 출처와 산식을 확인하세요."}</p></div>
      </section>

      <section className="landing-faq-v2" aria-labelledby="landingFaqTitle">
        <div className="landing-section-head compact"><div><span className="eyebrow">FAQ</span><h2 id="landingFaqTitle">{isEnglishLang() ? "About StockForum" : "스톡포럼 이용 안내"}</h2></div></div>
        <div className="landing-faq-list">{faq.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div>
      </section>
    </div>
  );
}

// B7(2단계): 종목 스크리너 페이지 — 가격 조건만(펀더멘털 400 고지). 결과는 조건 충족 나열.
function ScreenerApp() {
  const [filters, setFilters] = useState({
    min_52w_position_pct: "",
    max_52w_position_pct: "",
    min_avg_volume_20d: "",
    min_avg_dollar_volume_20d: "",
    max_drawdown_1y_pct: "",
    sma200_position: "",
    limit: "50",
  });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ranOnce, setRanOnce] = useState(false);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const runScreener = async (custom) => {
    setLoading(true);
    setError("");
    const f = custom || filters;
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) params.set(k, String(v));
    });
    try {
      const res = await fetch(`${API_BASE}/api/screener?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.detail || `조회 실패 (${res.status})`);
        setRows([]);
        setMeta(null);
      } else {
        setRows(Array.isArray(json.rows) ? json.rows : []);
        setMeta(json);
      }
    } catch (exc) {
      setError(exc.message || "네트워크 오류");
    } finally {
      setRanOnce(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    // 첫 진입 시 기본 조건 1회 실행 (콜드 ~10s 안내)
    runScreener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtNum = (v, digits = 0) =>
    v === null || v === undefined || !Number.isFinite(Number(v)) ? "N/A" : Number(v).toLocaleString("ko-KR", { maximumFractionDigits: digits });

  return (
    <div className="screener-app">
      <header className="screener-header">
        <h1>{sfText("종목 스크리너")}</h1>
        <p className="screener-disclaimer">
          {sfText("저장된 전체 시장 일봉의 가격 조건 충족 나열입니다 — 매수 추천이 아닙니다. 펀더멘털 조건(PER/PBR 등)은 확정 캐시가 62종목만 커버해 지원하지 않습니다.")}
        </p>
      </header>
      <form
        className="screener-form"
        onSubmit={(e) => { e.preventDefault(); runScreener(); }}
      >
        <label>{sfText("52주 위치 최소(%)")}
          <input type="number" min="-5" max="105" step="1" value={filters.min_52w_position_pct} onChange={setF("min_52w_position_pct")} placeholder="예: 20" />
        </label>
        <label>{sfText("52주 위치 최대(%)")}
          <input type="number" min="-5" max="105" step="1" value={filters.max_52w_position_pct} onChange={setF("max_52w_position_pct")} placeholder="예: 80" />
        </label>
        <label>{sfText("20일 평균 거래량 최소")}
          <input type="number" min="0" step="any" value={filters.min_avg_volume_20d} onChange={setF("min_avg_volume_20d")} placeholder="예: 500000" />
        </label>
        <label>{sfText("20일 평균 거래대금 최소")}
          <input type="number" min="0" step="any" value={filters.min_avg_dollar_volume_20d} onChange={setF("min_avg_dollar_volume_20d")} placeholder="예: 10000000000" />
        </label>
        <label>{sfText("1년 최대 낙폭 한도(%)")}
          <input type="number" min="-100" max="0" step="1" value={filters.max_drawdown_1y_pct} onChange={setF("max_drawdown_1y_pct")} placeholder="예: -30" />
        </label>
        <label>{sfText("SMA200 위치")}
          <select value={filters.sma200_position} onChange={setF("sma200_position")}>
            <option value="">{sfText("전체")}</option>
            <option value="above">{sfText("200일선 위")}</option>
            <option value="below">{sfText("200일선 아래")}</option>
          </select>
        </label>
        <label>{sfText("최대 행 수")}
          <input type="number" min="1" max="200" value={filters.limit} onChange={setF("limit")} />
        </label>
        <button type="submit" className="screener-run" disabled={loading}>
          {loading ? sfText("조회 중... (콜드 시 ~10초)") : sfText("스크린 실행")}
        </button>
      </form>
      {error && <div className="error-box">{error}</div>}
      {meta && (
        <p className="screener-meta">
          {sfText(`평가 ${fmtNum(meta.evaluated_tickers)}종목 · 조건 충족 ${fmtNum(meta.matched)}종목`)}
          {Array.isArray(meta.rows) && meta.rows.length === meta.limit ? sfText(` (상위 ${meta.limit}행만 표시)`) : null}
        </p>
      )}
      {rows.length > 0 && (
        <div className="table-container screener-table-wrap">
          <table className="screener-table">
            <thead>
              <tr>
                <th scope="col">{sfText("티커")}</th>
                <th scope="col">{sfText("종가")}</th>
                <th scope="col">{sfText("일변화")}</th>
                <th scope="col">{sfText("52주 위치")}</th>
                <th scope="col">{sfText("52주 최고")}</th>
                <th scope="col">{sfText("52주 최저")}</th>
                <th scope="col">{sfText("MDD(1년)")}</th>
                <th scope="col">{sfText("20일 평균 거래량")}</th>
                <th scope="col">{sfText("기준일")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ticker}>
                  <td><a href={`/analysis?ticker=${encodeURIComponent(r.ticker)}`}>{r.ticker}</a></td>
                  <td>{fmtNum(r.last_close, 2)}</td>
                  <td className={Number(r.day_change_pct) >= 0 ? "is-up" : "is-down"}>{formatSnapPct(r.day_change_pct !== null && r.day_change_pct !== undefined ? r.day_change_pct / 100 : null)}</td>
                  <td>{r.week_52_position_pct !== null && r.week_52_position_pct !== undefined ? `${Number(r.week_52_position_pct).toFixed(1)}%` : "N/A"}</td>
                  <td>{fmtNum(r.week_52_high, 2)}</td>
                  <td>{fmtNum(r.week_52_low, 2)}</td>
                  <td>{r.mdd_1y_pct !== null && r.mdd_1y_pct !== undefined ? `${Number(r.mdd_1y_pct).toFixed(1)}%` : "N/A"}</td>
                  <td>{fmtNum(r.avg_volume_20d)}</td>
                  <td>{r.as_of_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ranOnce && !loading && !error && rows.length === 0 && (
        <p className="screener-empty">{sfText("조건을 충족한 종목이 없습니다. 조건을 완화해 다시 실행하세요.")}</p>
      )}
      <p className="screener-footnote">{sfText("데이터는 저장된 일봉 종가 기준(실시간 아님). 스크리닝 결과는 투자 조언이 아닙니다.")}</p>
    </div>
  );
}

const root = createRoot(document.getElementById("root"));
const path = window.location?.pathname || "/";
const routeHead = {
  "/": {
    title: isEnglishLang() ? "StockForum | Stock Performance Analytics & Portfolio Management" : "스톡포럼 | 주식 성과 지표 분석·포트폴리오 관리",
    description: isEnglishLang() ? "Stock performance analytics, portfolio management and investment ideas in one place. Review returns, risk and MDD per ticker." : "주식 성과 지표 분석과 포트폴리오 관리, 투자 아이디어를 한 곳에서. 수익률·리스크·MDD를 종목별로 확인하세요.",
  },
  "/analysis": {
    title: isEnglishLang() ? "Stock Analysis | StockForum Performance Analytics" : "종목 분석 | 스톡포럼 주식 성과 지표 분석",
    description: isEnglishLang() ? "Analyze stock price trends, returns, risk, MDD, Sharpe Ratio and Sortino Ratio per ticker on StockForum." : "스톡포럼 종목 분석에서 주식 가격 흐름, 수익률, 리스크, MDD, Sharpe Ratio, Sortino Ratio 등 종목별 성과 지표를 확인하세요.",
  },
  "/portfolio-analysis": {
    title: isEnglishLang() ? "Portfolio Analysis | StockForum Portfolio Management" : "포트폴리오 분석 | 스톡포럼 포트폴리오 관리",
    description: isEnglishLang() ? "Compare returns, risk, MDD and performance metrics across multiple stocks and portfolios on StockForum." : "스톡포럼 포트폴리오 분석에서 여러 종목과 포트폴리오의 수익률, 리스크, MDD, 성과 지표를 비교하고 관리하세요.",
  },
  "/screener": {
    title: isEnglishLang() ? "Stock Screener | StockForum" : "종목 스크리너 | 스톡포럼",
    description: isEnglishLang() ? "Screen the whole stored market by price conditions: 52-week position, volume, 1-year drawdown, SMA200." : "전체 시장 가격 조건 스크리닝: 52주 위치, 거래량, 1년 최대낙폭, 200일선. 매수 추천이 아닌 조건 충족 나열.",
  },
};
const head = routeHead[path];
if (head) {
  document.title = head.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", head.description);
  document.querySelector('meta[property="og:title"]')?.setAttribute("content", head.title);
  document.querySelector('meta[property="og:description"]')?.setAttribute("content", head.description);
}
root.render(
  path === "/"
    ? <LandingPage />
    : path === "/portfolio-analysis" || path === "/portfolio-compare"
      ? <PortfolioCompareApp />
      : path === "/my-portfolio"
        ? <MyPortfolioApp />
        : path === "/screener"
          ? <ScreenerApp />
          : <StockAnalysisApp />,
);
